function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React from "react";
import ClipPlayer from "./clip-player";
import { EditModeModal, SmartResizeModal } from "./edit-mode";
import { TemplateCard, TemplateSheet, TemplatePreviewSheet, PiecePreview, recipeOf, angleFromRecipe, mediaPromptFromRecipe, mediaRowFor, videoRefFor } from "./templates";
import MakeStage from "./make";
import { wordDiff, nameOf, initialsOf } from "./collab";
import { ShippedList, isLive as pubIsLive, isShopify as isShopifyTarget, shippedByPlunk, targetName } from "./shipped";
import { getPages } from "../web-activity/data";
import { withRouter } from "react-router";
import { getOnboardingStatus } from "../../functions/onboarding-status";
import { headers as bearerHeaders } from "../../endpoints/market-intelligence";
import endpoints from "../../endpoints";
import { Loader } from "../../components/loaders";
import Error from "../../components/error";
import ServiceFactory from "../../service";
import { getBillingStatus } from "../../functions/billing-status";
import { loadBrandKit } from "../../functions/brand-kit";
import { num, usd, rangeLabelFor } from "../sales/territory-common";
import { getGlobalRange, subscribePeriod, unsubscribePeriod } from "../../functions/period-store";
import { kwService } from "../keywords/common";
import account from "../../data/account";
import { INPUT, CARD } from "../../components/form-styles";
import { searchProducts } from "./images-api";
import { hasTenantFeature } from "../../functions/tenant-features";
import { VoicePicker, PreviewLineButton } from "./voices";
import { lastVideoQuality } from "./video-quality";

/*
 * Content Hub Builder (spec: PlunkDocs/specs/CONTENT_HUB_BUILDER_2026-09-27.md; mockup content-hub-builder-2026-09-27).
 * One surface per Story: Brief → Plan → Write → Media → Ship → Measure. Left rail = stages, center = the stage,
 * right rail = Sources (brand kit, linked products, what's working, assets, credits).
 *
 * Every action reuses the existing rails: repurpose (drafts), variants (edit/approve), agent runs (image /
 * storyboard / video — charged like every other run), assets + blocks, publish endpoints and the calendar.
 * Progress persists as a `builder_state` block on the story's primary piece (blocks are versioned; the latest wins),
 * so nothing new is needed server-side for the shell.
 *
 * Route: /Content/build/:storyId — a story id is the primary container's id; a single piece opens as a story of one.
 */

function svc(api) {
  const S = ServiceFactory.getType(api, "product");
  return new S();
}
async function safe(p, fb) {
  try {
    return (await p) || fb;
  } catch (e) {
    return fb;
  }
}
const toast = m => {
  const t = document.querySelector("paper-toast");
  if (t && m) t.show(m);
};
const strip = h => String(h || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

// Builder shell (DS mockup storyflow 2026-10-09, Brief.dc.html / Make.dc.html): a top bar with a horizontal step bar.
// User-facing names: Ship is "Publish", Measure is "Results". The stage keys stay (saved states use them).
const STAGES = [["brief", "Brief"], ["make", "Make"], ["ship", "Publish"], ["measure", "Results"]];
const LEGACY_STAGE = {
  plan: "make",
  write: "make",
  media: "make"
};
const GOALS = [["launch", "Launch / promote a product"], ["sell_down", "Sell down inventory"], ["reorder", "Dealer / wholesale reorder"], ["educate", "Educate / SEO"], ["seasonal", "Seasonal or event"]];
// Goal / audience lists are workspace-driven (no hard-coded vertical): the wholesale items only show for
// manufacturing / both business models, and Settings › Brand "Who you sell to" replaces the default audiences.
const AUDIENCES = [["retail", "Retail shoppers"], ["wholesale", "Wholesale dealers"], ["designers", "Designers / trade"], ["subscribers", "Email subscribers"]];
const WHOLESALE_ONLY = new Set(["wholesale", "designers", "reorder"]);
const CHANNEL_ICON = {
  product_copy: "inventory_2",
  blog: "article",
  landing: "web",
  email: "mail_outline",
  social_instagram: "photo_camera",
  social_facebook: "thumb_up_off_alt",
  social_pinterest: "push_pin",
  social_linkedin: "work_outline",
  gbp_post: "storefront"
};
const CHANNELS = [["product_copy", "Product copy"], ["blog", "Blog"], ["landing", "Landing page"], ["email", "Email"], ["social_instagram", "Instagram"], ["social_facebook", "Facebook"], ["social_pinterest", "Pinterest"], ["social_linkedin", "LinkedIn"], ["gbp_post", "Google Business"]];
const REPURPOSABLE = new Set(["email", "landing", "blog", "social_instagram", "social_facebook", "social_pinterest", "social_linkedin", "gbp_post", "product_copy"]);
// Video pieces are template-driven since 2026-10-02 (DS: "videos and templates don't sync"; "just have templates pick in Make").
// The video entries stay so saved plans keep their labels and the engines keep their kind keys.
const MEDIA = [["hero", "Hero image", 6, "1:1"], ["lifestyle", "Lifestyle set (3)", 18, "16:9"], ["sceneset", "Scene set (4)", 24, "1:1"], ["video169", "Narrated video 16:9", 10, "16:9"], ["video916", "Reel 9:16", 10, "9:16"], ["cinematic", "Cinematic clip · Veo 8s", 40, "16:9"]];
const IMAGE_MEDIA = MEDIA.filter(([k]) => !["video169", "video916", "cinematic"].includes(k)); // Story images live under Make (make.jsx mirrors this list)
const VIDEO_KINDS = new Set(["video169", "video916", "cinematic"]);
// Blocks that are machine state, not story copy — never fed to the planner / repurposer as source text.
const NOT_COPY = /^(design_filled|social_media|comment|locale_meta|variant_history|piece_map|site_frame)$|image|link|video|previous_|transcript_pending|storyboard|builder_state|seo_brief|email_meta|locale_meta|variant_history|comment|safety/i;
const SCENE_PROMPTS = {
  lifestyle: ["Lifestyle photo, product in use, natural light", "Lifestyle photo, close detail of the product, editorial", "Lifestyle photo, wide scene with the product as the hero"],
  sceneset: ["Hero: the product alone, clean studio light", "Lifestyle: the product in use, natural light", "Detail: close crop of material, texture and finish", "Seasonal: the product in this season's setting, atmospheric light"]
};
const SCENE_LABELS = {
  lifestyle: ["In use", "Detail", "Wide"],
  sceneset: ["Hero", "Lifestyle", "Detail", "Seasonal"]
};
const PLAYBOOKS = [["launch", "Launch", {
  goal: "launch",
  audience: "retail",
  channels: ["product_copy", "landing", "blog", "email", "social_instagram", "social_facebook"],
  media: ["hero", "lifestyle", "video169"],
  angle: "New arrival: what it is, who it's for, why now. Lead with the one thing only this product does."
}], ["sale", "Sale / promo", {
  goal: "sell_down",
  audience: "retail",
  channels: ["email", "landing", "social_instagram", "social_facebook"],
  media: ["hero", "video916"],
  angle: "A clear offer with an end date. Say what's included, the saving and when it ends: nothing else."
}], ["restock", "Back in stock", {
  goal: "launch",
  audience: "subscribers",
  channels: ["email", "product_copy", "social_instagram"],
  media: ["hero"],
  angle: "It's back. Lead with the demand (it sold out), what changed if anything, and how to get it before it goes again."
}], ["seasonal", "Seasonal", {
  goal: "seasonal",
  audience: "retail",
  channels: ["blog", "email", "social_instagram", "social_facebook", "gbp_post"],
  media: ["lifestyle", "video169"],
  angle: "Tie the product to the moment: the season, the occasion, the weather: and what to do about it this week."
}], ["wholesale", "Wholesale reorder", {
  goal: "reorder",
  audience: "wholesale",
  channels: ["email", "landing", "product_copy", "social_linkedin"],
  media: ["hero"],
  angle: "Dealer-first: sell-through, margin, lead time, minimums. The facts a buyer needs to reorder, no consumer fluff."
}]];
// Brief "What it is for" (2026-10-09): one chip is the old playbook prefill and the goal together, in plain words.
const PURPOSES = [["launch", "Launch something new"], ["sale", "Run a sale or offer"], ["restock", "Back in stock"], ["seasonal", "Seasonal"], ["educate", "Teach or explain"], ["wholesale", "Dealer reorder"]];
const GOAL_TO_PURPOSE = {
  launch: "launch",
  sell_down: "sale",
  seasonal: "seasonal",
  reorder: "wholesale",
  educate: "educate"
};
// Brief "Where it goes": plain names and the order the mockup reads in. Make, Publish and Results keep LABEL.
const BRIEF_CHANNELS = [["landing", "Web page"], ["email", "Email"], ["social_instagram", "Instagram"], ["social_facebook", "Facebook"], ["blog", "Blog post"], ["social_linkedin", "LinkedIn"], ["social_pinterest", "Pinterest"], ["gbp_post", "Google Business"], ["product_copy", "Product page"]];
const BRIEF_LABEL = Object.fromEntries(BRIEF_CHANNELS);
// "Where it goes" video destinations: each makes its own video in the platform's shape and publishes there.
const VIDEO_DEST = {
  youtube: {
    label: "YouTube",
    media: "video169",
    aspect: "16:9",
    hint: "A narrated 16:9 video, published to your YouTube channel"
  },
  tiktok: {
    label: "TikTok",
    media: "video916",
    aspect: "9:16",
    hint: "A 9:16 reel, published to TikTok"
  }
};

// ---- "What the writers know": source blocks as readable cards (never raw JSON or a raw page dump).
const SRC_HIDE = new Set(["piece_map", "site_frame", "source_url"]); // machine state, or folded into the web page card
const plainText = h => String(h || "").replace(/<(br|\/p|\/div|\/li|\/h\d)[^>]*>/gi, "\n").replace(/<[^>]+>/g, " ").replace(/&nbsp;/g, " ").replace(/&amp;/g, "&").replace(/&quot;/g, "\"").replace(/&#39;/g, "'").replace(/[ \t]+/g, " ").replace(/\n\s*\n\s*\n+/g, "\n\n").trim();
// Description text for a video: links kept as "text url", line breaks kept, placeholders and writer labels dropped.
const captionText = h => plainText(String(h || "").replace(/<a\b[^>]*?href\s*=\s*["']?([^"'\s>]+)["']?[^>]*>(.*?)<\/a>/gis, (m, u, t) => {
  const x = t.replace(/<[^>]+>/g, "").trim();
  return !x || x.replace(/\/$/, "") === u.replace(/\/$/, "") ? u : `${x} ${u}`;
})).replace(/[ \t]*[:\-–—]?[ \t]*[\[{]\s*(link|url|landing[ _]?page|cta[ _]?link)\s*[\]}]/gi, "").replace(/^[ \t]*(hook|cta|call to action|caption|end card)[ \t]*:[ \t]*/gim, "").replace(/[ \t]+\n/g, "\n").replace(/\n[ \t]+/g, "\n").trim();
const oneLine = t => String(t || "").replace(/\s+/g, " ").trim();
const clip = (t, n) => {
  const s = oneLine(t);
  return s.length > n ? s.slice(0, n - 1).replace(/\s+\S*$/, "") + "…" : s;
};
const firstWords = (t, n) => {
  const w = oneLine(t).split(" ").filter(Boolean);
  return w.slice(0, n).join(" ") + (w.length > n ? "…" : "");
};
const hostPath = u => {
  try {
    const x = new URL(u);
    return (x.host.replace(/^www\./, "") + x.pathname.replace(/\/+$/, "")).slice(0, 80);
  } catch (e) {
    return String(u || "").replace(/^https?:\/\//, "").replace(/\/+$/, "").slice(0, 80);
  }
};
/** The first real sentence: skips menu and footer runs (short, mostly Capitalised words with no full stop). */
const firstSentence = (t, n) => {
  const s = oneLine(t).replace(/(^|\s)#{1,6}\s+/g, "$1").replace(/\*\*|__/g, "").trim();
  if (!s) return "";
  const parts = s.split(/(?<=[.!?])\s+/);
  const real = parts.find(p => {
    const w = p.split(" ");
    const caps = w.filter(x => /^[A-Z0-9]/.test(x)).length;
    return w.length >= 6 && /[a-z]{3,}/.test(p) && caps / w.length < 0.6;
  });
  return clip(real || parts[0], n || 140);
};
const tryJson = body => {
  const s = String(body || "").trim();
  if (!/^[[{]/.test(s)) return null;
  try {
    const j = JSON.parse(s);
    return j && typeof j === "object" ? j : null;
  } catch (e) {
    return null;
  }
};
const pickKey = (o, ...keys) => {
  for (const k of keys) {
    const hit = Object.keys(o).find(x => x.toLowerCase() === k.toLowerCase());
    if (hit && o[hit] != null && o[hit] !== "") return o[hit];
  }
  return null;
};
const asList = v => (Array.isArray(v) ? v : v != null ? [v] : []).map(x => x && typeof x === "object" ? pickKey(x, "text", "headline", "value") || "" : String(x)).map(oneLine).filter(Boolean);
const humanKey = k => String(k).replace(/([a-z])([A-Z])/g, "$1 $2").replace(/_/g, " ").toLowerCase().replace(/^\w/, c => c.toUpperCase());
const plural = (n, one, many) => `${n} ${n === 1 ? one : many || one + "s"}`;
/** A JSON body (imported ad rows, saved records) as a title, a one-line summary and readable lines. */
function describeJson(j) {
  const o = Array.isArray(j) ? j.find(x => x && typeof x === "object") : j;
  if (!o) return null;
  const heads = asList(pickKey(o, "headlines", "headline"));
  const descs = asList(pickKey(o, "descriptions", "description"));
  const urlV = pickKey(o, "finalUrls", "finalUrl", "url", "link");
  const url = Array.isArray(urlV) ? urlV[0] : urlV;
  if (heads.length || pickKey(o, "adGroup", "adType", "campaign")) {
    return {
      badge: "AD",
      title: heads[0] || "Ad",
      summary: ["Ad", heads.length ? plural(heads.length, "headline") : "", descs.length ? plural(descs.length, "description") : "", url ? hostPath(String(url)) : ""].filter(Boolean).join(" · "),
      lines: heads.map(h => `Headline: ${h}`).concat(descs.map(d => `Description: ${d}`), url ? [`Goes to: ${url}`] : [])
    };
  }
  const lines = Object.keys(o).filter(k => o[k] != null && o[k] !== "" && typeof o[k] !== "object").slice(0, 30).map(k => `${humanKey(k)}: ${clip(String(o[k]), 300)}`);
  const t = pickKey(o, "title", "name");
  return {
    badge: "DAT",
    title: t ? clip(String(t), 80) : "Saved details",
    summary: [plural(lines.length, "detail"), Array.isArray(j) && j.length > 1 ? plural(j.length, "item") : ""].filter(Boolean).join(" · "),
    lines
  };
}
/** Lines of a "Key: value" fact sheet (product_facts, offer) as a map. */
const factMap = text => {
  const m = {};
  String(text || "").split("\n").forEach(l => {
    const i = l.indexOf(":");
    if (i > 0 && i < 40) m[l.slice(0, i).trim().toLowerCase()] = l.slice(i + 1).trim();
  });
  return m;
};
/** One source block as a card: { badge, title, summary, text | lines }. */
function describeBlock(x, url) {
  const kind = String(x.kind || "").toLowerCase();
  const json = tryJson(x.body);
  if (json) {
    const d = describeJson(json);
    if (d) return d;
  }
  const text = plainText(x.body);
  if (kind === "webpage") return {
    badge: "WEB",
    title: url ? hostPath(url) : firstWords(firstSentence(text, 80), 8) || "Web page",
    summary: ["Web page", firstSentence(text, 120)].filter(Boolean).join(" · "),
    text,
    url
  };
  if (kind === "product_facts") {
    const f = factMap(text);
    return {
      badge: "PRD",
      image: x.assetRef || null,
      title: f.product || firstWords(text, 6),
      summary: ["Product", f.price, f.colors || f.color, f.availability].filter(Boolean).map(s => clip(s, 40)).join(" · "),
      text
    };
  }
  if (kind === "document") {
    const m = text.match(/^From the document (.+?):\s*\n?/);
    const name = m ? m[1] : "Document";
    const rest = m ? text.slice(m[0].length) : text;
    const ext = (name.match(/\.([a-z0-9]+)$/i) || [])[1] || "";
    return {
      badge: /^pdf$/i.test(ext) ? "PDF" : /^docx?$/i.test(ext) ? "DOC" : "TXT",
      title: name,
      summary: ["Document", firstSentence(rest, 110)].filter(Boolean).join(" · "),
      text: rest
    };
  }
  if (kind === "reference") {
    const m = text.match(/^From the piece "(.+?)":\s*\n?/);
    const rest = m ? text.slice(m[0].length) : text;
    return {
      badge: "REF",
      title: m ? m[1] : "An earlier piece",
      summary: ["From an earlier piece", firstSentence(rest, 110)].filter(Boolean).join(" · "),
      text: rest
    };
  }
  if (kind === "review") {
    const m = text.match(/^(★+)\s*"(.*)":\s*(.+?)\s*\((.+?)\)\s*$/s);
    return {
      badge: "REV",
      title: m ? `${m[1].length}-star review from ${clip(m[3], 40)}` : "Customer review",
      summary: m ? `"${clip(m[2], 110)}"` : firstSentence(text, 120),
      text
    };
  }
  if (kind === "social_proof") return {
    badge: "REV",
    title: clip(text.split(":")[0], 60) || "Product rating",
    summary: clip(text.split(":").slice(1).join(":"), 120) || "Product rating",
    text
  };
  if (kind === "screenshot_note") {
    const [head, ...rest] = text.split("\n");
    return {
      badge: "IMG",
      image: x.assetRef || null,
      title: clip(head, 70) || "Picture",
      summary: ["Picture", firstSentence(rest.join(" "), 110)].filter(Boolean).join(" · "),
      text: rest.join("\n") || text
    };
  }
  if (kind === "offer") {
    const f = factMap(text);
    return {
      badge: "TXT",
      title: "The offer",
      summary: [f["dates"], f["price or discount"], f["code"] ? `code ${f["code"]}` : ""].filter(Boolean).map(s => clip(s, 40)).join(" · ") || firstSentence(text, 120),
      text
    };
  }
  const named = {
    subject_line: "Subject line",
    meta_description: "Page description",
    copy: "Copy"
  }[kind];
  return {
    badge: "TXT",
    title: named || firstWords(text, 7),
    summary: named ? clip(text, 120) : plural(oneLine(text).split(" ").length, "word"),
    text
  };
}
const byId = (a, b) => {
  const x = String(a.id || ""),
    y = String(b.id || "");
  return x.length - y.length || x.localeCompare(y);
};
const relDay = iso => {
  const t = Date.parse(iso || "");
  if (!t) return "";
  const d = new Date(t);
  const now = new Date();
  const day = x => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((day(now) - day(d)) / 86400000);
  return diff <= 0 ? "today" : diff === 1 ? "yesterday" : diff < 7 ? `${diff} days ago` : d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric"
  });
};
const SOCIAL_CHANNELS = new Set(["social_instagram", "social_facebook", "social_pinterest", "social_linkedin", "gbp_post"]);
const SEO_CHANNELS = new Set(["blog", "landing", "web", "website", "product_copy"]);
const LABEL = Object.fromEntries(CHANNELS.concat([["web", "Website"], ["website", "Website"], ["video", "Video"]]));
const label = ch => LABEL[String(ch || "").toLowerCase()] || String(ch || "–").replace(/_/g, " ");
const TARGET = {
  shopify_product: "Shopify · product description",
  shopify_article: "Shopify · blog post",
  shopify_page: "Shopify · page",
  shopify_media: "Shopify · product media",
  email_draft: "Plunk · email automation",
  email: "Plunk · email",
  instagram: "Instagram · post",
  facebook: "Facebook · post",
  meta: "Facebook / Instagram",
  gbp: "Google Business · post",
  gbp_post: "Google Business · post",
  pinterest: "Pinterest · pin",
  linkedin: "LinkedIn · post",
  youtube: "YouTube · video",
  tiktok: "TikTok · video",
  wordpress: "WordPress",
  custom_api: "Custom API",
  website: "Website",
  shopify_translate: "Shopify · translation",
  shopify_translation: "Shopify · translation"
};
const targetLabel = t => TARGET[String(t || "").toLowerCase()] || label(t);
const statusColor = {
  draft: "var(--plk-text-muted)",
  approved: "var(--plk-info-text)",
  published: "var(--plk-success-text)",
  live: "var(--plk-success-text)",
  retired: "var(--plk-text-subtle)"
};
const Chip = ({
  text,
  color,
  bg,
  cls
}) => /*#__PURE__*/React.createElement("span", {
  className: "plk-chip " + (cls || (color || bg ? "" : "solid")),
  style: color || bg ? {
    color: color || "var(--plk-text-inverse)",
    background: bg || "var(--plk-text-muted)"
  } : undefined
}, text);
const STATUS_CLS = {
  draft: "solid",
  approved: "solid info",
  published: "solid ok",
  live: "solid ok",
  retired: "solid muted"
};
const Status = ({
  s
}) => /*#__PURE__*/React.createElement(Chip, {
  text: String(s || "–").toLowerCase(),
  cls: STATUS_CLS[String(s || "").toLowerCase()] || "solid"
});
const Btn = ({
  primary,
  story,
  flat,
  children,
  ...p
}) => /*#__PURE__*/React.createElement("button", _extends({
  className: "k-button k-button-sm k-rounded-md " + (primary ? "k-button-solid k-button-solid-primary" : flat ? "k-button-flat" : "k-button-solid k-button-solid-base"),
  style: story ? {
    background: "var(--plk-accent)",
    borderColor: "var(--plk-accent)",
    color: "var(--plk-text-inverse)"
  } : undefined
}, p), children);
const inp = INPUT;
const card = CARD;
const dayOffset = n => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};
// A real trailing-30-day window as a custom range token (the named "last_30_days" is not a DateTimeRange preset — it
// silently collapsed to today, which hid every stat row older than a day in Measure).
const last30 = () => `custom_${dayOffset(-30).replace(/-/g, "")}_${dayOffset(0).replace(/-/g, "")}`;
// Path of a page URL for matching against Website Analytics rows (host and query dropped, trailing slash trimmed).
const pathKey = u => {
  if (!u) return "";
  try {
    const x = new URL(u, "https://x");
    return (x.pathname || "/").replace(/\/+$/, "") || "/";
  } catch (e) {
    return String(u).split("?")[0].replace(/\/+$/, "") || "/";
  }
};
// Blocks come back ORDER BY kind, version DESC — the newest version of a kind is FIRST. Pick by version, never by position.
// ---- No lost edits (CONTENT_HUB_REVIEW_DESK phase 1): a piece's version is the SHA-256 of title + U+0001 + body, the
// same hash ContentController.VariantHash computes. A save sends the hash it started from; a 409 means someone saved first.
async function textHash(title, body) {
  const data = new TextEncoder().encode(String(title || "") + "\u0001" + String(body || ""));
  const buf = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(buf)).map(x => x.toString(16).padStart(2, "0")).join("");
}
const forVariant = v => x => {
  try {
    return String(JSON.parse(x.body || "{}").variantId) === String(v.id);
  } catch (e) {
    return false;
  }
};
const parseJson = b => {
  if (!b) return null;
  try {
    return JSON.parse(b.body || "{}");
  } catch (e) {
    return null;
  }
};
const latestBlock = (blocks, kind, test) => (blocks || []).filter(b => String(b.kind || "") === kind && (!test || test(b))).sort((a, b) => (Number(b.version) || 0) - (Number(a.version) || 0))[0];
class ContentBuilder extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "loadFunnels", async () => {
      const funnels = {};
      await Promise.all(this.state.members.map(async m => {
        funnels[String(m.id)] = await safe(svc(`/content/containers/detail/${String(m.id)}/funnel/${this.measureRange()}`).getAsync(), null);
      }));
      if (this._alive) this.setState({
        funnels
      });
    });
    // Content Hub templates (CONTENT_HUB_TEMPLATES_2026-10-01): starters + the workspace's own + this story's.
    _defineProperty(this, "loadTemplates", async () => {
      const r = await safe(svc(`/content/templates?storyId=${encodeURIComponent(this.state.storyId)}`).getAsync(), {
        items: []
      });
      if (this._alive) this.setState({
        templates: r && r.items || []
      });
    });
    _defineProperty(this, "toggleTemplate", (b, t, setB) => {
      const key = String(t.channel || "any").toLowerCase();
      const cur = {
        ...(b && b.templates || {})
      };
      const on = cur[key] === String(t.id);
      if (on) delete cur[key];else cur[key] = String(t.id);
      const patch = {
        templates: cur
      };
      if (!on) {
        if (CHANNELS.some(([k]) => k === key) && !b.channels.includes(key)) patch.channels = [...b.channels, key];
        const mk = mediaRowFor(t);
        if (mk && !b.media.includes(mk)) patch.media = [...b.media, mk];
      }
      setB(patch);
    });
    _defineProperty(this, "markUsed", async (templateId, q) => {
      if (!templateId) return;
      try {
        await svc(`/content/templates/${String(templateId)}/used?${q}`).postAsync({});
      } catch (e) {/* bookkeeping only */}
    });
    _defineProperty(this, "load", async () => {
      const sid = this.state.storyId;
      if (!sid) {
        this.setState({
          loading: false,
          error: "No story id."
        });
        return;
      }
      try {
        const [list, ob, brand, conns, social, assets, billing, audiencesRaw, sendTime] = await Promise.all([this.loadAllContainers(last30()), getOnboardingStatus(false).catch(() => null), loadBrandKit(false).catch(() => null), safe(svc("/content/connectors").getAsync(), {
          items: []
        }), safe(svc("/social/accounts").getAsync(), {
          items: []
        }), safe(svc("/content/assets?kind=image&$top=60").getAsync(), {
          items: []
        }), getBillingStatus(false).catch(() => null), safe(svc("/audience").getAsync(), {
          items: []
        }), safe(svc("/content/email/send-time").getAsync(), null)]);
        const rows = list && list.items || [];
        let members = rows.filter(c => String(c.storyId) === sid);
        if (!members.length) {
          const one = rows.find(c => String(c.id) === sid);
          if (one) members = [one];
        }
        const details = {};
        for (const m of members) details[String(m.id)] = await safe(svc(`/content/containers/detail/${String(m.id)}`).getAsync(), null);
        const funnels = {};
        await Promise.all(members.map(async m => {
          funnels[String(m.id)] = await safe(svc(`/content/containers/detail/${String(m.id)}/funnel/${this.measureRange()}`).getAsync(), null);
        }));
        const recsRaw = await safe(svc("/content/recommendations").getAsync(), {
          items: []
        });
        if (!members.length && !details[sid]) {
          const d = await safe(svc(`/content/containers/detail/${sid}`).getAsync(), null);
          if (d && d.container) {
            members = [{
              id: d.container.id,
              title: d.container.title,
              storyId: sid
            }];
            details[sid] = d;
          }
        }
        if (!members.length) {
          this.setState({
            loading: false,
            error: "That story is not in the library."
          });
          return;
        }
        const primary = members.find(m => String(m.id) === sid) || members[0];
        const pd = details[String(primary.id)];
        let bs = this.state.bs;
        const saved = latestBlock(pd && pd.blocks || [], "builder_state");
        if (saved) {
          try {
            bs = {
              ...bs,
              ...JSON.parse(saved.body || "{}")
            };
          } catch (e) {/* fresh */}
        }
        // a bell link (?piece=<variant id>) opens Make on that piece with its notes open (phase 3)
        let deepPiece = null;
        try {
          deepPiece = new URLSearchParams(window.location.search).get("piece");
        } catch (e) {/* no query */}
        this._deepPiece = deepPiece || null;
        const stage = deepPiece ? "make" : LEGACY_STAGE[bs.stage] || bs.stage || "brief";
        const credits = billing && typeof (billing.creditBalance ?? billing.balance) !== "undefined" ? Number(billing.creditBalance ?? billing.balance) : null;
        if (!this._alive) return;
        this.setState({
          loading: false,
          members,
          details,
          primary,
          bs,
          stage,
          brand,
          shopify: ob && ob.shopify ? {
            shop: ob.shop
          } : null,
          connectors: conns && conns.items || [],
          social: social && social.items || [],
          assets: assets && assets.items || [],
          credits,
          creditsLevel: billing && billing.monthlyCredits != null ? Number(billing.monthlyCredits) : null,
          plan: bs.plan || null,
          mediaOut: bs.mediaOut && typeof bs.mediaOut === "object" ? bs.mediaOut : {},
          // generated media survives a reload (2026-09-29)
          audiences: (Array.isArray(audiencesRaw) ? audiencesRaw : audiencesRaw && (audiencesRaw.items || audiencesRaw.value) || []).filter(a => a && a.name),
          sendTime: sendTime && sendTime.suggested ? sendTime : null,
          funnels,
          recs: recsRaw && recsRaw.items || []
        });
        if (ob && ob.shopify) this.loadMarkets();
        this.detectWholesale();
      } catch (e) {
        if (this._alive) this.setState({
          loading: false,
          error: e && e.message || "Could not load the story."
        });
      }
    });
    // Shopify Markets + published languages (slice 1) — drives "Translate for a market" and the Ship translation target.
    _defineProperty(this, "loadMarkets", async () => {
      const shop = this.state.shopify && this.state.shopify.shop;
      if (!shop) return;
      try {
        const base = String(endpoints.product.root || "").replace(/\/v1\/?$/, "");
        const h = await bearerHeaders();
        const r = await fetch(`${base}/shopify/markets?shop=${encodeURIComponent(shop)}`, {
          headers: h
        });
        const j = await r.json().catch(() => ({}));
        if (r.ok && this._alive) this.setState({
          markets: j
        });
      } catch (e) {/* no markets: the translate control stays hidden */}
    });
    _defineProperty(this, "reloadDetails", async () => {
      const details = {
        ...this.state.details
      };
      for (const m of this.state.members) details[String(m.id)] = await safe(svc(`/content/containers/detail/${String(m.id)}`).getAsync(), details[String(m.id)]);
      this._detailsAt = Date.now();
      if (this._alive) this.setState({
        details,
        ship: null,
        safety: {}
      }); // Ship rows derive from approvals: rebuild on next render; safety re-checks edited copy
      if (this.teamOn()) this.loadReviews(); // a save can reset sign-offs (phase 4)
    });
    /** Save a piece's title/body without overwriting someone else's save. Returns true when the text was saved.
     *  The base is the text this person last saw: their own last save if it is newer than the last reload, else the
     *  piece as loaded. A 409 opens the conflict sheet (keep mine / keep theirs / merge), which settles the promise. */
    _defineProperty(this, "saveText", async (v, patch) => {
      const id = String(v.id);
      const title = patch.title != null ? patch.title : v.title || "";
      const body = patch.body != null ? patch.body : v.body || "";
      const own = (this._saved || {})[id];
      const base = own && own.at > (this._detailsAt || 0) ? own : {
        title: v.title,
        body: v.body
      };
      const send = async (baseHash, chosen) => {
        const h = await bearerHeaders();
        h["content-type"] = "application/json";
        const t = chosen || {
          title,
          body
        };
        const r = await fetch(`${String(endpoints.product.root || "")}/content/variants/${id}`, {
          method: "PUT",
          headers: h,
          body: JSON.stringify({
            title: t.title,
            body: t.body,
            baseHash
          })
        });
        const text = await r.text();
        let j = {};
        try {
          j = JSON.parse(text);
        } catch (e) {/* not json */}
        return {
          status: r.status,
          ok: r.ok,
          j
        };
      };
      try {
        let res = await send(await textHash(base.title, base.body));
        if (res.status === 409 && res.j && res.j.current) {
          const choice = await new Promise(resolve => this.setState({
            conflict: {
              v,
              mine: {
                title,
                body
              },
              theirs: res.j.current,
              by: res.j.by,
              at: res.j.at,
              merge: null,
              resolve
            }
          }));
          this.setState({
            conflict: null
          });
          if (!choice) {
            await this.reloadDetails();
            return false;
          } // keep theirs, or cancel
          res = await send(res.j.current.hash, choice); // keep mine / the merged copy, over theirs
          if (res.status === 409) {
            toast("It changed again while you were choosing. Open it once more to compare.");
            await this.reloadDetails();
            return false;
          }
          if (res.ok) {
            (this._saved = this._saved || {})[id] = {
              title: choice.title,
              body: choice.body,
              at: Date.now()
            };
            return true;
          }
        }
        if (!res.ok) {
          toast(res.j && (res.j.error || res.j.message) || "Could not save that piece.");
          return false;
        }
        (this._saved = this._saved || {})[id] = {
          title,
          body,
          at: Date.now()
        };
        return true;
      } catch (e) {
        console.warn("[saveText]", e);
        toast("Could not save that piece.");
        return false;
      }
    });
    // ---- Presence (phase 1): who else has this story open and who is typing in which piece. Heartbeat every 30 s while
    // the tab is visible; Make reports the selected piece and whether its editor is open via setPresence().
    _defineProperty(this, "setPresence", (variantId, editing) => {
      const next = {
        variantId: variantId ? String(variantId) : null,
        editing: !!editing
      };
      const cur = this._pres || {};
      this._pres = next;
      if (cur.variantId !== next.variantId || cur.editing !== next.editing) this.beat();
    });
    _defineProperty(this, "beat", async leave => {
      const sid = String(this.state.storyId || this.state.primary && (this.state.primary.storyId || this.state.primary.id) || "");
      if (!sid) return;
      if (!leave && typeof document !== "undefined" && document.hidden) return;
      try {
        const r = await svc(`/content/stories/${encodeURIComponent(sid)}/presence`).postAsync({
          ...(this._pres || {}),
          leave: !!leave
        });
        if (this._alive && r && Array.isArray(r.others)) this.setState({
          presence: r.others
        });
      } catch (e) {/* presence is best-effort */}
    });
    // ---- Team (phase 3, CONTENT_HUB_REVIEW_DESK §4.1-4.2): story owner, Copy / Media assignees with due dates, notes that
    // point at words, threads, @mentions, resolve. All content blocks (latest `assignment` per role wins). Assigning,
    // mentioning and replying reach people's bells. A solo workspace (one person) sees none of it.
    _defineProperty(this, "loadTeam", async () => {
      const r = await safe(svc("/content/team").getAsync(), null);
      if (this._alive && r) {
        this.setState({
          team: r
        });
        if ((r.users || []).length > 1) this.loadReviews();
      }
    });
    _defineProperty(this, "assign", async (v, role, assignee, due) => {
      const cid = v ? v.containerId : this.state.primary && this.state.primary.id;
      if (!cid) return false;
      try {
        await svc(`/content/containers/${String(cid)}/assign`).postAsync({
          variantId: v ? String(v.id) : null,
          role,
          assignee: assignee || "",
          due: due || null,
          storyId: String(this.state.storyId || "")
        });
        await this.reloadDetails();
        return true;
      } catch (e) {
        toast(e && e.message || "Could not assign that.");
        return false;
      }
    });
    _defineProperty(this, "postNote", async (v, {
      text,
      anchor,
      parentId,
      mentions
    }) => {
      try {
        await svc(`/content/containers/${String(v.containerId)}/comments`).postAsync({
          variantId: String(v.id),
          text,
          anchor: anchor || null,
          parentId: parentId || null,
          mentions: mentions || [],
          storyId: String(this.state.storyId || "")
        });
        await this.reloadDetails();
        return true;
      } catch (e) {
        toast(e && e.message || "Could not post the note.");
        return false;
      }
    });
    _defineProperty(this, "loadReviews", async () => {
      const sid = this.storyIdStr();
      if (!sid) return;
      const r = await safe(svc(`/content/stories/${sid}/reviews`).getAsync(), null);
      if (this._alive && r) this.setState({
        reviews: r
      });
    });
    _defineProperty(this, "requestReview", async body => {
      try {
        await svc(`/content/stories/${this.storyIdStr()}/reviews`).postAsync(body);
        await this.loadReviews();
        toast("Review requested. The reviewers will see it in their notifications.");
        return true;
      } catch (e) {
        toast(e && e.message || "Could not request the review.");
        return false;
      }
    });
    _defineProperty(this, "castVote", async (reviewId, v, vote, note) => {
      try {
        await svc(`/content/reviews/${reviewId}/votes`).postAsync({
          variantId: String(v.id),
          vote,
          note: note || ""
        });
        await this.reloadDetails();
        await this.loadReviews();
        return true;
      } catch (e) {
        toast(e && e.message || "Could not record that.");
        return false;
      }
    });
    _defineProperty(this, "cancelReview", async reviewId => {
      try {
        await svc(`/content/reviews/${reviewId}/cancel`).postAsync({});
        await this.loadReviews();
        return true;
      } catch (e) {
        toast(e && e.message || "Could not cancel the review.");
        return false;
      }
    });
    _defineProperty(this, "hashOf", (title, body) => textHash(title, body));
    _defineProperty(this, "resolveNote", async (id, resolved) => {
      try {
        await svc(`/content/comments/${id}/resolve`).putAsync({
          resolved
        });
        await this.reloadDetails();
        return true;
      } catch (e) {
        toast(e && e.message || "Could not change that thread.");
        return false;
      }
    });
    // Generated media is part of the saved Builder state, so previews are still there after a reload.
    _defineProperty(this, "setMedia", patch => {
      const mediaOut = {
        ...this.state.mediaOut,
        ...patch
      };
      this.setState({
        mediaOut
      });
      this.saveState({
        mediaOut
      });
    });
    _defineProperty(this, "saveState", async patch => {
      const bs = {
        ...this.state.bs,
        ...patch,
        stage: patch && patch.stage || this.state.stage
      };
      this.setState({
        bs
      });
      try {
        await svc(`/content/containers/${String(this.state.primary.id)}/blocks`).postAsync({
          kind: "builder_state",
          body: JSON.stringify(bs)
        });
      } catch (e) {/* stays in memory */}
    });
    _defineProperty(this, "go", stage => {
      this._primaryClick = null;
      this.setState({
        stage,
        ship: stage === "ship" ? null : this.state.ship,
        primaryOverride: null,
        moreOpen: false
      });
      this.saveState({
        stage
      });
      window.scrollTo(0, 0);
    });
    // ---------------------------------------------------------------- agents (same rails as the Content view; charged at enqueue)
    _defineProperty(this, "runAgent", async (agent, userMessage, parameters) => {
      const base = String(endpoints.product.root || "");
      const h = await bearerHeaders();
      h["content-type"] = "application/json";
      const r = await fetch(`${base}/agent/run`, {
        method: "POST",
        headers: h,
        body: JSON.stringify({
          agent,
          instruction: userMessage,
          parameters
        })
      });
      const text = await r.text();
      let j = {};
      try {
        j = JSON.parse(text);
      } catch (e) {/* not json */}
      if (r.status === 402) {
        const e = new Error(j && j.error || "Out of generation credits.");
        e.outOfCredits = true;
        throw e;
      }
      if (!r.ok) throw new Error(j && j.error || `Agent run failed (${r.status})`);
      return j;
    });
    _defineProperty(this, "waitAgent", async (runId, everyMs, maxTries) => {
      const base = String(endpoints.product.root || "");
      for (let i = 0; i < maxTries && this._alive; i++) {
        await new Promise(res => setTimeout(res, everyMs));
        try {
          const h = await bearerHeaders();
          const r = await fetch(`${base}/agent/run/${encodeURIComponent(runId)}`, {
            headers: h
          });
          const run = await r.json();
          const status = String(run.status || run.Status || "").toLowerCase();
          if (["completed", "complete", "succeeded", "success"].includes(status)) {
            let ctx = {};
            try {
              ctx = JSON.parse(run.contextOutput || run.ContextOutput || "{}");
            } catch (e) {/* ignore */}
            let res = {};
            try {
              res = JSON.parse(run.runResult || run.RunResult || "{}");
            } catch (e) {/* ignore */}
            return {
              ok: true,
              ctx,
              res,
              run
            };
          }
          if (["failed", "error", "cancelled"].includes(status)) return {
            ok: false,
            run
          };
        } catch (e) {/* retry */}
      }
      return {
        ok: false,
        timeout: true
      };
    });
    _defineProperty(this, "agentError", (e, fallback) => {
      if (e && e.outOfCredits) toast("Out of generation credits: add a credit pack under Billing & Usage (account.plunk.io).");else toast(e && e.message || fallback);
    });
    // ---------------------------------------------------------------- Brief
    // Wholesale workspaces get the dealer / trade goal and audiences. Explicit signals only: the account's
    // businessModel (manufacturing / both) or the territoryRep feature; a retail Shopify store stays retail.
    _defineProperty(this, "detectWholesale", async () => {
      let wholesale = false;
      try {
        wholesale = !!hasTenantFeature("territoryRep");
      } catch (e) {/* no registry */}
      if (!wholesale) {
        try {
          const tid = (account.get() || {}).tenantId;
          const rows = tid ? await safe(svc(`/account/${tid}`).getAsync(), null) : null;
          const row = Array.isArray(rows) ? rows[0] : rows;
          const bm = String(row && (row.businessModel || row.BusinessModel) || "").toLowerCase();
          wholesale = bm === "manufacturing" || bm === "both";
        } catch (e) {/* unknown → retail */}
      }
      if (this._alive) this.setState({
        wholesale
      });
    });
    /** Brief edits save on their own (debounced), so leaving the page before planning keeps them. */
    _defineProperty(this, "queueBriefSave", () => {
      clearTimeout(this._briefT);
      this._briefT = setTimeout(() => {
        this._briefT = null;
        if (this._alive) this.saveState({
          brief: this.state.bs.brief
        });
      }, 2000);
    });
    _defineProperty(this, "setBrief", patch => {
      const brief = {
        ...this.state.bs.brief,
        ...patch
      };
      this.setState({
        bs: {
          ...this.state.bs,
          brief
        }
      });
      this.queueBriefSave();
    });
    _defineProperty(this, "pickPurpose", k => {
      const b = this.state.bs.brief;
      const pb = PLAYBOOKS.find(([x]) => x === k);
      if (!pb) {
        this.setBrief({
          goal: k === "educate" ? "educate" : b.goal,
          playbook: k
        });
        return;
      }
      // the preset's angle only replaces an empty one or another preset's, never what the user wrote
      const own = b.angle && !PLAYBOOKS.some(([,, p]) => p.angle === b.angle);
      this.setBrief({
        ...pb[2],
        playbook: k,
        angle: own ? b.angle : pb[2].angle
      });
    });
    // YouTube / TikTok chips: a video in that platform's shape (YouTube 16:9 narrated, TikTok 9:16 reel), made for it.
    _defineProperty(this, "toggleVideoDest", dest => {
      const b = this.state.bs.brief;
      const cur = {
        ...(b && b.templates || {})
      };
      const key = `video:${dest}`;
      if (cur[key]) {
        delete cur[key];
        this.setBrief({
          templates: cur
        });
        return;
      }
      const opts = this.videoTemplateOptions();
      const want = VIDEO_DEST[dest].media;
      const shaped = opts.filter(t => mediaRowFor(t) === want);
      const t = shaped.find(x => x.scope === "plunk") || shaped[0] || opts.find(x => x.scope === "plunk") || opts[0];
      if (!t) return;
      this.setBrief({
        templates: {
          ...cur,
          [key]: String(t.id)
        }
      });
    });
    _defineProperty(this, "toggleVideo", () => {
      const b = this.state.bs.brief;
      const cur = {
        ...(b && b.templates || {})
      };
      if (this.videoPicks(b).some(x => !x.slot)) {
        Object.keys(cur).forEach(k => {
          if (k.startsWith("video:") && !VIDEO_DEST[k.slice(6)]) delete cur[k];
        });
        this.setBrief({
          templates: cur
        });
        return;
      }
      const opts = this.videoTemplateOptions();
      const t = opts.find(x => x.scope === "plunk") || opts[0];
      if (!t) return;
      this.setBrief({
        templates: {
          ...cur,
          [`video:${String(t.id)}`]: String(t.id)
        }
      });
    });
    _defineProperty(this, "toggleAdd", k => this.setState({
      srcAdd: this.state.srcAdd === k ? null : k
    }));
    /** Take a fact off the Brief (the writers stop using it on the next plan or rewrite). */
    _defineProperty(this, "removeCard", async c => {
      if (!c || !c.ids || !c.ids.length || !c.cid) return;
      this.setBusy(`rm:${c.key}`, true);
      try {
        const r = await svc(`/content/containers/${c.cid}/blocks?ids=${encodeURIComponent(c.ids.join(","))}`).deleteAsync();
        if (!r || !(Number(r.removed) > 0)) {
          toast("That one could not be removed.");
          return;
        }
        if (this._alive) this.setState({
          srcRemove: null
        });
        await this.reloadDetails();
        toast("Removed. The writers will not use it from now on.");
      } catch (e) {
        toast(e && e.message || "That one could not be removed.");
      } finally {
        this.setBusy(`rm:${c.key}`, false);
      }
    });
    _defineProperty(this, "addWebPage", async () => {
      const u = (this.state.webDraft || "").trim();
      if (!/^https?:\/\/\S+\.\S+/i.test(u)) {
        toast("Paste the full address, starting with https://");
        return;
      }
      const cid = String(this.state.primary.id);
      this.setBusy("srcweb", true);
      try {
        // ids exceed 2^53: the container id goes in as a raw number literal (postAsync sends a string body verbatim)
        await svc("/content/import").postAsync(JSON.stringify({
          url: u,
          readOnly: true
        }).replace(/}$/, /^\d+$/.test(cid) ? `,"containerId":${cid}}` : `,"containerId":${JSON.stringify(cid)}}`));
        if (this._alive) this.setState({
          webDraft: "",
          srcAdd: null
        });
        await this.reloadDetails();
        toast("Page added: the writers can use it now.");
      } catch (e) {
        toast(e && e.message || "Could not read that page.");
      } finally {
        this.setBusy("srcweb", false);
      }
    });
    _defineProperty(this, "findProducts", q => {
      this.setState({
        prodQ: q
      });
      clearTimeout(this._prodT);
      if (q.trim().length < 2) {
        this.setState({
          prodHits: []
        });
        return;
      }
      this._prodT = setTimeout(async () => {
        const hits = await safe(searchProducts(q.trim(), 8), []);
        if (this._alive && this.state.prodQ === q) this.setState({
          prodHits: hits || []
        });
      }, 250);
    });
    _defineProperty(this, "addProduct", async p => {
      this.setBusy(`prod:${p.id}`, true);
      try {
        const r = await svc(`/content/containers/${String(this.state.primary.id)}/products`).postAsync({
          productIds: [String(p.id)]
        });
        if (r && Number(r.added) === 0) {
          toast("That product could not be found in the catalog.");
          return;
        }
        await this.reloadDetails();
        toast(`${p.title || "Product"} added: its facts are now in what the writers know.`);
      } catch (e) {
        toast(e && e.message || "Could not add the product.");
      } finally {
        this.setBusy(`prod:${p.id}`, false);
      }
    });
    _defineProperty(this, "addDocument", async f => {
      if (!f) return;
      if (!/\.(pdf|docx|txt|md)$/i.test(f.name || "")) {
        toast("Add a PDF, Word (.docx), .txt or .md file.");
        return;
      }
      this.setBusy("srcdoc", true);
      try {
        const pre = await svc("/content/assets/upload-url").postAsync({
          fileName: f.name,
          contentType: f.type || "application/octet-stream",
          bytes: f.size
        });
        if (!pre || !pre.uploadUrl) throw new Error("Could not prepare the upload.");
        const put = await fetch(pre.uploadUrl, {
          method: "PUT",
          headers: {
            "content-type": pre.contentType || f.type || "application/octet-stream"
          },
          body: f
        });
        if (!put.ok) throw new Error(`Upload failed (${put.status})`);
        const r = await svc(`/content/containers/${String(this.state.primary.id)}/documents`).postAsync({
          url: pre.url,
          fileName: f.name
        });
        await this.reloadDetails();
        toast(`${f.name} added${r && r.truncated ? ": it is long, so the first part is used" : ""}.`);
      } catch (e) {
        toast(e && e.message || "That document could not be read.");
      } finally {
        this.setBusy("srcdoc", false);
        if (this._docInput) this._docInput.value = "";
      }
    });
    _defineProperty(this, "addKnown", async () => {
      const body = (this.state.knownDraft || "").trim();
      const p = this.state.primary;
      if (!body || !p) return;
      this.setState({
        busy: {
          ...this.state.busy,
          known: true
        }
      });
      try {
        const block = await svc(`/content/containers/${String(p.id)}/blocks`).postAsync({
          kind: "text",
          body
        });
        const key = String(p.id);
        const d = this.state.details[key] || {};
        const added = block && (block.id || block.Id) ? block : {
          id: Date.now(),
          kind: "text",
          body
        };
        if (this._alive) this.setState({
          knownDraft: "",
          details: {
            ...this.state.details,
            [key]: {
              ...d,
              blocks: [...(d.blocks || []), added]
            }
          }
        });
        toast("Added: the plan and drafts will use it.");
      } catch (e) {
        toast(e && e.message || "Could not add the notes.");
      } finally {
        if (this._alive) this.setState({
          busy: {
            ...this.state.busy,
            known: false
          }
        });
      }
    });
    _defineProperty(this, "pullReviews", async () => {
      this.setBusy("reviews", true);
      try {
        const cid = String(this.state.primary.id);
        const [gbp, shop] = await Promise.all([safe(svc("/social/reviews?$top=60").getAsync(), {
          items: []
        }), this.shopifyReviews()]);
        const seen = new Set(this.allBlocks().filter(b => b.kind === "review" || b.kind === "social_proof").map(b => strip(b.body)));
        let added = 0;
        const quotes = (gbp && gbp.items || []).filter(r => strip(r.text || r.Text) && Number(r.rating || r.Rating || 0) >= 4).slice(0, 8);
        for (const q of quotes) {
          const stars = Math.max(1, Math.min(5, Number(q.rating || q.Rating) || 5));
          const body = `${"★".repeat(stars)} "${strip(q.text || q.Text).slice(0, 400)}": ${q.author || q.Author || "a customer"}${q.locationRef || q.LocationRef ? `, ${q.locationRef || q.LocationRef}` : ""} (Google review)`;
          if (seen.has(strip(body))) continue;
          await safe(svc(`/content/containers/${cid}/blocks`).postAsync({
            kind: "review",
            body
          }), null);
          added++;
        }
        for (const p of shop && shop.items || []) {
          if (p.rating == null) continue;
          const body = `${p.title}: rated ${Number(p.rating).toFixed(1)} out of ${p.scaleMax || 5}${p.count ? ` by ${num(p.count)} customers` : ""} (Shopify product reviews)`;
          if (seen.has(strip(body))) continue;
          await safe(svc(`/content/containers/${cid}/blocks`).postAsync({
            kind: "social_proof",
            body
          }), null);
          added++;
        }
        toast(added ? `${added} review${added === 1 ? "" : "s"} / rating${added === 1 ? "" : "s"} added as source material.` : this.reviewCount() ? "Nothing new: the story already has every review." : "No reviews found: connect Google Business under Manage Data › Connect, or add product reviews in Shopify.");
        await this.reloadDetails();
      } catch (e) {
        toast(e && e.message || "Could not pull reviews.");
      } finally {
        this.setBusy("reviews", false);
      }
    });
    _defineProperty(this, "shopifyReviews", async () => {
      const shop = this.state.shopify && this.state.shopify.shop;
      if (!shop) return null;
      const ids = this.productLinks().map(l => String(l.refId || l.ref_id || "")).filter(Boolean);
      if (!ids.length) return null;
      try {
        const h = await bearerHeaders();
        const r = await fetch(`${this.shopifyBase()}/shopify/content/reviews?shop=${encodeURIComponent(shop)}&productId=${encodeURIComponent(ids.join(","))}`, {
          headers: h
        });
        return r.ok ? await r.json() : null;
      } catch (e) {
        return null;
      }
    });
    // ---------------------------------------------------------------- Planner (content_planner_agent: free, LLM cost logged)
    /** Plan the pieces. From Brief it opens Make; Make calls planIt({ stay: true }) to re-plan where it is. */
    _defineProperty(this, "planIt", async opts => {
      if (this.state.busy.plan) return;
      const stay = !!(opts && opts.stay === true);
      const next = stay ? this.state.stage : "make";
      clearTimeout(this._briefT);
      this._briefT = null;
      const b = this.state.bs.brief;
      const base = this.buildPlan(b);
      this.setBusy("plan", true);
      try {
        const brand = this.state.brand || {};
        const products = this.productLinks().map(l => ({
          title: l.title || l.name || String(l.refId || l.ref_id || "").replace(/^shopify:/, ""),
          price: l.price,
          inventory: l.inventory,
          description: strip(l.description || "").slice(0, 400)
        }));
        const performance = {
          pieces: this.state.members.map(m => ({
            title: m.title,
            channel: m.channel || m.kind,
            sessions: Number(m.sessions) || 0,
            conversions: Number(m.conversions) || 0,
            revenue: Number(m.revenue || m.sales) || 0
          }))
        };
        const existing = [...new Set(this.allVariants().map(v => String(v.channel || "").toLowerCase()))];
        const msg = [`STORY: ${this.storyTitle()}`, `GOAL: ${(GOALS.find(g => g[0] === b.goal) || [])[1] || b.goal}`, `AUDIENCE: ${(this.audienceOptions().find(a => a[0] === b.audience) || [])[1] || b.audience}`, b.angle ? `MERCHANT'S ANGLE: ${b.angle}` : "", `REQUESTED CHANNELS: ${b.channels.join(", ")}`, `REQUESTED MEDIA: ${b.media.join(", ")}`, this.videoPicks(b).some(x => x.slot) ? `VIDEO FOR: ${this.videoPicks(b).filter(x => x.slot).map(x => VIDEO_DEST[x.slot].label).join(", ")}` : "", "", "SOURCE COPY:", this.storyText("\n\n").slice(0, 5000)].filter(x => x !== "").join("\n");
        const r = await this.runAgent("content_planner_agent", msg, {
          brand: JSON.stringify({
            company: brand.company,
            industry: brand.industry,
            voice: brand.voice
          }),
          products: JSON.stringify(products),
          performance: JSON.stringify(performance),
          existingChannels: JSON.stringify(existing),
          today: dayOffset(0)
        });
        const done = await this.waitAgent(r && (r.runId || r.id || r.agentRunId || r.Id), 4000, 45);
        const out = this.parseAgentJson(done);
        if (!out || !Array.isArray(out.pieces)) throw new Error(done.timeout ? "The planner is still thinking: this is the default plan; Re-plan in a moment." : "The planner returned nothing usable: this is the default plan.");
        const plan = this.mergePlan(base, out);
        this.saveState({
          brief: b,
          plan,
          planNote: out.summary || null,
          stage: next
        });
        this.setState({
          plan,
          stage: next,
          ship: null
        });
      } catch (e) {
        this.agentError(e, "Planner failed: this is the default plan.");
        this.saveState({
          brief: b,
          plan: base,
          planNote: null,
          stage: next
        });
        this.setState({
          plan: base,
          stage: next,
          ship: null
        });
      } finally {
        this.setBusy("plan", false);
        if (!stay) window.scrollTo(0, 0);
      }
    });
    _defineProperty(this, "archiveStory", async () => {
      this.setBusy("archive", true);
      try {
        for (const m of this.state.members) await safe(svc(`/content/containers/${String(m.id)}`).putAsync({
          title: m.title,
          summary: m.summary,
          topic: m.topic,
          status: "archived"
        }), null);
        toast("Story archived. Restore it from the Library's archived list.");
        this.backToStories();
      } finally {
        if (this._alive) this.setState({
          archiveAsk: false
        });
        this.setBusy("archive", false);
      }
    });
    // ---------------------------------------------------------------- Write
    _defineProperty(this, "generateDrafts", async () => {
      const plan = this.state.plan || [];
      const existing = new Set(this.allVariants().map(v => String(v.channel || "").toLowerCase()));
      const chans = plan.filter(r => r.on && r.kind === "draft" && !existing.has(r.channel) && REPURPOSABLE.has(r.channel)).map(r => r.channel);
      const skipped = plan.filter(r => r.on && r.kind === "draft" && !existing.has(r.channel) && !REPURPOSABLE.has(r.channel)).map(r => label(r.channel));
      if (!chans.length) {
        toast(skipped.length ? `Nothing to generate: ${skipped.join(", ")} drafts are written by hand for now.` : "Every planned channel already has a draft.");
        return;
      }
      this.setBusy("drafts", true);
      try {
        const guidance = {};
        plan.forEach(r => {
          if (r.kind === "draft" && r.on && r.angle && chans.includes(r.channel)) guidance[r.channel] = r.angle;
        });
        // the story's images ride with every draft (hero first); template ids exceed 2^53 so they go in as raw literals
        const tmap = plan.filter(r => r.kind === "draft" && r.on && r.templateId && chans.includes(r.channel)).map(r => `"${r.channel}":${String(r.templateId)}`);
        const body = JSON.stringify({
          channels: chans,
          guidance,
          imageUrls: this.storyImages().slice(0, 4)
        }).replace(/}$/, tmap.length ? `,"templates":{${tmap.join(",")}}}` : "}");
        await svc(`/content/containers/${String(this.state.primary.id)}/repurpose`).postAsync(body);
        toast(`Writing ${chans.length} draft${chans.length === 1 ? "" : "s"} from the story: they land here within a few minutes.`);
        const want = new Set(chans);
        const poll = async n => {
          if (!this._alive) return;
          await this.reloadDetails();
          const have = new Set(this.allVariants().map(v => String(v.channel || "").toLowerCase()));
          const missing = [...want].filter(c => !have.has(c));
          if (missing.length && n < 24) {
            const pollTimer = setTimeout(() => poll(n + 1), 10000);
            this.setState({
              pollTimer
            });
          } else {
            this.setBusy("drafts", false);
            if (!missing.length) toast("Drafts are in: review and approve.");
          }
        };
        poll(0);
      } catch (e) {
        this.setBusy("drafts", false);
        toast(e && e.message || "Repurpose failed.");
      }
    });
    _defineProperty(this, "setVariantStatus", async (v, status) => {
      try {
        await svc(`/content/variants/${String(v.id)}/status`).postAsync({
          status
        });
        await this.reloadDetails();
      } catch (e) {
        toast(e && e.message || "That didn't go through.");
      }
    });
    _defineProperty(this, "saveVariant", async v => {
      const ed = this.state.editing[String(v.id)];
      if (!ed) return;
      try {
        if (!(await this.saveText(v, {
          title: ed.title,
          body: ed.body
        }))) return;
        const editing = {
          ...this.state.editing
        };
        delete editing[String(v.id)];
        this.setState({
          editing
        });
        await this.reloadDetails();
      } catch (e) {
        toast(e && e.message || "Could not save.");
      }
    });
    _defineProperty(this, "setSocialMedia", async (v, sel) => {
      const cid = String(v.piece ? v.piece.id : v.containerId);
      try {
        await svc(`/content/containers/${cid}/blocks`).postAsync({
          kind: "social_media",
          body: JSON.stringify({
            variantId: String(v.id),
            imageUrl: sel.imageUrl || null,
            videoUrl: sel.videoUrl || null
          })
        });
        this.setState({
          mediaPick: null
        });
        await this.reloadDetails();
      } catch (e) {
        toast("Could not attach that media.");
      }
    });
    _defineProperty(this, "translateFor", async (v, opt) => {
      if (!opt) return;
      this.setBusy(`tr:${v.id}`, true);
      try {
        // variant ids exceed 2^53 — send sourceVariantId as a number literal in a pre-built body (postAsync sends a string verbatim)
        const body = JSON.stringify({
          channels: [String(v.channel || "").toLowerCase()],
          locale: opt.locale,
          market: opt.market || undefined,
          marketId: opt.marketId || undefined
        }).replace(/}$/, `,"sourceVariantId":${String(v.id)}}`);
        await svc(`/content/containers/${String(v.containerId)}/repurpose`).postAsync(body);
        toast(`Translating into ${opt.name}${opt.market ? ` for ${opt.market}` : ""}: it lands here within a minute.`);
        const before = this.allVariants().length;
        const poll = async n => {
          if (!this._alive) return;
          await this.reloadDetails();
          if (this.allVariants().length <= before && n < 18) setTimeout(() => poll(n + 1), 10000);else this.setBusy(`tr:${v.id}`, false);
        };
        setTimeout(() => poll(0), 8000);
      } catch (e) {
        this.setBusy(`tr:${v.id}`, false);
        toast(e && e.message || "Could not start the translation.");
      }
    });
    _defineProperty(this, "restoreVersion", async (v, h) => {
      try {
        if (await this.saveText(v, {
          title: h.title || "",
          body: h.body || ""
        })) {
          await this.reloadDetails();
          toast("Restored: the text it replaced is kept in history.");
        }
      } catch (e) {
        toast("Could not restore that version.");
      }
    });
    _defineProperty(this, "addComment", async v => {
      const id = String(v.id);
      const text = (this.state.commentDraft[id] || "").trim();
      if (!text) return;
      let by = "";
      try {
        by = (account.get() || {}).email || "";
      } catch (e) {/* anonymous */}
      try {
        await svc(`/content/containers/${String(v.containerId)}/blocks`).postAsync({
          kind: "comment",
          body: JSON.stringify({
            variantId: id,
            text,
            by,
            at: new Date().toISOString()
          })
        });
        this.setState({
          commentDraft: {
            ...this.state.commentDraft,
            [id]: ""
          }
        });
        await this.reloadDetails();
      } catch (e) {
        toast("Could not post the comment.");
      }
    });
    _defineProperty(this, "saveSeo", async (v, brief) => {
      await safe(svc(`/content/containers/${String(v.containerId)}/blocks`).postAsync({
        kind: "seo_brief",
        body: JSON.stringify({
          ...brief,
          variantId: String(v.id),
          savedAt: new Date().toISOString()
        })
      }), null);
      await this.reloadDetails();
    });
    _defineProperty(this, "seoBrief", async v => {
      const key = `seo:${v.id}`;
      this.setBusy(key, true);
      try {
        const brand = this.state.brand || {};
        const [kw, opp] = await Promise.all([safe(kwService("?active=true").getAsync(), []), safe(kwService("/opportunities?top=15").getAsync(), [])]);
        const keywords = (Array.isArray(kw) ? kw : kw && kw.items || []).slice(0, 40).map(k => ({
          term: k.term,
          searchVolume: k.searchVolume,
          position: k.currentPosition ?? k.position,
          topicGroup: k.topicGroup
        }));
        const opportunities = (Array.isArray(opp) ? opp : opp && opp.items || []).slice(0, 15).map(o => ({
          term: o.term,
          searchVolume: o.searchVolume,
          difficulty: o.difficulty
        }));
        const products = this.productLinks().map(l => ({
          title: l.title || l.name || String(l.refId || l.ref_id || "").replace(/^shopify:/, ""),
          url: l.url
        })).filter(p => p.url);
        const pages = this.state.members.map(m => ({
          title: m.title,
          url: m.url
        })).filter(p => p.url && String(p.url) !== String(v.piece && v.piece.url));
        const target = (this.state.seoKeyword[String(v.id)] || "").trim();
        const msg = `${target ? `TARGET KEYWORD: ${target}\n\n` : ""}TITLE: ${v.title || this.storyTitle()}\n\n${strip(v.body).slice(0, 6000)}`;
        const r = await this.runAgent("content_seo_brief_agent", msg, {
          brand: JSON.stringify({
            company: brand.company,
            industry: brand.industry,
            voice: brand.voice
          }),
          keywords: JSON.stringify(keywords),
          opportunities: JSON.stringify(opportunities),
          products: JSON.stringify(products),
          pages: JSON.stringify(pages)
        });
        const done = await this.waitAgent(r && (r.runId || r.id || r.agentRunId || r.Id), 4000, 45);
        let out = done.ok && (done.ctx && done.ctx.metaTitle && done.ctx || done.res && done.res.metaTitle && done.res) || null;
        if (!out && done.ok) {
          const raw = done.run && (done.run.runResult || done.run.RunResult) || "";
          try {
            out = JSON.parse(String(raw).replace(/^```(json)?|```$/g, "").trim());
          } catch (e) {
            out = null;
          }
        }
        if (typeof out === "string") {
          try {
            out = JSON.parse(out);
          } catch (e) {
            out = null;
          }
        }
        if (!out || !out.metaTitle) {
          toast(done.timeout ? "Still writing: try again in a moment." : "The SEO agent returned nothing usable.");
          return;
        }
        await this.saveSeo(v, out);
        this.setState({
          seoOpen: {
            ...this.state.seoOpen,
            [String(v.id)]: true
          }
        });
        toast("SEO brief saved: the meta title and description go out with the Shopify write.");
      } catch (e) {
        this.agentError(e, "Could not start the SEO brief.");
      } finally {
        this.setBusy(key, false);
      }
    });
    _defineProperty(this, "saveEmailMeta", async (v, patch) => {
      const meta = {
        ...(this.emailMetaFor(v) || {
          variantId: String(v.id),
          subjects: [v.title].filter(Boolean)
        }),
        ...patch,
        variantId: String(v.id)
      };
      await safe(svc(`/content/containers/${String(v.containerId)}/blocks`).postAsync({
        kind: "email_meta",
        body: JSON.stringify(meta)
      }), null);
      // Re-save the variant so a Draft automation picks up the new preheader / subject (server-side sync).
      await this.saveText(v, {
        title: patch.title || v.title || "",
        body: v.body || ""
      });
      await this.reloadDetails();
    });
    _defineProperty(this, "useSubject", async (v, subject) => {
      try {
        if (!(await this.saveText(v, {
          title: subject,
          body: v.body || ""
        }))) return;
        await this.reloadDetails();
        toast("Subject updated: the automation draft follows.");
      } catch (e) {
        toast("Could not update the subject.");
      }
    });
    _defineProperty(this, "segmentVersion", async (v, audience) => {
      if (!audience) return;
      this.setBusy(`seg:${v.id}`, true);
      try {
        const plan = this.state.plan || [];
        const row = plan.find(r => r.kind === "draft" && r.channel === "email");
        await svc(`/content/containers/${String(v.containerId)}/repurpose`).postAsync({
          channels: ["email"],
          segment: audience.name,
          segmentAudienceId: String(audience.id),
          guidance: row && row.angle ? {
            email: row.angle
          } : undefined
        });
        toast(`Writing an email version for "${audience.name}": it lands here within a minute.`);
        const before = this.allVariants().filter(x => String(x.channel || "").toLowerCase() === "email").length;
        const poll = async n => {
          if (!this._alive) return;
          await this.reloadDetails();
          const now = this.allVariants().filter(x => String(x.channel || "").toLowerCase() === "email").length;
          if (now <= before && n < 18) setTimeout(() => poll(n + 1), 10000);else this.setBusy(`seg:${v.id}`, false);
        };
        setTimeout(() => poll(0), 8000);
      } catch (e) {
        this.setBusy(`seg:${v.id}`, false);
        toast(e && e.message || "Could not start the segment version.");
      }
    });
    // ---------------------------------------------------------------- Media
    _defineProperty(this, "makeImage", async (row, idx) => {
      const key = `${row.key}:${idx || 0}`;
      const refs = this.storyImages().slice(0, 1);
      if (!refs.length) {
        toast("The story has no product image to ground the render: add one to a piece first.");
        return;
      }
      const brief = this.state.bs.brief;
      const desc = (SCENE_PROMPTS[row.media] || ["Hero product photo, clean studio background"])[idx || 0] || "Product photo";
      const prompt = row.prompt ? `${desc}. ${row.prompt} Reference product: "${this.storyTitle()}". ${brief.angle || ""}`.trim() : `${desc} for "${this.storyTitle()}". ${brief.angle ? brief.angle + " " : ""}${this.storyText(" ").slice(0, 400)}`.trim();
      this.setBusy(key, true);
      try {
        const r = await this.runAgent("image_generator_agent", prompt, {
          imageUrls: refs,
          aspectRatio: row.aspect || "1:1",
          count: "1",
          resolution: "2k"
        });
        const id = r && (r.runId || r.id || r.agentRunId || r.Id);
        toast("Image agent running…");
        const done = await this.waitAgent(id, 6000, 40);
        const urls = done.ok && (done.ctx && done.ctx.urls || done.res && done.res.urls) || [];
        if (!urls.length) {
          toast(done.timeout ? "Still rendering: check Assets in a minute." : "The image agent could not produce an image.");
          return;
        }
        const url = urls[0];
        const cid = String(this.state.primary.id);
        const a = await safe(svc("/content/assets").postAsync({
          kind: "image",
          url,
          title: `Generated · ${this.storyTitle()}`,
          source: "plunk_image",
          altText: prompt.slice(0, 125)
        }), null);
        if (a && (a.id || a.Id)) await safe(svc(`/content/assets/${String(a.id || a.Id)}/link`).postAsync({
          containerId: cid,
          role: "generated"
        }), null);
        if (a && (a.id || a.Id) && row.templateId) this.markUsed(row.templateId, `assetId=${String(a.id || a.Id)}`);
        await safe(svc(`/content/containers/${cid}/blocks`).postAsync({
          kind: "image_ref",
          body: url,
          assetRef: url
        }), null);
        this.setMedia({
          [key]: url
        });
        toast("Image ready: on the story's primary piece and in Assets.");
        this.reloadDetails();
        this.refreshCredits();
      } catch (e) {
        this.agentError(e, "Could not start the image.");
      } finally {
        this.setBusy(key, false);
      }
    });
    _defineProperty(this, "writeStoryboard", async row => {
      const images = this.storyImages().slice(0, 6);
      if (!images.length) {
        toast("Pick or generate at least one image first.");
        return;
      }
      this.setBusy(`sb:${row.key}`, true);
      try {
        const prompt = `${this.storyTitle()}\n\n${row.prompt ? "Direction: " + row.prompt + "\n\n" : ""}${this.state.bs.brief.angle ? this.state.bs.brief.angle + "\n\n" : ""}${this.storyText("\n\n")}`.slice(0, 4000);
        const r = await this.runAgent("content_storyboard_agent", prompt, {
          imageUrls: images,
          aspectRatio: row.aspect
        });
        const done = await this.waitAgent(r && (r.runId || r.id || r.agentRunId || r.Id), 4000, 45);
        let scenes = done.ok && (done.ctx && done.ctx.scenes || done.res && done.res.scenes);
        if (!scenes && done.ok) {
          const raw = done.run && (done.run.runResult || done.run.RunResult) || "";
          try {
            const j = JSON.parse(raw.replace(/^```(json)?|```$/g, "").trim());
            scenes = j.scenes || j;
          } catch (e) {
            scenes = null;
          }
        }
        if (typeof scenes === "string") {
          try {
            scenes = JSON.parse(scenes);
          } catch (e) {
            scenes = null;
          }
        }
        if (Array.isArray(scenes) && scenes.length) this.setState({
          storyboard: {
            key: row.key,
            aspect: row.aspect,
            scenes: scenes.map(sc => ({
              imageUrl: sc.imageUrl || sc.image || images[0],
              narration: sc.narration || sc.script || ""
            }))
          }
        });else toast(done.timeout ? "Still writing: try again in a moment." : "The storyboard agent returned nothing usable.");
      } catch (e) {
        this.agentError(e, "Could not start the storyboard.");
      } finally {
        this.setBusy(`sb:${row.key}`, false);
      }
    });
    // A finished video from Assets becomes this row's video exactly like a made one (2026-10-16): a draft video
    // variant, the asset linked to it, the video_ref block, mediaOut — so Approve and Publish work on it.
    _defineProperty(this, "useExistingVideo", async (row, x, opts) => {
      const cid = String(this.state.primary.id);
      const title = x && x.title || this.storyTitle();
      this.setBusy(`rv:${row.key}`, true);
      try {
        let aid = x && x.id;
        if (!aid) {
          const a = await safe(svc("/content/assets").postAsync({
            kind: "video",
            url: x.url,
            title: `Video · ${title}`,
            source: "manual"
          }), null);
          aid = a && (a.id || a.Id);
        }
        const variant = await svc(`/content/containers/${cid}/variants`).postAsync({
          channel: "video",
          title,
          body: this.videoCopy(row && row.aspect === "9:16"),
          status: "draft"
        });
        const vid = variant && (variant.id || variant.Id);
        if (!vid) throw new Error("The video version could not be created.");
        if (aid) await safe(svc(`/content/assets/${String(aid)}/link`).postAsync({
          containerId: cid,
          variantId: String(vid),
          role: "video"
        }), null);
        await safe(svc(`/content/containers/${cid}/blocks`).postAsync({
          kind: "video_ref",
          body: `variant:${vid} row:${row.key}`,
          assetRef: x.url
        }), null);
        this.setMedia({
          [row.key]: x.url
        });
        this.setState({
          storyboard: null
        });
        if (!(opts && opts.quiet)) toast(`"${title}" is now this piece's video. Approve it to move on.`);
        await this.reloadDetails();
        return String(vid);
      } catch (e) {
        toast(e && e.message || "Could not use that video.");
      } finally {
        this.setBusy(`rv:${row.key}`, false);
      }
    });
    _defineProperty(this, "renderVideo", async row => {
      const sb = this.state.storyboard && this.state.storyboard.key === row.key ? this.state.storyboard.scenes.filter(s => s.imageUrl && (s.narration || "").trim()) : [];
      if (!sb.length) {
        toast("Write the storyboard first.");
        return;
      }
      this.setBusy(`rv:${row.key}`, true);
      const cid = String(this.state.primary.id);
      const title = this.storyTitle();
      try {
        await safe(svc(`/content/containers/${cid}/blocks`).postAsync({
          kind: "storyboard",
          body: JSON.stringify({
            scenes: sb,
            aspectRatio: row.aspect
          })
        }), null);
        const prompt = `${title}\n\n${this.storyText("\n\n")}`.slice(0, 4000);
        const musicUrl = (this.state.bs.musicUrl || "").trim();
        const r = await this.runAgent("content_video_agent", prompt, {
          imageUrls: sb.map(s => s.imageUrl),
          aspectRatio: row.aspect,
          voice: this.state.bs.voice || "Joanna",
          recipe: row.aspect === "9:16" ? "reel" : "promo",
          titleText: row.aspect === "9:16" ? undefined : title,
          endText: this.state.bs.brief && this.state.bs.brief.cta || undefined,
          scenes: JSON.stringify(sb.map(s => ({
            imageUrl: s.imageUrl,
            narration: s.narration.trim(),
            overlayText: (s.overlayText || "").trim() || undefined
          }))),
          captions: this.state.bs.captions === "off" ? "false" : "true",
          ...(musicUrl ? {
            musicUrl
          } : {})
        });
        toast("Video agent running: a few minutes.");
        const done = await this.waitAgent(r && (r.runId || r.id || r.agentRunId || r.Id), 15000, 60);
        const url = done.ok && (done.ctx && done.ctx.videoUrl || done.res && done.res.videoUrl);
        if (!url) {
          toast(done.timeout ? "Still rendering: it appears on the piece when done." : "The video agent could not render this story.");
          return;
        }
        const a = await safe(svc("/content/assets").postAsync({
          kind: "video",
          url,
          title: `Video · ${title}`,
          source: "plunk_video"
        }), null);
        const variant = await safe(svc(`/content/containers/${cid}/variants`).postAsync({
          channel: "video",
          title,
          body: this.videoCopy(row && row.aspect === "9:16"),
          status: "draft"
        }), null);
        const vid = variant && (variant.id || variant.Id);
        if (a && (a.id || a.Id) && vid) await safe(svc(`/content/assets/${String(a.id || a.Id)}/link`).postAsync({
          containerId: cid,
          variantId: String(vid),
          role: "video"
        }), null);
        await safe(svc(`/content/containers/${cid}/blocks`).postAsync({
          kind: "video_ref",
          body: `variant:${vid} row:${row.key}`,
          assetRef: url
        }), null);
        this.setMedia({
          [row.key]: url
        });
        this.setState({
          storyboard: null
        });
        toast("Video ready: a video variant on the story's primary piece.");
        this.reloadDetails();
        this.refreshCredits();
      } catch (e) {
        this.agentError(e, "Could not start the video.");
      } finally {
        this.setBusy(`rv:${row.key}`, false);
      }
    });
    // Per-scene regenerate (slice 3): a fresh narration for one scene (free) or a new image for it (6 cr).
    _defineProperty(this, "rewriteScene", async i => {
      const sb = this.state.storyboard;
      if (!sb) return;
      const sc = sb.scenes[i];
      const row = (this.state.plan || []).find(r => r.key === sb.key) || {};
      this.setBusy(`sc:w:${i}`, true);
      try {
        const last = i === sb.scenes.length - 1;
        const msg = `SINGLE SCENE\nimageUrl: ${sc.imageUrl}\nposition: scene ${i + 1} of ${sb.scenes.length}${last ? " (the closing call to action)" : ""}\ncurrent narration: ${sc.narration || "(none)"}\nsay: ${row.prompt || this.state.bs.brief.angle || "a fresh take on the same point"}\n\nSOURCE:\n${this.storyTitle()}\n${this.storyText("\n\n")}`.slice(0, 4000);
        const r = await this.runAgent("content_storyboard_agent", msg, {
          imageUrls: [sc.imageUrl],
          aspectRatio: sb.aspect
        });
        const done = await this.waitAgent(r && (r.runId || r.id || r.agentRunId || r.Id), 4000, 30);
        let scenes = done.ok && (done.ctx && done.ctx.scenes || done.res && done.res.scenes);
        if (!scenes && done.ok) {
          const raw = done.run && (done.run.runResult || done.run.RunResult) || "";
          try {
            const j = JSON.parse(String(raw).replace(/^```(json)?|```$/g, "").trim());
            scenes = j.scenes || j;
          } catch (e) {
            scenes = null;
          }
        }
        if (typeof scenes === "string") {
          try {
            scenes = JSON.parse(scenes);
          } catch (e) {
            scenes = null;
          }
        }
        const text = Array.isArray(scenes) && scenes[0] && (scenes[0].narration || scenes[0].script);
        if (text) this.setSb(sb.scenes.map((x, k) => k === i ? {
          ...x,
          narration: String(text)
        } : x));else toast("The storyboard agent returned nothing usable for that scene.");
      } catch (e) {
        this.agentError(e, "Could not rewrite the scene.");
      } finally {
        this.setBusy(`sc:w:${i}`, false);
      }
    });
    _defineProperty(this, "regenSceneImage", async i => {
      const sb = this.state.storyboard;
      if (!sb) return;
      const sc = sb.scenes[i];
      const refs = this.storyImages().slice(0, 1);
      if (!refs.length) {
        toast("The story has no product image to ground the render.");
        return;
      }
      this.setBusy(`sc:i:${i}`, true);
      try {
        const prompt = `${sc.narration || "Product scene"}: a still for a narrated video about "${this.storyTitle()}", ${sb.aspect} framing, no text overlays.`;
        const r = await this.runAgent("image_generator_agent", prompt, {
          imageUrls: refs,
          aspectRatio: sb.aspect || "16:9",
          count: "1",
          resolution: "2k"
        });
        toast("Image agent running…");
        const done = await this.waitAgent(r && (r.runId || r.id || r.agentRunId || r.Id), 6000, 40);
        const urls = done.ok && (done.ctx && done.ctx.urls || done.res && done.res.urls) || [];
        if (!urls.length) {
          toast(done.timeout ? "Still rendering: check Assets in a minute." : "The image agent could not produce an image.");
          return;
        }
        const url = urls[0];
        const cid = String(this.state.primary.id);
        const a = await safe(svc("/content/assets").postAsync({
          kind: "image",
          url,
          title: `Scene ${i + 1} · ${this.storyTitle()}`,
          source: "plunk_image",
          altText: (sc.narration || "").slice(0, 125)
        }), null);
        if (a && (a.id || a.Id)) await safe(svc(`/content/assets/${String(a.id || a.Id)}/link`).postAsync({
          containerId: cid,
          role: "generated"
        }), null);
        await safe(svc(`/content/containers/${cid}/blocks`).postAsync({
          kind: "image_ref",
          body: url,
          assetRef: url
        }), null);
        const cur = this.state.storyboard;
        if (cur && cur.key === sb.key) this.setSb(cur.scenes.map((x, k) => k === i ? {
          ...x,
          imageUrl: url
        } : x));
        this.reloadDetails();
        this.refreshCredits();
      } catch (e) {
        this.agentError(e, "Could not start the image.");
      } finally {
        this.setBusy(`sc:i:${i}`, false);
      }
    });
    // Shared tail for every rendered video: asset + `video` variant + video_ref block on the primary piece.
    _defineProperty(this, "saveVideo", async (url, assetTitle, variantBody, outKey, source) => {
      const cid = String(this.state.primary.id);
      const title = this.storyTitle();
      const a = await safe(svc("/content/assets").postAsync({
        kind: "video",
        url,
        title: assetTitle,
        source
      }), null);
      const variant = await safe(svc(`/content/containers/${cid}/variants`).postAsync({
        channel: "video",
        title,
        body: this.videoCopy(/9:16/.test(String(variantBody || ""))),
        status: "draft"
      }), null);
      const vid = variant && (variant.id || variant.Id);
      if (a && (a.id || a.Id) && vid) await safe(svc(`/content/assets/${String(a.id || a.Id)}/link`).postAsync({
        containerId: cid,
        variantId: String(vid),
        role: "video"
      }), null);
      await safe(svc(`/content/containers/${cid}/blocks`).postAsync({
        kind: "video_ref",
        body: `variant:${vid}`,
        assetRef: url
      }), null);
      if (outKey) this.setMedia({
        [outKey]: url
      });
      this.reloadDetails();
      this.refreshCredits();
    });
    /** Make's Edit / Trim (DS 2026-10-21, "YouTube video degrades after posting"): the edited file becomes the row's video
     *  for Publish too. Before, only the player switched and every network got the pre-edit file. */
    _defineProperty(this, "replaceVideoFile", async (rowKey, url) => {
      const before = (this.state.mediaOut || {})[rowKey];
      this.setMedia({
        [rowKey]: url
      });
      const all = this.renderedVideos();
      const rv = all.filter(x => x.rowKey === rowKey).pop() || all.filter(x => before && x.url === before).pop();
      if (!rv || !url || rv.url === url) return;
      const cid = String(rv.v.piece.id);
      await safe(svc(`/content/containers/${cid}/blocks`).postAsync({
        kind: "video_ref",
        body: `variant:${rv.v.id} row:${rowKey}`,
        assetRef: url
      }), null);
      this.reloadDetails();
    });
    // Cinematic clip (slice 3): Veo via content_cinematic_agent — priced opt-in (40 cr), grounded on the story's product image.
    _defineProperty(this, "makeClip", async row => {
      const refs = this.storyImages().slice(0, 1);
      if (!refs.length) {
        toast("The story has no product image to ground the clip: add one to a piece first.");
        return;
      }
      this.setBusy(`clip:${row.key}`, true);
      try {
        const prompt = (row.prompt ? `${row.prompt} ` : "") + `Cinematic 8-second product shot of "${this.storyTitle()}": slow camera move, natural light, the product exactly as in the reference image, no text. ${this.state.bs.brief.angle || ""}`.trim();
        // quality: the workspace's last Standard / Best choice (2026-10-12, cheaper video)
        const r = await this.runAgent("content_cinematic_agent", prompt, {
          imageUrl: refs[0],
          aspectRatio: row.aspect || "16:9",
          prompt,
          quality: lastVideoQuality()
        });
        toast("Cinematic agent running: a few minutes.");
        const done = await this.waitAgent(r && (r.runId || r.id || r.agentRunId || r.Id), 15000, 60);
        const url = this.videoUrlOf(done);
        if (!url) {
          toast(done.timeout ? "Still rendering: it appears on the piece when done." : "The cinematic agent could not render this clip.");
          return;
        }
        await this.saveVideo(url, `Clip · ${this.storyTitle()}`, `${this.storyTitle()} · cinematic ${row.aspect || "16:9"}`, row.key, "plunk_cinematic");
        toast("Clip ready: a video variant on the story's primary piece.");
      } catch (e) {
        this.agentError(e, "Could not start the clip.");
      } finally {
        this.setBusy(`clip:${row.key}`, false);
      }
    });
    // 9:16 auto-reframe (slice 3): MediaConvert centre crop via content_reframe_agent (no generation credits).
    // One storyboard, every format (2026-10-02): the 16:9 render reframes to 9:16 (Reels, Shorts, TikTok), 1:1 (feed) and 4:5 (portrait feed).
    _defineProperty(this, "reframe", async (videoUrl, outKey, aspect) => {
      aspect = aspect || "9:16";
      const tag = aspect.replace(":", "");
      const names = {
        "9:16": "Reel",
        "1:1": "Feed video",
        "4:5": "Portrait video"
      };
      const uses = {
        "9:16": "Reels, Shorts and TikTok",
        "1:1": "the feed",
        "4:5": "portrait feed posts"
      };
      this.setBusy(`rf:${outKey}:${tag}`, true);
      this.setBusy(`rf:${outKey}`, true);
      try {
        const r = await this.runAgent("content_reframe_agent", `Reframe this video to ${aspect} for ${uses[aspect] || aspect}: ${videoUrl}`, {
          videoUrl,
          aspectRatio: aspect
        });
        toast(`Reframing to ${aspect}: about a minute.`);
        const done = await this.waitAgent(r && (r.runId || r.id || r.agentRunId || r.Id), 10000, 40);
        const url = this.videoUrlOf(done);
        if (!url) {
          toast(done.timeout ? "Still reframing: it appears on the piece when done." : `The ${aspect} version could not be produced.`);
          return null;
        }
        await this.saveVideo(url, `${names[aspect] || aspect} · ${this.storyTitle()}`, `${this.storyTitle()} · ${aspect}`, `${outKey}:${tag}`, "plunk_reframe");
        toast(`${aspect} version ready: another video variant on the primary piece.`);
        return url;
      } catch (e) {
        this.agentError(e, "Could not start the reframe.");
        return null;
      } finally {
        this.setBusy(`rf:${outKey}:${tag}`, false);
        this.setBusy(`rf:${outKey}`, false);
      }
    });
    /** Thumbnail for a rendered clip (2026-10-02): the frame the user picked, or any story image, becomes the asset's poster. */
    _defineProperty(this, "setAssetPoster", async (videoUrl, posterUrl) => {
      const a = await safe(svc("/content/assets").postAsync({
        kind: "video",
        url: videoUrl
      }), null);
      const id = a && (a.id || a.Id);
      if (!id) {
        toast("Could not find that video in Assets.");
        return false;
      }
      const r = await safe(svc(`/content/assets/${String(id)}/poster`).postAsync({
        posterUrl
      }), null);
      if (!r) {
        toast("Could not save the thumbnail.");
        return false;
      }
      await this.reloadDetails();
      toast("Thumbnail saved: it shows in the picker, on Publish and as the cover on YouTube.");
      return true;
    });
    _defineProperty(this, "refreshCredits", async () => {
      try {
        const s = await getBillingStatus(true);
        if (s && this._alive) this.setState({
          credits: Number(s.creditBalance ?? s.balance)
        });
      } catch (e) {/* ignore */}
    });
    _defineProperty(this, "setSb", scenes => this.setState({
      storyboard: {
        ...this.state.storyboard,
        scenes
      }
    }));
    _defineProperty(this, "shopifyTranslate", async (v, d) => {
      const lm = this.localeFor(v) || {};
      const src = this.sourcePublication(lm, d);
      if (!src) throw new Error("publish the original to Shopify first, then publish the translation");
      const shop = this.state.shopify && this.state.shopify.shop || "";
      const h = await bearerHeaders();
      h["content-type"] = "application/json";
      const html = /<[a-z][\s\S]*>/i.test(v.body || "") ? v.body : (v.body || "").split(/\n{2,}/).map(p => `<p>${p.replace(/\n/g, "<br/>")}</p>`).join("");
      const seo = this.seoFor(v);
      const body = {
        shop,
        resourceId: src.externalId || src.external_id,
        locale: lm.locale,
        marketId: lm.marketId || undefined,
        title: v.title,
        html,
        url: src.url,
        seoTitle: seo && seo.metaTitle ? seo.metaTitle : undefined,
        seoDescription: seo && seo.metaDescription ? seo.metaDescription : undefined
      };
      const r = await fetch(`${this.shopifyBase()}/shopify/content/translate`, {
        method: "POST",
        headers: h,
        body: JSON.stringify(body)
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `Shopify translation failed (${r.status})`);
      await svc(`/content/variants/${String(v.id)}/published`).postAsync({
        target: "shopify_translation",
        externalId: body.resourceId,
        url: j.url || src.url
      });
    });
    // Brand safety (slice 4): content_brand_safety_agent (free; LLM cost logged) on every selected Ship row.
    // Critical = banned word or a claim the policy forbids → the row is blocked unless "publish anyway" is ticked.
    _defineProperty(this, "safetyCheck", async rows => {
      const brand = this.state.brand || {};
      const products = this.productLinks().map(l => ({
        title: l.title || l.name || String(l.refId || l.ref_id || "").replace(/^shopify:/, ""),
        price: l.price
      }));
      const results = {};
      await Promise.all(rows.map(async r => {
        try {
          const res = await this.runAgent("content_brand_safety_agent", `CHANNEL: ${r.v.channel}\nTITLE: ${r.v.title || ""}\n\n${strip(r.v.body).slice(0, 6000)}`, {
            brand: JSON.stringify({
              company: brand.company,
              industry: brand.industry,
              voice: brand.voice,
              bannedWords: brand.bannedWords || "",
              claimsPolicy: brand.claimsPolicy || ""
            }),
            products: JSON.stringify(products),
            channel: String(r.v.channel || "")
          });
          const done = await this.waitAgent(res && (res.runId || res.id || res.agentRunId || res.Id), 3000, 40);
          let out = done.ok && (done.ctx && Array.isArray(done.ctx.issues) && done.ctx || done.res && Array.isArray(done.res.issues) && done.res) || null;
          if (!out && done.ok) {
            const raw = done.run && (done.run.runResult || done.run.RunResult) || "";
            try {
              out = JSON.parse(String(raw).replace(/^```(json)?|```$/g, "").trim());
            } catch (e) {
              out = null;
            }
          }
          if (typeof out === "string") {
            try {
              out = JSON.parse(out);
            } catch (e) {
              out = null;
            }
          }
          results[r.key] = out && Array.isArray(out.issues) ? {
            issues: out.issues.filter(i => i && i.text),
            summary: out.summary || "",
            at: Date.now()
          } : {
            error: done.timeout ? "the check timed out" : "no result"
          };
        } catch (e) {
          results[r.key] = {
            error: e && e.message || "check failed"
          };
        }
        if (this._alive) this.setState({
          safety: {
            ...this.state.safety,
            ...results
          }
        });
      }));
      return results;
    });
    _defineProperty(this, "isCritical", s => !!s && !s.error && (s.issues || []).some(i => String(i.severity || "").toLowerCase() === "critical"));
    _defineProperty(this, "checkSelected", async () => {
      const rows = (this.state.ship || []).filter(r => r.on && r.target);
      if (!rows.length) {
        toast("Select something to check first.");
        return;
      }
      this.setBusy("safety", true);
      try {
        const res = await this.safetyCheck(rows);
        const bad = rows.filter(r => this.isCritical(res[r.key])).length;
        toast(bad ? `${bad} piece${bad === 1 ? "" : "s"} blocked by brand safety: see the notes on each row.` : "Brand-safe: nothing blocked.");
      } finally {
        this.setBusy("safety", false);
      }
    });
    /** Shipped list › Update (owned target, publish again in place) or Publish again (a retired row): tick the Ship row for it. */
    _defineProperty(this, "shipAgain", (pub, v) => {
      const ship = this.state.ship || this.buildShip();
      const target = String(pub.target || "").toLowerCase();
      const key = String(v.id);
      let found = false;
      const next = ship.map(r => {
        if (r.key !== key) return r;
        found = true;
        const has = r.targets.some(([k]) => k === target);
        return {
          ...r,
          on: true,
          again: true,
          target: has ? target : r.target
        };
      });
      if (!found) {
        toast("That piece is not ready to publish: approve it under Make first.");
        return;
      }
      this.setState({
        ship: next
      });
      window.scrollTo(0, 0);
      toast("Row ticked above: Publish when ready.");
    });
    _defineProperty(this, "shopifyWrite", async (v, d, target, again) => {
      const shop = this.state.shopify && this.state.shopify.shop || "";
      const link = (d && d.links || []).find(l => String(l.linkType || l.link_type || "").toLowerCase() === "product");
      const base = String(endpoints.product.root || "").replace(/\/v1\/?$/, "");
      const h = await bearerHeaders();
      h["content-type"] = "application/json";
      let body;
      if (target === "shopify_media") {
        const vb = videoRefFor(d && d.blocks && d.blocks, v.id);
        if (!vb) throw new Error("This variant has no rendered video.");
        body = {
          shop,
          kind: "media",
          productId: link && (link.refId || link.ref_id),
          mediaUrl: vb.assetRef,
          mediaType: "video",
          alt: v.title || ""
        };
      } else {
        const kind = target === "shopify_product" ? "product" : target === "shopify_article" ? "article" : "page";
        const html = /<[a-z][\s\S]*>/i.test(v.body || "") ? v.body : (v.body || "").split(/\n{2,}/).map(p => `<p>${p.replace(/\n/g, "<br/>")}</p>`).join("");
        body = {
          shop,
          kind,
          productId: link && (link.refId || link.ref_id),
          title: v.title,
          html,
          publish: kind !== "product" ? true : undefined,
          author: this.state.brand && this.state.brand.company || undefined
        };
        // Publish again (ship-once): the page / article we created before gets the current copy instead of a second one.
        const prior = again ? this.livePub(v, d, target) : null;
        if (prior && prior.externalId) body.externalId = prior.externalId;
        const seo = this.seoFor(v);
        if (seo) {
          if (seo.metaTitle) body.seoTitle = seo.metaTitle;
          if (seo.metaDescription) body.seoDescription = seo.metaDescription;
          if (seo.slug && kind !== "product") body.handle = seo.slug;
        }
      }
      const r = await fetch(`${base}/shopify/content/publish`, {
        method: "POST",
        headers: h,
        body: JSON.stringify(body)
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `Shopify publish failed (${r.status})`);
      await svc(`/content/variants/${String(v.id)}/published`).postAsync({
        target,
        externalId: j.externalId,
        url: j.url,
        previousHtml: j.previousHtml
      });
    });
    _defineProperty(this, "publishOne", async row => {
      const {
        v,
        d,
        target
      } = row;
      const again = !!row.again;
      const firstImage = (d && d.blocks || []).find(b => /image/i.test(b.kind || "")) || {};
      const vb = videoRefFor(d && d.blocks && d.blocks, v.id);
      if (target === "shopify_translate") return this.shopifyTranslate(v, d);
      if (target.startsWith("shopify_")) return this.shopifyWrite(v, d, target, again);
      if (target === "custom_api") return svc(`/content/variants/${String(v.id)}/publish/custom`).postAsync({
        kind: v.channel,
        videoUrl: vb ? vb.assetRef : undefined
      });
      if (target === "wordpress") return svc(`/content/variants/${String(v.id)}/publish/cms`).postAsync({
        provider: "wordpress",
        republish: again
      });
      if (target === "email_draft") return svc(`/content/containers/${String(v.containerId)}/publish/email`).postAsync({});
      if (target === "manual") return svc(`/content/variants/${String(v.id)}/publications/manual`).postAsync({
        url: (row.manualUrl || "").trim() || undefined
      });
      if (["facebook", "instagram", "gbp", "pinterest", "linkedin", "youtube", "tiktok"].includes(target)) {
        const pick = this.socialMediaFor(v, d);
        const videoUrl = pick.videoUrl || (vb ? vb.assetRef : undefined);
        const imageUrl = videoUrl ? this.posterFor(videoUrl) || undefined : pick.imageUrl || firstImage.assetRef || firstImage.body; // a video's thumbnail rides as the Reel cover
        if ((target === "instagram" || target === "pinterest") && !imageUrl && !videoUrl) throw new Error(`${targetLabel(target)} needs an image`);
        if ((target === "youtube" || target === "tiktok") && !videoUrl) throw new Error(`${targetLabel(target)} needs a rendered video`);
        const link = d && d.container && d.container.url || undefined;
        return svc(`/content/variants/${String(v.id)}/publish/social`).postAsync({
          platform: target,
          imageUrl,
          videoUrl,
          link,
          republish: again
        });
      }
      throw new Error("No target");
    });
    _defineProperty(this, "shipNow", async () => {
      const rows = (this.state.ship || []).filter(r => r.on && r.target);
      if (!rows.length) return;
      this.setBusy("ship", true);
      // Brand-safety preflight: unchecked rows are checked now; a critical finding blocks the row unless overridden.
      const unchecked = rows.filter(r => !this.state.safety[r.key] || this.state.safety[r.key].error);
      let safety = {
        ...this.state.safety
      };
      if (unchecked.length) {
        toast(`Checking brand safety on ${unchecked.length} piece${unchecked.length === 1 ? "" : "s"}…`);
        safety = {
          ...safety,
          ...(await this.safetyCheck(unchecked))
        };
      }
      const blocked = rows.filter(r => this.isCritical(safety[r.key]) && !r.override);
      if (blocked.length) {
        this.setBusy("ship", false);
        toast(`${blocked.length} piece${blocked.length === 1 ? "" : "s"} blocked by brand safety: fix the copy under Make, or tick "publish anyway".`);
        return;
      }
      let ok = 0,
        scheduled = 0,
        queued = 0;
      const failed = [];
      const shippedBefore = this.shippedCount();
      const SOCIAL = ["facebook", "instagram", "gbp", "pinterest", "linkedin", "youtube", "tiktok"];
      for (const r of rows) {
        try {
          // Ship-once: never send a piece to a target it is already live on unless the row says "publish again".
          const live = this.livePub(r.v, r.d, r.target);
          if (live && !r.again && r.target !== "email_draft") throw new Error(`already published there on ${String(live.publishedDate || live.createdDate || "").slice(0, 10)}: tick "Publish again" to send it once more`);
          const emailSend = r.target === "email_draft" && r.audienceId;
          const now = r.target === "shopify_translate"; // translations write now (the calendar worker has no translation path)
          if (r.when && !emailSend && !now) {
            await svc("/content/schedule").postAsync(JSON.stringify({
              target: r.target,
              scheduledDate: r.when,
              note: `Builder · ${this.storyTitle()}`,
              approved: true,
              republish: !!r.again
            }).replace(/^\{/, `{"variantId":${String(r.v.id)},"containerId":${r.v.containerId ? String(r.v.containerId) : "null"},`));
            scheduled++;
          } else if (emailSend) {
            let automationId = r.automationId;
            if (!automationId) {
              const res = await svc(`/content/containers/${String(r.v.containerId)}/publish/email`).postAsync({});
              automationId = String(res && (res.externalId || res.ExternalId) || "");
            }
            if (!automationId) throw new Error("the email draft could not be created");
            const hour = this.state.sendTime && this.state.sendTime.suggested ? this.state.sendTime.suggested : "14:00";
            await svc(`/campaign/email/automations/${automationId}/send`).postAsync({
              audienceId: String(r.audienceId),
              sendAt: r.when ? new Date(`${r.when}T${hour}:00Z`).toISOString() : null,
              subjectB: r.ab && r.subjectB ? r.subjectB : null
            });
            queued++;
          } else {
            await this.publishOne(r);
            if (SOCIAL.includes(r.target)) queued++;else ok++;
          }
        } catch (e) {
          failed.push(`${label(r.v.channel)} → ${targetLabel(r.target)}: ${e && e.message && e.message.length < 240 ? e.message : "failed"}`);
        }
      }
      this.setBusy("ship", false);
      toast(failed.length ? `${ok} published · ${queued} queued · ${scheduled} scheduled · ${failed.length} failed` : `${ok} published now · ${queued} queued to their network · ${scheduled} scheduled.`);
      await this.reloadDetails();
      this.setState({
        ship: this.buildShip(),
        shipResult: {
          ok,
          queued,
          scheduled,
          failed,
          at: new Date()
        }
      });
      this.saveState({
        stageDone: {
          ...this.state.bs.stageDone,
          ship: true
        }
      });
      // Social posts go out from the worker a few seconds later: keep reloading until their rows land (2 minutes at most).
      if (queued) this.pollShipped(shippedBefore + queued, 0);
    });
    _defineProperty(this, "pollShipped", (want, n) => {
      if (!this._alive || n >= 24) return;
      setTimeout(async () => {
        if (!this._alive) return;
        try {
          await this.reloadDetails();
        } catch (e) {/* next tick retries */}
        if (this.shippedCount() < want) this.pollShipped(want, n + 1);
      }, 5000);
    });
    _defineProperty(this, "adToFlight", v => {
      const d = this.state.details[String(v.piece && v.piece.id || v.containerId)] || {};
      try {
        sessionStorage.setItem("plunk-flight-prefill", JSON.stringify(this.flightPrefill(v, d)));
      } catch (e) {/* private mode */}
      this.props.history.push("/Campaigns/FlightBuilder");
    });
    // Web page stats (Website Analytics, the global range) for the pages this story published: matched by path.
    _defineProperty(this, "loadPageStats", async () => {
      if (this.state.pageStats !== undefined) return;
      this.setState({
        pageStats: null
      });
      const r = await getPages(this.measureRange(), {
        top: 500,
        orderByDesc: "Views"
      }).catch(() => null);
      const idx = {};
      (r && r.items || []).forEach(pg => {
        const k = pathKey(pg.url);
        if (k && !idx[k]) idx[k] = pg;
      });
      if (this._alive) this.setState({
        pageStats: idx
      });
    });
    /** Results › Best piece: write a version of the best piece for a sibling channel (the same plan row Make's + Add makes), then open Make. */
    _defineProperty(this, "makeSibling", ch => {
      const plan = (this.state.plan || []).slice();
      if (!this.state.plan || !ch) {
        this.go("make");
        return;
      }
      const brief = {
        ...this.state.bs.brief,
        templates: {
          ...(this.state.bs.brief.templates || {})
        }
      };
      const t = this.templateFor(brief, ch);
      const have = plan.filter(r => r.kind === "draft" && r.channel === ch).length;
      const key = have ? `d:${ch}:${have + 1}` : `d:${ch}`;
      plan.push({
        key,
        on: true,
        kind: "draft",
        channel: ch,
        piece: this.storyTitle(),
        status: "to write",
        when: new Date().toISOString().slice(0, 10),
        templateId: t ? String(t.id) : null,
        angle: have ? "A different take from the first post: another angle, another visual." : ""
      });
      if (!(brief.channels || []).includes(ch)) brief.channels = [...(brief.channels || []), ch];
      this.setState({
        plan,
        bs: {
          ...this.state.bs,
          brief
        }
      });
      this.saveState({
        plan,
        brief
      });
      this.go("make");
      toast(`Added a ${this.kindName(ch).toLowerCase()} to Make. It is at the end of the list, ready to write.`);
    });
    /** Make can own the top bar's primary button: b.setPrimary({ label, onClick, disabled, title }), or b.setPrimary(null)
     *  for the default ("Go to Publish"). Safe to call from componentDidMount / componentDidUpdate: it only re-renders when
     *  the label, disabled or title change, and the latest onClick is always the one used. Cleared on every step change. */
    _defineProperty(this, "setPrimary", spec => {
      this._primaryClick = spec && spec.onClick;
      const cur = this.state.primaryOverride || null;
      const next = spec && spec.label ? {
        stage: this.state.stage,
        label: String(spec.label),
        disabled: !!spec.disabled,
        title: spec.title || ""
      } : null;
      if (!cur && !next) return;
      if (cur && next && cur.stage === next.stage && cur.label === next.label && cur.disabled === next.disabled && cur.title === next.title) return;
      this.setState({
        primaryOverride: next
      });
    });
    _defineProperty(this, "backToStories", () => this.props.history.push("/Content?tab=stories"));
    this.state = {
      loading: true,
      error: null,
      storyId: String(props.params && props.params.storyId || ""),
      members: [],
      details: {},
      primary: null,
      stage: "brief",
      bs: {
        brief: {
          goal: "launch",
          audience: "retail",
          channels: ["product_copy", "blog", "social_instagram", "email"],
          media: ["hero", "video169"],
          angle: "",
          playbook: "",
          templates: {}
        },
        plan: null,
        planNote: null,
        voice: "Joanna",
        stageDone: {}
      },
      templates: [],
      tplSheet: null,
      tplPreview: null,
      shipPreview: null,
      seoOpen: {},
      seoKeyword: {},
      sendTime: null,
      previewEdit: {},
      brand: null,
      shopify: null,
      connectors: [],
      social: [],
      assets: [],
      credits: null,
      creditsLevel: null,
      busy: {},
      editing: {},
      plan: null,
      ship: null,
      storyboard: null,
      mediaOut: {},
      pollTimer: null,
      markets: null,
      safety: {},
      histOpen: {},
      cmtOpen: {},
      commentDraft: {},
      wholesale: false,
      // shell + Brief (2026-10-09): Make's top-bar button, the ··· menu, source cards and their add panels
      primaryOverride: null,
      moreOpen: false,
      srcOpen: {},
      srcAll: false,
      srcAdd: null,
      webDraft: "",
      prodQ: "",
      prodHits: []
    };
    this._alive = true;
  }
  async componentDidMount() {
    await this.load();
    this.loadTemplates();
    this.loadTeam();
    this._beatT = setInterval(() => this.beat(), 30000);
    this.beat();
    this._onVis = () => {
      if (!document.hidden) this.beat();
    };
    document.addEventListener("visibilitychange", this._onVis);
    // New story (create-story.jsx) hands over ?goal=: apply the matching playbook once, on a brief that was never touched.
    try {
      const q = new URLSearchParams(window.location.search);
      const goal = q.get("goal");
      const map = {
        launch: "launch",
        sell_down: "sale",
        seasonal: "seasonal",
        reorder: "wholesale"
      };
      const pb = goal && map[goal] && PLAYBOOKS.find(([k]) => k === map[goal]);
      if (pb && !(this.state.bs.brief && this.state.bs.brief.playbook) && !this.state.plan) {
        const brief = {
          ...this.state.bs.brief,
          ...pb[2],
          playbook: pb[0]
        };
        this.setState({
          bs: {
            ...this.state.bs,
            brief
          }
        });
        this.saveState({
          brief
        });
      }
    } catch (e) {/* no query */}
    // Measure follows the global range picker (DS 2026-10-02): funnels and page stats reload on change.
    this._periodTok = subscribePeriod(() => {
      if (this._alive) {
        this.setState({
          pageStats: undefined
        });
        this.loadFunnels();
      }
    });
  }
  measureRange() {
    return getGlobalRange() || "this_month";
  }
  measureLabel() {
    return rangeLabelFor(this.measureRange());
  }
  tpl(id) {
    return id ? this.state.templates.find(t => String(t.id) === String(id)) : null;
  }
  templateFor(b, channel) {
    const m = b && b.templates || {};
    if (Object.prototype.hasOwnProperty.call(m, channel)) return this.tpl(m[channel]); // an explicit pick (null = no template)
    return this.tpl(m.any) || this.state.templates.find(t => String(t.channel || "").toLowerCase() === channel && t.scope === "plunk") || null;
  }
  // Video pieces with a destination (DS 2026-10-21: "Where it goes" had no TikTok or YouTube, so videos lost
  // where they were for). brief.templates["video:youtube"] / ["video:tiktok"] = the template made for that
  // platform; plain "video:<id>" keys are the generic Video chip. One plan row per key, so YouTube and TikTok
  // get their own video even when they share a template.
  videoPicks(b) {
    const m = b && b.templates || {};
    const out = [];
    Object.keys(m).forEach(k => {
      if (!k.startsWith("video:")) return;
      const t = this.tpl(m[k]);
      if (!t) return;
      const slot = VIDEO_DEST[k.slice(6)] ? k.slice(6) : null;
      if (!slot && out.some(x => !x.slot && String(x.t.id) === String(t.id))) return;
      out.push({
        t,
        slot
      });
    });
    return out;
  }
  // Every picked video template (brief.templates["video:<id>"]) is a video piece.
  videoTemplates(b) {
    const m = b && b.templates || {};
    const out = [];
    Object.keys(m).forEach(k => {
      if (!k.startsWith("video:")) return;
      const t = this.tpl(m[k]);
      if (t && !out.some(x => String(x.id) === String(t.id))) out.push(t);
    });
    return out;
  }
  templateForMedia(b, mk) {
    const m = b && b.templates || {};
    for (const id of Object.values(m)) {
      const t = this.tpl(id);
      if (t && mediaRowFor(t) === mk) return t;
    }
    if (/^video|^cinematic/.test(mk) && m.video) return this.tpl(m.video);
    return null;
  }
  componentWillUnmount() {
    // a brief edit still waiting for its debounced save goes now (straight to the block: the component is leaving)
    if (this._briefT && this.state.primary) {
      clearTimeout(this._briefT);
      safe(svc(`/content/containers/${String(this.state.primary.id)}/blocks`).postAsync({
        kind: "builder_state",
        body: JSON.stringify({
          ...this.state.bs,
          stage: this.state.stage
        })
      }), null);
    }
    this._alive = false;
    clearTimeout(this._prodT);
    unsubscribePeriod(this._periodTok);
    if (this.state.pollTimer) clearTimeout(this.state.pollTimer);
    clearInterval(this._beatT);
    if (this._onVis) document.removeEventListener("visibilitychange", this._onVis);
    this.beat(true);
  }
  setBusy(k, v) {
    this.setState({
      busy: {
        ...this.state.busy,
        [k]: v
      }
    });
  }

  // ---------------------------------------------------------------- load
  // The library endpoint caps a page at 100 (and treats a larger $top as invalid, returning 24): page through it so a
  // story's pieces are all found, however big the library (2026-10-07).
  async loadAllContainers(range) {
    const all = [];
    const PAGE = 100;
    for (let skip = 0; skip < 2000; skip += PAGE) {
      const d = await safe(svc(`/content/containers/${range}?$top=${PAGE}&$skip=${skip}`).getAsync(), null);
      const items = d && d.items || [];
      all.push(...items);
      if (items.length < PAGE) break;
    }
    return {
      items: all
    };
  }
  shopifyBase() {
    return String(endpoints.product.root || "").replace(/\/v1\/?$/, "");
  }
  /** Others on this story, optionally only those on one piece. */
  othersOn(variantId) {
    const ps = this.state.presence || [];
    return variantId == null ? ps : ps.filter(x => String(x.variantId || "") === String(variantId));
  }
  teamOn() {
    const t = this.state.team;
    return !!(t && (t.users || []).length > 1);
  }
  teamUsers() {
    return this.state.team && this.state.team.users || [];
  }
  me() {
    let e = this.state.team && this.state.team.me || "";
    if (!e) {
      try {
        e = (account.get() || {}).email || "";
      } catch (x) {/* anonymous */}
    }
    return String(e).toLowerCase();
  }
  isModerator() {
    return !!(this.state.team && this.state.team.moderator);
  }
  blocksOf(cid) {
    return (this.state.details[String(cid)] || {}).blocks || [];
  }
  /** {owner|copy|media: {assignee, due, by, at}} for a piece (variantId) or the story (variantId null). */
  assignmentsOf(cid, variantId) {
    const out = {};
    this.blocksOf(cid).filter(b => b.kind === "assignment").map(b => parseJson(b)).filter(Boolean).filter(a => String(a.variantId || "") === String(variantId || "")).sort((a, b) => new Date(a.at) - new Date(b.at)).forEach(a => {
      out[a.role] = a;
    });
    Object.keys(out).forEach(k => {
      if (!out[k].assignee) delete out[k];
    });
    return out;
  }
  assigneesFor(v) {
    return v ? this.assignmentsOf(v.piece ? v.piece.id : v.containerId, v.id) : {};
  }
  storyOwner() {
    const p = this.state.primary;
    if (!p) return null;
    const a = this.assignmentsOf(p.id, null).owner;
    return a ? a.assignee : p.createdBy ? String(p.createdBy).toLowerCase() : null;
  }
  /** Threads on a piece, oldest first: [{...note, id, replies: [...]}]. Older notes (no id, no anchor) show as general notes. */
  notesFor(v) {
    const d = this.state.details[String(v.piece ? v.piece.id : v.containerId)] || {};
    const all = (d.blocks || []).filter(b => b.kind === "comment").map(b => {
      const j = parseJson(b);
      return j && j.text ? {
        ...j,
        id: String(b.id)
      } : null;
    }).filter(Boolean);
    const mine = all.filter(c => String(c.variantId || "") === String(v.id) || c.parentId && all.some(r => r.id === c.parentId && String(r.variantId || "") === String(v.id)));
    const byAt = (a, b) => new Date(a.at) - new Date(b.at);
    return mine.filter(c => !c.parentId).sort(byAt).map(c => ({
      ...c,
      replies: mine.filter(r => r.parentId === c.id).sort(byAt)
    }));
  }
  // ---- Review and sign-off (phase 4, CONTENT_HUB_REVIEW_DESK §4.3): rounds on this story, one vote per reviewer per piece.
  storyIdStr() {
    return String(this.state.storyId || this.state.primary && (this.state.primary.storyId || this.state.primary.id) || "");
  }
  reviewsOn() {
    return this.teamOn() && !!(this.state.reviews && this.state.reviews.available);
  }
  /** "Moderators approve" is switched on for this workspace and I'm not one (approval is open by default). */
  approvalLocked() {
    return this.reviewsOn() && !!(this.state.team && this.state.team.moderatorApproval) && !this.isModerator();
  }
  /** The newest round this piece is in: { review, votes } (votes for this piece only), or null. */
  reviewOf(v) {
    const rs = this.state.reviews && this.state.reviews.reviews || [];
    for (const r of rs) {
      const votes = (r.votes || []).filter(x => String(x.variantId) === String(v.id));
      if (votes.length) return {
        review: r,
        votes
      };
    }
    return null;
  }
  /** in_review | changes | signed | null for a piece that is not approved or published. */
  reviewState(v) {
    const st = String(v.status || "").toLowerCase();
    if (st === "approved" || st === "published" || st === "live") return null;
    const rv = this.reviewOf(v);
    if (!rv || rv.review.status === "cancelled") return null;
    if (rv.votes.some(x => x.vote === "changes_requested")) return "changes";
    if (rv.votes.filter(x => x.required).every(x => x.vote === "signed_off")) return "signed";
    return "in_review";
  }
  renderConflict() {
    const c = this.state.conflict;
    if (!c) return null;
    const who = c.by ? nameOf(c.by) : "Someone";
    const when = c.at ? new Date(c.at).toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit"
    }) : "";
    const plainMine = plainText(c.mine.body);
    const plainTheirs = plainText(c.theirs.body);
    const owned = /<(p|div|section|h\d|table)\b/i.test(String(c.theirs.body || "") + String(c.mine.body || "")); // page/email HTML: keep one or the other
    return /*#__PURE__*/React.createElement("div", {
      className: "wa-modal-backdrop hub-edit-backdrop"
    }, /*#__PURE__*/React.createElement("div", {
      className: "hub-trash-sheet hub-sched bx-conflict",
      role: "dialog",
      "aria-modal": "true",
      "aria-labelledby": "bx-conflict-h",
      style: {
        maxWidth: 860,
        width: "min(860px, calc(100vw - 32px))"
      }
    }, /*#__PURE__*/React.createElement("h3", {
      id: "bx-conflict-h",
      className: "hub-h3",
      style: {
        margin: 0
      }
    }, who, " saved this piece", when ? ` at ${when}` : "", ", after you opened it."), /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        margin: "6px 0 12px"
      }
    }, "Your changes are not lost. Keep yours, keep ", who.split(" ")[0], "'s, or ", owned ? "copy what you need across after keeping theirs" : "edit a copy that has both", "."), c.merge == null ? /*#__PURE__*/React.createElement("div", {
      style: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
        gap: 12
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        border: "1px solid var(--plk-border)",
        borderRadius: 10,
        padding: 12,
        minWidth: 0
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "territory-hint",
      style: {
        margin: "0 0 6px"
      }
    }, "Yours (not saved)"), c.mine.title ? /*#__PURE__*/React.createElement("b", null, strip(c.mine.title)) : null, /*#__PURE__*/React.createElement("div", {
      style: {
        whiteSpace: "pre-wrap",
        maxHeight: 280,
        overflow: "auto",
        fontSize: 13
      }
    }, plainMine)), /*#__PURE__*/React.createElement("div", {
      style: {
        border: "1px solid var(--plk-border)",
        borderRadius: 10,
        padding: 12,
        minWidth: 0
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "territory-hint",
      style: {
        margin: "0 0 6px"
      }
    }, who, "'s (saved)"), c.theirs.title ? /*#__PURE__*/React.createElement("b", null, strip(c.theirs.title)) : null, /*#__PURE__*/React.createElement("div", {
      style: {
        whiteSpace: "pre-wrap",
        maxHeight: 280,
        overflow: "auto",
        fontSize: 13
      }
    }, plainTheirs))) : /*#__PURE__*/React.createElement("div", {
      style: {
        display: "grid",
        gap: 8
      }
    }, /*#__PURE__*/React.createElement("label", {
      className: "territory-hint",
      htmlFor: "bx-merge",
      style: {
        margin: 0
      }
    }, "Your merged copy. ", who, "'s saved text is below for reference."), /*#__PURE__*/React.createElement("textarea", {
      id: "bx-merge",
      rows: 9,
      value: c.merge,
      onChange: e => this.setState({
        conflict: {
          ...c,
          merge: e.target.value
        }
      }),
      style: {
        width: "100%",
        fontSize: 13,
        padding: 10,
        border: "1px solid var(--plk-border)",
        borderRadius: 8
      }
    }), /*#__PURE__*/React.createElement("div", {
      style: {
        whiteSpace: "pre-wrap",
        maxHeight: 160,
        overflow: "auto",
        fontSize: 12.5,
        color: "var(--plk-text-muted)",
        borderLeft: "2px solid var(--plk-border)",
        paddingLeft: 10
      }
    }, plainTheirs)), /*#__PURE__*/React.createElement("div", {
      className: "hub-tplp-acts",
      style: {
        justifyContent: "flex-end",
        gap: 8,
        marginTop: 14,
        flexWrap: "wrap"
      }
    }, c.merge == null ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("button", {
      className: "territory-toggle sm",
      style: {
        margin: 0
      },
      onClick: () => c.resolve(null)
    }, "Keep ", who.split(" ")[0], "'s"), !owned && /*#__PURE__*/React.createElement("button", {
      className: "territory-toggle sm",
      style: {
        margin: 0
      },
      onClick: () => this.setState({
        conflict: {
          ...c,
          merge: plainMine
        }
      })
    }, "Edit a merged copy"), /*#__PURE__*/React.createElement("button", {
      className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary",
      onClick: () => c.resolve(c.mine)
    }, "Keep mine")) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("button", {
      className: "territory-toggle sm",
      style: {
        margin: 0
      },
      onClick: () => this.setState({
        conflict: {
          ...c,
          merge: null
        }
      })
    }, "Back"), /*#__PURE__*/React.createElement("button", {
      className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary",
      disabled: !String(c.merge).trim(),
      onClick: () => c.resolve({
        title: c.mine.title,
        body: c.merge
      })
    }, "Save the merged copy")))));
  }
  // derived
  allVariants() {
    return this.state.members.flatMap(m => ((this.state.details[String(m.id)] || {}).variants || []).map(v => ({
      ...v,
      containerId: v.containerId || m.id,
      piece: m
    })));
  }
  allBlocks() {
    return this.state.members.flatMap(m => (this.state.details[String(m.id)] || {}).blocks || []);
  }
  productLinks() {
    return this.state.members.flatMap(m => ((this.state.details[String(m.id)] || {}).links || []).filter(l => String(l.linkType || l.link_type || "").toLowerCase() === "product"));
  }
  storyText(sep) {
    const fromBlocks = this.allBlocks().filter(b => !NOT_COPY.test(b.kind || "")).map(b => strip(b.body)).filter(Boolean);
    const fromVariants = this.allVariants().map(v => [v.title, strip(v.body)].filter(Boolean).join(": ")).filter(Boolean);
    return (fromBlocks.length ? fromBlocks : fromVariants).join(sep || "\n\n").trim();
  }
  storyImages() {
    const own = this.allBlocks().filter(b => /image/i.test(b.kind || "")).map(b => b.assetRef || b.body).filter(Boolean);
    const lib = (this.state.assets || []).map(a => a.url).filter(u => u && !own.includes(u));
    return own.concat(lib);
  }
  /** A video's description (YouTube description; Facebook, Instagram, TikTok caption): the story's approved post copy,
   *  Facebook first (it carries the link), else the story text. Never the size label (2026-10-16). */
  captionText(h) {
    return captionText(h);
  }
  videoCopy(vertical) {
    // A tall video (TikTok, Reels, Shorts) reads like the Instagram caption; a wide one (YouTube, Facebook) like the
    // Facebook post. Line breaks and links are kept, and a writer's "{link}" placeholder never reaches a description.
    // Without an approved post: the title and what the brief leads with, never a dump of the source facts (it read
    // as instructions, DS 2026-10-21).
    const vs = this.allVariants().filter(v => ["approved", "published"].includes(String(v.status || "").toLowerCase()));
    const order = vertical ? ["social_instagram", "social_facebook", "social_linkedin", "social_pinterest"] : ["social_facebook", "social_linkedin", "social_instagram", "social_pinterest"];
    for (const ch of order) {
      const v = vs.find(x => String(x.channel || "").toLowerCase() === ch && strip(x.body).length > 40);
      if (v) return captionText(v.body).slice(0, 4500);
    }
    const angle = String(this.state.bs && this.state.bs.brief && this.state.bs.brief.angle || "").trim();
    return [this.storyTitle(), angle].filter(Boolean).join("\n\n");
  }
  storyTitle() {
    const p = this.state.primary;
    return p && (p.storyTitle || p.title) || "Story";
  }
  /** Poster frame for a clip URL: the asset's thumbnail (set with Pick a thumbnail, or by the render) from any loaded asset list. */
  posterFor(url) {
    if (!url) return undefined;
    const pools = this.state.members.map(m => (this.state.details[String(m.id)] || {}).assets || []).concat([this.state.assets || []]);
    for (const pool of pools) {
      const a = pool.find(x => x && x.url === url && x.posterUrl);
      if (a) return a.posterUrl;
    }
    return undefined;
  }
  goalOptions() {
    return GOALS.filter(([k]) => this.state.wholesale || !WHOLESALE_ONLY.has(k));
  }
  audienceOptions() {
    const custom = String(this.state.brand && this.state.brand.audiences || "").split(",").map(x => x.trim()).filter(Boolean);
    const base = custom.length ? custom.map(x => [x, x]) : AUDIENCES.filter(([k]) => this.state.wholesale || !WHOLESALE_ONLY.has(k));
    if (custom.length && !custom.some(x => /subscriber/i.test(x))) base.push(["subscribers", "Email subscribers"]);
    return base;
  }
  playbookOptions() {
    return PLAYBOOKS.filter(([k]) => this.state.wholesale || k !== "wholesale");
  }
  purposeOptions() {
    return PURPOSES.filter(([k]) => this.state.wholesale || k !== "wholesale");
  }
  videoTemplateOptions() {
    return (this.state.templates || []).filter(t => {
      const mk = mediaRowFor(t);
      return mk && /^video|^cinematic/.test(mk);
    });
  }
  renderBrief() {
    const b = this.state.bs.brief;
    const audOpts = this.audienceOptions();
    const audOther = !audOpts.some(([k]) => k === b.audience);
    const setB = this.setBrief;
    const toggle = (key, v) => setB({
      [key]: b[key].includes(v) ? b[key].filter(x => x !== v) : [...b[key], v]
    });
    const destOn = d => !!(b.templates || {})[`video:${d}`];
    const purpose = b.playbook || GOAL_TO_PURPOSE[b.goal] || "";
    const videoOn = this.videoPicks(b).some(x => !x.slot);
    const canVideo = this.videoTemplates(b).length > 0 || this.videoTemplateOptions().length > 0;
    const places = b.channels.length + (videoOn ? 1 : 0) + Object.keys(VIDEO_DEST).filter(destOn).length;
    return /*#__PURE__*/React.createElement("div", {
      className: "bx-brief"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bx-brief-2"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bx-field"
    }, /*#__PURE__*/React.createElement("span", {
      className: "bx-field-h",
      id: "bx-purpose"
    }, "What it is for"), /*#__PURE__*/React.createElement("div", {
      className: "bx-chips",
      role: "group",
      "aria-labelledby": "bx-purpose"
    }, this.purposeOptions().map(([k, l]) => /*#__PURE__*/React.createElement("button", {
      key: k,
      type: "button",
      className: "bx-chip" + (purpose === k ? " on" : ""),
      "aria-pressed": purpose === k,
      onClick: () => this.pickPurpose(k)
    }, l)))), /*#__PURE__*/React.createElement("div", {
      className: "bx-field"
    }, /*#__PURE__*/React.createElement("label", {
      className: "bx-field-h",
      htmlFor: "bx-aud"
    }, "Who it is for"), /*#__PURE__*/React.createElement("select", {
      id: "bx-aud",
      className: "bx-input",
      value: audOther ? "__other" : b.audience,
      onChange: e => setB({
        audience: e.target.value === "__other" ? "" : e.target.value
      })
    }, audOpts.map(([k, l]) => /*#__PURE__*/React.createElement("option", {
      key: k,
      value: k
    }, l)), /*#__PURE__*/React.createElement("option", {
      value: "__other"
    }, "Someone else")), audOther && /*#__PURE__*/React.createElement("input", {
      className: "bx-input",
      "aria-label": "Who it is for, in your words",
      value: b.audience,
      placeholder: "Describe them, e.g. first-time buyers",
      autoFocus: !b.audience,
      onChange: e => setB({
        audience: e.target.value
      })
    }), /*#__PURE__*/React.createElement("span", {
      className: "bx-hint"
    }, "Change this list in ", /*#__PURE__*/React.createElement("a", {
      onClick: () => this.props.history.push("/Settings/Brand")
    }, "Brand kit"), "."))), /*#__PURE__*/React.createElement("div", {
      className: "bx-field"
    }, /*#__PURE__*/React.createElement("span", {
      className: "bx-field-h",
      id: "bx-where"
    }, "Where it goes ", /*#__PURE__*/React.createElement("i", null, plural(places, "place"))), /*#__PURE__*/React.createElement("div", {
      className: "bx-chips",
      role: "group",
      "aria-labelledby": "bx-where"
    }, BRIEF_CHANNELS.slice(0, 4).map(([k, l]) => /*#__PURE__*/React.createElement("button", {
      key: k,
      type: "button",
      className: "bx-chip" + (b.channels.includes(k) ? " on" : ""),
      "aria-pressed": b.channels.includes(k),
      onClick: () => toggle("channels", k)
    }, l)), canVideo && /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "bx-chip" + (videoOn ? " on" : ""),
      "aria-pressed": videoOn,
      title: "A short video made from the story's pictures, with narration",
      onClick: this.toggleVideo
    }, "Video"), canVideo && Object.keys(VIDEO_DEST).map(d => /*#__PURE__*/React.createElement("button", {
      key: d,
      type: "button",
      className: "bx-chip" + (destOn(d) ? " on" : ""),
      "aria-pressed": destOn(d),
      title: VIDEO_DEST[d].hint,
      onClick: () => this.toggleVideoDest(d)
    }, VIDEO_DEST[d].label)), BRIEF_CHANNELS.slice(4).map(([k, l]) => /*#__PURE__*/React.createElement("button", {
      key: k,
      type: "button",
      className: "bx-chip" + (b.channels.includes(k) ? " on" : ""),
      "aria-pressed": b.channels.includes(k),
      onClick: () => toggle("channels", k)
    }, l)))), /*#__PURE__*/React.createElement("div", {
      className: "bx-field"
    }, /*#__PURE__*/React.createElement("label", {
      className: "bx-field-h",
      htmlFor: "bx-lead"
    }, "Lead with ", /*#__PURE__*/React.createElement("i", null, "optional, the one thing every piece should say first")), /*#__PURE__*/React.createElement("textarea", {
      id: "bx-lead",
      className: "bx-input",
      rows: 2,
      value: b.angle,
      placeholder: "e.g. Three new colors and 15% off for two weeks",
      onChange: e => setB({
        angle: e.target.value
      })
    })), this.renderKnown());
  }
  /** Source blocks as cards, a web page and its address folded into one. */
  sourceCards() {
    const all = this.allBlocks();
    const urls = all.filter(x => x.kind === "source_url").sort(byId);
    const pages = all.filter(x => x.kind === "webpage").sort(byId);
    const urlFor = new Map(pages.map((p, i) => [p, urls[i] ? oneLine(urls[i].body) : ""]));
    const urlBlockFor = new Map(pages.map((p, i) => [p, urls[i] || null]));
    const cidOf = x => String(x.containerId || x.ContainerId || this.state.primary && this.state.primary.id || "");
    const cards = all.filter(x => !NOT_COPY.test(x.kind || "") && !SRC_HIDE.has(String(x.kind || "")) && strip(x.body)).map((x, i) => ({
      key: String(x.id || `${x.kind}:${i}`),
      ids: [x.id, urlBlockFor.get(x) && urlBlockFor.get(x).id].filter(Boolean).map(String),
      cid: cidOf(x),
      ...describeBlock(x, urlFor.get(x))
    }));
    // an address with no page text (the page could not be read) still shows, so nothing is hidden from the user
    urls.slice(pages.length).forEach((u, i) => {
      const href = oneLine(u.body);
      if (href) cards.push({
        key: String(u.id || `url:${i}`),
        ids: u.id ? [String(u.id)] : [],
        cid: cidOf(u),
        badge: "WEB",
        title: hostPath(href),
        summary: "Web page · address only, no text was read",
        text: href,
        url: href
      });
    });
    // the same page read twice shows once
    const seenCard = new Set();
    return cards.filter(c => {
      const k = `${c.badge}|${c.title}|${c.summary}`;
      if (seenCard.has(k)) return false;
      seenCard.add(k);
      return true;
    });
  }
  /** "What the writers know": every source block as a readable card, plus ways to add more. The planner and
   *  writers read exactly these (and the brief), so the list is the honest answer to "where do the facts come from". */
  renderKnown() {
    const cards = this.sourceCards();
    const open = this.state.srcOpen || {};
    const add = this.state.srcAdd;
    const busy = this.state.busy;
    const all = !!this.state.srcAll || cards.length <= 8;
    const shown = all ? cards : cards.slice(0, 6);
    const draft = this.state.knownDraft || "";
    return /*#__PURE__*/React.createElement("section", {
      className: "bx-field bx-known",
      "aria-labelledby": "bx-known-h"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bx-known-h"
    }, /*#__PURE__*/React.createElement("span", {
      className: "bx-field-h",
      id: "bx-known-h"
    }, "What the writers know"), /*#__PURE__*/React.createElement("span", null, "They use only these. Nothing is made up.")), cards.length ? /*#__PURE__*/React.createElement("ul", {
      className: "bx-src-list"
    }, shown.map(c => {
      const isOpen = !!open[c.key];
      return /*#__PURE__*/React.createElement("li", {
        key: c.key,
        className: "bx-src" + (isOpen ? " open" : "")
      }, /*#__PURE__*/React.createElement("div", {
        className: "bx-src-row"
      }, c.image ? /*#__PURE__*/React.createElement("img", {
        className: "bx-src-badge",
        src: c.image,
        alt: "",
        style: {
          objectFit: "cover",
          padding: 0
        },
        onError: e => {
          e.currentTarget.style.visibility = "hidden";
        }
      }) : /*#__PURE__*/React.createElement("span", {
        className: "bx-src-badge",
        "aria-hidden": "true"
      }, c.badge), /*#__PURE__*/React.createElement("div", {
        className: "bx-src-t"
      }, /*#__PURE__*/React.createElement("b", {
        title: c.title
      }, c.title), /*#__PURE__*/React.createElement("span", {
        title: c.summary
      }, c.summary)), /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "bx-link quiet",
        "aria-expanded": isOpen,
        onClick: () => this.setState({
          srcOpen: {
            ...open,
            [c.key]: !isOpen
          }
        })
      }, isOpen ? "Hide" : "View"), c.ids && c.ids.length > 0 && (this.state.srcRemove === c.key ? /*#__PURE__*/React.createElement("span", {
        className: "bx-src-ask"
      }, /*#__PURE__*/React.createElement("span", null, "Remove?"), /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "yes",
        disabled: !!busy[`rm:${c.key}`],
        onClick: () => this.removeCard(c)
      }, busy[`rm:${c.key}`] ? "Removing…" : "Remove"), /*#__PURE__*/React.createElement("button", {
        type: "button",
        disabled: !!busy[`rm:${c.key}`],
        onClick: () => this.setState({
          srcRemove: null
        })
      }, "Keep")) : /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "bx-src-x",
        "aria-label": `Remove ${c.title}`,
        title: "Remove from what the writers know",
        onClick: () => this.setState({
          srcRemove: c.key
        })
      }, /*#__PURE__*/React.createElement("span", {
        className: "material-icons",
        "aria-hidden": "true"
      }, "close")))), isOpen && /*#__PURE__*/React.createElement("div", {
        className: "bx-src-full"
      }, c.url && /*#__PURE__*/React.createElement("a", {
        href: c.url,
        target: "_blank",
        rel: "noreferrer",
        className: "bx-src-url"
      }, c.url), c.lines ? /*#__PURE__*/React.createElement("ul", null, c.lines.map((l, i) => /*#__PURE__*/React.createElement("li", {
        key: i
      }, l))) : /*#__PURE__*/React.createElement("p", null, String(c.text || "").slice(0, 8000), String(c.text || "").length > 8000 ? "…" : "")));
    })) : /*#__PURE__*/React.createElement("div", {
      className: "bx-src-empty"
    }, "Nothing yet. Without facts the writers only have the title and what you lead with. A few plain lines, a web page or a product is plenty."), !all && /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "bx-link",
      onClick: () => this.setState({
        srcAll: true
      })
    }, "Show all ", cards.length), (() => {
      // the story's own pictures only (image blocks on its pieces), not the whole Assets library
      const picked = x => {
        try {
          const j = JSON.parse(x.body || "{}");
          return j.imageUrl || j.ImageUrl || null;
        } catch (e) {
          return null;
        }
      };
      const pics = [...new Set(this.allBlocks().map(x => /image/i.test(x.kind || "") ? x.assetRef || x.body : x.kind === "social_media" ? picked(x) : null).filter(u => /^https?:\/\//i.test(u || "") && !/\.(mp4|mov|webm)(\?|$)/i.test(u)))];
      if (!pics.length) return null;
      return /*#__PURE__*/React.createElement("div", {
        className: "bx-src-pics",
        "aria-label": "The story's pictures"
      }, /*#__PURE__*/React.createElement("span", {
        className: "bx-hint",
        style: {
          display: "block",
          margin: "8px 0 6px"
        }
      }, plural(pics.length, "picture"), " in this story, for the pieces and videos"), /*#__PURE__*/React.createElement("div", {
        style: {
          display: "flex",
          gap: 8,
          flexWrap: "wrap"
        }
      }, pics.slice(0, 12).map(u => /*#__PURE__*/React.createElement("a", {
        key: u,
        href: u,
        target: "_blank",
        rel: "noreferrer"
      }, /*#__PURE__*/React.createElement("img", {
        src: u,
        alt: "",
        loading: "lazy",
        style: {
          width: 72,
          height: 72,
          objectFit: "cover",
          borderRadius: 8,
          border: "1px solid var(--plk-border)",
          display: "block"
        },
        onError: e => {
          e.currentTarget.parentNode.style.display = "none";
        }
      }))), pics.length > 12 && /*#__PURE__*/React.createElement("span", {
        className: "bx-hint",
        style: {
          alignSelf: "center"
        }
      }, "+", pics.length - 12, " more")));
    })(), /*#__PURE__*/React.createElement("div", {
      className: "bx-src-adds"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "bx-add" + (add === "note" ? " on" : ""),
      "aria-expanded": add === "note",
      onClick: () => this.toggleAdd("note")
    }, "+ A fact or note"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "bx-add" + (add === "web" ? " on" : ""),
      "aria-expanded": add === "web",
      onClick: () => this.toggleAdd("web")
    }, "+ A web page"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "bx-add" + (add === "products" ? " on" : ""),
      "aria-expanded": add === "products",
      onClick: () => this.toggleAdd("products")
    }, "+ Products"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "bx-add",
      disabled: !!busy.srcdoc,
      onClick: () => this._docInput && this._docInput.click()
    }, busy.srcdoc ? "Reading the document…" : "+ A document"), /*#__PURE__*/React.createElement("input", {
      type: "file",
      hidden: true,
      accept: ".pdf,.docx,.txt,.md",
      ref: el => {
        this._docInput = el;
      },
      onChange: e => this.addDocument(e.target.files && e.target.files[0])
    }), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "bx-add",
      disabled: !!busy.reviews,
      title: `Google Business reviews${this.state.shopify ? " and Shopify product ratings" : ""}, quoted word for word`,
      onClick: this.pullReviews
    }, busy.reviews ? "Looking for reviews…" : "+ Reviews")), add === "note" && /*#__PURE__*/React.createElement("div", {
      className: "bx-add-panel"
    }, /*#__PURE__*/React.createElement("textarea", {
      className: "bx-input",
      rows: 3,
      value: draft,
      disabled: !!busy.known,
      autoFocus: true,
      "aria-label": "A fact or note for the writers",
      placeholder: "What it is, who it is for, prices, dates, what makes it different. A few plain lines is plenty.",
      onChange: e => this.setState({
        knownDraft: e.target.value
      })
    }), /*#__PURE__*/React.createElement("div", {
      className: "bx-add-acts"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "bx-btn",
      onClick: () => this.setState({
        srcAdd: null
      })
    }, "Cancel"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "bx-btn ink",
      disabled: !!busy.known || !draft.trim(),
      onClick: async () => {
        await this.addKnown();
        if (this._alive && !this.state.knownDraft) this.setState({
          srcAdd: null
        });
      }
    }, busy.known ? "Adding…" : "Add"))), add === "web" && /*#__PURE__*/React.createElement("div", {
      className: "bx-add-panel row"
    }, /*#__PURE__*/React.createElement("input", {
      className: "bx-input",
      type: "url",
      autoFocus: true,
      "aria-label": "Web page address",
      value: this.state.webDraft || "",
      placeholder: "https://yourstore.com/page",
      disabled: !!busy.srcweb,
      onChange: e => this.setState({
        webDraft: e.target.value
      }),
      onKeyDown: e => {
        if (e.key === "Enter") this.addWebPage();
      }
    }), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "bx-btn ink",
      disabled: !!busy.srcweb || !(this.state.webDraft || "").trim(),
      onClick: this.addWebPage
    }, busy.srcweb ? "Reading the page…" : "Add page")), add === "products" && /*#__PURE__*/React.createElement("div", {
      className: "bx-add-panel"
    }, /*#__PURE__*/React.createElement("input", {
      className: "bx-input",
      autoFocus: true,
      "aria-label": "Search your products",
      value: this.state.prodQ || "",
      placeholder: "Search your products by name",
      onChange: e => this.findProducts(e.target.value)
    }), (this.state.prodHits || []).length > 0 && /*#__PURE__*/React.createElement("ul", {
      className: "bx-prod-hits"
    }, this.state.prodHits.map(p => /*#__PURE__*/React.createElement("li", {
      key: p.id
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      disabled: !!busy[`prod:${p.id}`],
      onClick: () => this.addProduct(p)
    }, p.imageUrl ? /*#__PURE__*/React.createElement("img", {
      src: p.imageUrl,
      alt: ""
    }) : /*#__PURE__*/React.createElement("span", {
      className: "material-icons",
      "aria-hidden": "true"
    }, "inventory_2"), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("b", null, p.title), p.productType ? /*#__PURE__*/React.createElement("i", null, p.productType) : null), /*#__PURE__*/React.createElement("em", null, busy[`prod:${p.id}`] ? "Adding…" : "Add"))))), (this.state.prodQ || "").trim().length >= 2 && !(this.state.prodHits || []).length && /*#__PURE__*/React.createElement("span", {
      className: "bx-hint"
    }, "No matching products yet.")));
  }
  estimate(b) {
    const existing = new Set(this.allVariants().map(v => String(v.channel || "").toLowerCase()));
    const drafts = b.channels.filter(c => !existing.has(c)).length;
    const images = MEDIA.filter(([k]) => b.media.includes(k) && !VIDEO_KINDS.has(k)).reduce((a, m) => a + m[2] / 6, 0);
    const videos = MEDIA.filter(([k]) => b.media.includes(k) && VIDEO_KINDS.has(k)).length;
    const credits = MEDIA.filter(([k]) => b.media.includes(k)).reduce((a, m) => a + m[2], 0);
    return {
      drafts,
      images,
      videos,
      credits
    };
  }
  // Reviews & UGC as source material (slice 2): Google Business reviews (Social rail) + Shopify product ratings → `review` / `social_proof` blocks.
  reviewCount() {
    return this.allBlocks().filter(b => b.kind === "review" || b.kind === "social_proof").length;
  }
  buildPlan(b) {
    const rows = [];
    let day = 0;
    const existing = this.allVariants();
    b.channels.forEach(ch => {
      const have = existing.find(v => String(v.channel || "").toLowerCase() === ch);
      const t = this.templateFor(b, ch);
      rows.push({
        key: `d:${ch}`,
        on: true,
        kind: "draft",
        channel: ch,
        piece: have ? have.piece.title : this.storyTitle(),
        status: have ? have.status : "to write",
        when: dayOffset(day++),
        templateId: t ? String(t.id) : null,
        angle: t ? angleFromRecipe(t) : undefined
      });
    });
    // Images are made per piece in Make (the slot's Generate popover); no story-level image rows are planned.
    this.videoPicks(b).forEach(({
      t,
      slot
    }) => rows.push(this.videoRow(t, dayOffset(Math.max(0, day - 1)), slot)));
    return rows;
  }
  videoRow(t, when, slot) {
    const mk = mediaRowFor(t) || "video169";
    const m = MEDIA.find(x => x[0] === mk);
    const media = recipeOf(t).media || {};
    const dest = slot ? VIDEO_DEST[slot] : null;
    return {
      key: slot ? `m:v:${slot}` : `m:v:${String(t.id)}`,
      on: true,
      kind: "media",
      media: mk,
      label: dest ? `${dest.label} · ${t.name}` : t.name,
      goesTo: slot || undefined,
      credits: m ? m[2] : 10,
      aspect: dest ? dest.aspect : media.aspect || (m ? m[3] : "16:9"),
      when: when || dayOffset(0),
      templateId: String(t.id),
      prompt: mediaPromptFromRecipe(t)
    };
  }
  parseAgentJson(done) {
    if (!done || !done.ok) return null;
    let out = done.ctx && done.ctx.pieces && done.ctx || done.res && done.res.pieces && done.res || null;
    if (!out) {
      const raw = done.run && (done.run.runResult || done.run.RunResult) || "";
      try {
        out = JSON.parse(String(raw).replace(/^```(json)?|```$/g, "").trim());
      } catch (e) {
        out = null;
      }
    }
    if (typeof out === "string") {
      try {
        out = JSON.parse(out);
      } catch (e) {
        out = null;
      }
    }
    return out && typeof out === "object" ? out : null;
  }
  mergePlan(base, out) {
    const plan = base.map(r => ({
      ...r
    }));
    const day = n => dayOffset(Math.max(0, Math.min(60, Number(n) || 0)));
    (out.pieces || []).forEach(p => {
      const ch = String(p.channel || "").toLowerCase();
      if (!ch) return;
      const angle = [p.angle, Array.isArray(p.keyPoints) && p.keyPoints.length ? `Key points: ${p.keyPoints.join("; ")}` : ""].filter(Boolean).join(" ");
      // Only the places the brief asked for (DS 2026-10-21): planner extras used to land in Make as a column of
      // "Skipped" pieces nobody picked. Add one from Make's "+ Add" instead.
      const row = plan.find(r => r.kind === "draft" && r.channel === ch);
      if (row) {
        row.angle = angle;
        row.when = day(p.dayOffset);
      }
    });
    (out.media || []).forEach(m => {
      const row = plan.find(r => r.kind === "media" && r.media === String(m.kind || "").toLowerCase());
      if (row) {
        row.prompt = m.prompt || "";
        if (m.dayOffset != null) row.when = day(m.dayOffset);
      }
    });
    return plan;
  }
  // Stage heading (the step bar already says which step this is). make.jsx uses head() and footer() too.
  head(title, hint, actions) {
    return /*#__PURE__*/React.createElement("div", {
      className: "hub-bld-head bx-head"
    }, /*#__PURE__*/React.createElement("div", {
      className: "hub-bld-head-text"
    }, /*#__PURE__*/React.createElement("h2", null, title), hint && /*#__PURE__*/React.createElement("p", null, hint)), actions && /*#__PURE__*/React.createElement("div", {
      className: "hub-bld-head-actions"
    }, actions));
  }
  footer(left, back, next) {
    return /*#__PURE__*/React.createElement("div", {
      className: "hub-bld-foot bx-foot"
    }, /*#__PURE__*/React.createElement("span", {
      className: "territory-hint",
      style: {
        margin: 0
      }
    }, left), /*#__PURE__*/React.createElement("span", {
      style: {
        display: "flex",
        gap: 8
      }
    }, back && STAGES.some(s => s[0] === back) && /*#__PURE__*/React.createElement(Btn, {
      flat: true,
      onClick: () => this.go(back)
    }, "\u2190 ", STAGES.find(s => s[0] === back)[1]), next && /*#__PURE__*/React.createElement(Btn, {
      story: true,
      onClick: next[1]
    }, next[0])));
  }

  // ---------------------------------------------------------------- Plan
  renderPlan() {
    const plan = this.state.plan || this.buildPlan(this.state.bs.brief);
    const setRow = (key, patch) => {
      const p = plan.map(r => r.key === key ? {
        ...r,
        ...patch
      } : r);
      this.setState({
        plan: p
      });
    };
    const note = this.state.bs.planNote;
    const planned = plan.some(r => r.angle || r.prompt);
    return /*#__PURE__*/React.createElement("div", null, this.head("The plan", "One row per draft and per media item. Untick what you don't want. The angle guides that draft, and dates go to the calendar when you ship.", /*#__PURE__*/React.createElement(Btn, {
      disabled: !!this.state.busy.plan,
      onClick: this.planIt
    }, this.state.busy.plan ? "Planning…" : planned ? "Re-plan" : "Let the planner take a pass")), note && /*#__PURE__*/React.createElement("div", {
      className: "hub-bld-callout"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "auto_awesome"), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("b", null, "The planner's take"), note)), /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table",
      style: {
        width: "100%"
      }
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", {
      style: {
        width: 28
      }
    }), /*#__PURE__*/React.createElement("th", {
      style: {
        width: 160
      }
    }, "What"), /*#__PURE__*/React.createElement("th", null, "Angle / prompt"), /*#__PURE__*/React.createElement("th", {
      style: {
        width: 90
      }
    }, "Status"), /*#__PURE__*/React.createElement("th", {
      style: {
        width: 150
      }
    }, "When"))), /*#__PURE__*/React.createElement("tbody", null, plan.map(r => /*#__PURE__*/React.createElement("tr", {
      key: r.key,
      style: {
        opacity: r.on ? 1 : 0.55
      }
    }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      checked: r.on,
      onChange: () => setRow(r.key, {
        on: !r.on
      }),
      className: "hub-cb"
    })), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("div", null, r.kind === "draft" ? /*#__PURE__*/React.createElement(React.Fragment, null, label(r.channel), " draft") : /*#__PURE__*/React.createElement(React.Fragment, null, r.label, " ", /*#__PURE__*/React.createElement("span", {
      className: "territory-hint",
      style: {
        margin: 0
      }
    }, "\xB7 ", r.aspect))), r.templateId && this.tpl(r.templateId) && /*#__PURE__*/React.createElement("span", {
      className: "hub-tpl-chip",
      title: "Made from this template; clear to drop the recipe for this row"
    }, this.tpl(r.templateId).name, /*#__PURE__*/React.createElement("a", {
      onClick: () => setRow(r.key, {
        templateId: null
      })
    }, "\xD7")), r.suggested && /*#__PURE__*/React.createElement(Chip, {
      text: "suggested",
      bg: "var(--plk-accent)"
    }), r.kind === "draft" && r.piece && r.piece !== this.storyTitle() && /*#__PURE__*/React.createElement("div", {
      className: "territory-hint",
      style: {
        margin: 0,
        fontSize: 11
      }
    }, r.piece)), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("textarea", {
      rows: Math.min(6, Math.max(2, Math.ceil(String((r.kind === "draft" ? r.angle : r.prompt) || "").length / 95))),
      value: r.kind === "draft" ? r.angle || "" : r.prompt || "",
      placeholder: r.kind === "draft" ? "Optional: the take for this channel" : "Optional: what the image or video should show",
      onChange: e => setRow(r.key, r.kind === "draft" ? {
        angle: e.target.value
      } : {
        prompt: e.target.value
      }),
      style: {
        ...inp,
        fontSize: 12,
        padding: "5px 7px",
        resize: "vertical",
        minHeight: 40
      }
    })), /*#__PURE__*/React.createElement("td", null, r.kind === "draft" ? /*#__PURE__*/React.createElement(Status, {
      s: r.status === "to write" ? "draft" : r.status
    }) : /*#__PURE__*/React.createElement(Chip, {
      text: "to make",
      bg: "var(--plk-warning-text)"
    })), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("input", {
      type: "date",
      value: r.when,
      onChange: e => setRow(r.key, {
        when: e.target.value
      }),
      style: {
        ...inp,
        padding: "4px 6px"
      }
    })))))), this.footer(`${plan.filter(r => r.on && r.kind === "draft").length} drafts · ${plan.filter(r => r.on && r.kind === "media").length} media`, "brief", ["Write the drafts →", () => {
      this.saveState({
        plan,
        stage: "write"
      });
      this.setState({
        plan,
        stage: "write"
      });
    }]));
  }
  // Localization (slice 1): a translated variant carries a `locale_meta` block {variantId, locale, market, marketId, sourceVariantId}.
  // Social media attachment (2026-10-02): what ships with a social post. A `social_media` block {variantId, imageUrl|videoUrl}
  // on the piece records a pick; otherwise the piece's first image (or that variant's rendered video) goes, as before.
  socialMediaFor(v, dArg) {
    const d = dArg || this.state.details[String(v.piece ? v.piece.id : v.containerId)] || {};
    const pick = parseJson(latestBlock(d.blocks, "social_media", forVariant(v)));
    if (pick && (pick.imageUrl || pick.videoUrl)) return {
      ...pick,
      picked: true
    };
    const vb = videoRefFor(d && d.blocks && d.blocks, v.id);
    if (vb) return {
      videoUrl: vb.assetRef,
      picked: false
    };
    const first = (d && d.blocks || []).find(b => /image/i.test(b.kind || ""));
    return {
      imageUrl: first ? first.assetRef || first.body : null,
      picked: false
    };
  }
  renderAttach(v) {
    const cur = this.socialMediaFor(v);
    const open = this.state.mediaPick === String(v.id);
    const images = this.storyImages();
    const videos = this.renderedVideos();
    const thumb = cur.videoUrl ? /*#__PURE__*/React.createElement("video", {
      src: cur.videoUrl,
      className: "hub-bld-attach-video",
      muted: true,
      playsInline: true,
      preload: "metadata"
    }) : cur.imageUrl ? /*#__PURE__*/React.createElement("img", {
      src: cur.imageUrl,
      alt: ""
    }) : /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "hide_image");
    return /*#__PURE__*/React.createElement("div", {
      className: "hub-bld-attach"
    }, /*#__PURE__*/React.createElement("div", {
      className: "hub-bld-attach-row"
    }, /*#__PURE__*/React.createElement("span", {
      className: "hub-bld-attach-thumb" + (cur.imageUrl || cur.videoUrl ? "" : " none")
    }, thumb), /*#__PURE__*/React.createElement("span", {
      className: "hub-bld-attach-text"
    }, /*#__PURE__*/React.createElement("b", null, cur.videoUrl ? "Video" : cur.imageUrl ? "Image" : "No media", cur.picked ? "" : cur.imageUrl || cur.videoUrl ? " · story default" : ""), /*#__PURE__*/React.createElement("i", null, cur.imageUrl || cur.videoUrl ? "Goes out with this post" : String(v.channel || "").toLowerCase() === "social_instagram" || String(v.channel || "").toLowerCase() === "social_pinterest" ? "Required for this channel" : "Text only")), /*#__PURE__*/React.createElement("button", {
      className: "territory-toggle xs",
      onClick: () => this.setState({
        mediaPick: open ? null : String(v.id)
      })
    }, open ? "Close" : "Change")), open && /*#__PURE__*/React.createElement("div", {
      className: "hub-bld-attach-pick"
    }, images.map(u => /*#__PURE__*/React.createElement("span", {
      key: u,
      className: "hub-bld-attach-opt" + (cur.imageUrl === u ? " on" : ""),
      onClick: () => this.setSocialMedia(v, {
        imageUrl: u
      })
    }, /*#__PURE__*/React.createElement("img", {
      src: u,
      alt: "",
      loading: "lazy"
    }))), videos.map(({
      v: vv,
      url
    }) => /*#__PURE__*/React.createElement("span", {
      key: String(vv.id),
      className: "hub-bld-attach-opt video" + (cur.videoUrl === url ? " on" : ""),
      title: vv.body || vv.title || "video",
      onClick: () => this.setSocialMedia(v, {
        videoUrl: url
      })
    }, /*#__PURE__*/React.createElement("video", {
      src: url,
      className: "hub-bld-attach-video",
      muted: true,
      playsInline: true,
      preload: "metadata"
    }), /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "play_arrow"))), !images.length && !videos.length && /*#__PURE__*/React.createElement("span", {
      className: "territory-hint",
      style: {
        margin: 0
      }
    }, "No images or videos on this story yet: make some under Media."), cur.picked && /*#__PURE__*/React.createElement("button", {
      className: "territory-toggle xs",
      onClick: () => this.setSocialMedia(v, {})
    }, "Use the story default")));
  }
  localeFor(v) {
    const d = this.state.details[String(v.piece ? v.piece.id : v.containerId)] || {};
    return parseJson(latestBlock(d.blocks, "locale_meta", forVariant(v)));
  }
  localeOptions() {
    const m = this.state.markets;
    if (!m) return [];
    // Added-but-unpublished languages count too: Shopify accepts translations for them, they just stay off the storefront until published.
    return (m.locales || []).filter(l => !l.primary).map(l => {
      const mk = (m.markets || []).filter(x => x.enabled && (x.locales || []).includes(l.locale));
      return {
        locale: l.locale,
        name: l.name || l.locale,
        market: mk[0] ? mk[0].name : "",
        marketId: mk[0] ? mk[0].id : "",
        label: `${l.name || l.locale}${mk.length ? ` · ${mk.map(x => x.name).join(", ")}` : ""}${l.published ? "" : " · not published yet"}`
      };
    });
  }
  // Version history (slice 4): the API writes a `variant_history` block before every edit; Restore is just another edit.
  historyFor(v) {
    const d = this.state.details[String(v.piece ? v.piece.id : v.containerId)] || {};
    return (d.blocks || []).filter(b => b.kind === "variant_history" && forVariant(v)(b)).map(parseJson).filter(Boolean).sort((a, b) => new Date(b.at) - new Date(a.at));
  }
  renderHistory(v) {
    const hs = this.historyFor(v);
    if (!hs.length) return null;
    const id = String(v.id);
    const open = !!this.state.histOpen[id];
    return /*#__PURE__*/React.createElement("div", {
      style: {
        fontSize: 11.5
      }
    }, /*#__PURE__*/React.createElement(Btn, {
      flat: true,
      onClick: () => this.setState({
        histOpen: {
          ...this.state.histOpen,
          [id]: !open
        }
      })
    }, open ? "Hide history" : `History · ${hs.length}`), open && hs.map((h, i) => /*#__PURE__*/React.createElement("div", {
      key: i,
      style: {
        borderTop: "1px dashed var(--plk-border)",
        padding: "4px 0"
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "territory-hint",
      style: {
        margin: 0
      }
    }, h.at ? new Date(h.at).toLocaleString() : "", h.by ? ` · ${h.by}` : ""), /*#__PURE__*/React.createElement("div", {
      style: {
        whiteSpace: "pre-wrap",
        maxHeight: 72,
        overflow: "auto",
        color: "var(--plk-text)"
      }
    }, h.title ? /*#__PURE__*/React.createElement("b", null, h.title, ": ") : null, strip(h.body).slice(0, 320)), /*#__PURE__*/React.createElement(Btn, {
      flat: true,
      onClick: () => this.restoreVersion(v, h)
    }, "Restore this version"))));
  }
  // Comments (slice 4): `comment` blocks {variantId, text, by, at} on the piece.
  commentsFor(v) {
    const d = this.state.details[String(v.piece ? v.piece.id : v.containerId)] || {};
    return (d.blocks || []).filter(b => b.kind === "comment" && forVariant(v)(b)).map(parseJson).filter(c => c && c.text).sort((a, b) => new Date(a.at) - new Date(b.at));
  }
  renderComments(v) {
    const id = String(v.id);
    const cs = this.commentsFor(v);
    const open = !!this.state.cmtOpen[id];
    return /*#__PURE__*/React.createElement("div", {
      style: {
        fontSize: 11.5
      }
    }, /*#__PURE__*/React.createElement(Btn, {
      flat: true,
      onClick: () => this.setState({
        cmtOpen: {
          ...this.state.cmtOpen,
          [id]: !open
        }
      })
    }, open ? "Hide comments" : cs.length ? `Comments · ${cs.length}` : "Comment"), open && /*#__PURE__*/React.createElement("div", {
      style: {
        display: "grid",
        gap: 4
      }
    }, cs.map((c, i) => /*#__PURE__*/React.createElement("div", {
      key: i,
      style: {
        borderTop: "1px dashed var(--plk-border)",
        padding: "4px 0"
      }
    }, /*#__PURE__*/React.createElement("span", {
      className: "territory-hint",
      style: {
        margin: 0
      }
    }, c.by || "someone", " \xB7 ", c.at ? new Date(c.at).toLocaleString() : ""), /*#__PURE__*/React.createElement("div", {
      style: {
        whiteSpace: "pre-wrap"
      }
    }, c.text))), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: 6
      }
    }, /*#__PURE__*/React.createElement("input", {
      style: {
        ...inp,
        padding: "5px 7px"
      },
      value: this.state.commentDraft[id] || "",
      placeholder: "Leave a note for the team\u2026",
      onChange: e => this.setState({
        commentDraft: {
          ...this.state.commentDraft,
          [id]: e.target.value
        }
      }),
      onKeyDown: e => {
        if (e.key === "Enter") this.addComment(v);
      }
    }), /*#__PURE__*/React.createElement(Btn, {
      primary: true,
      disabled: !(this.state.commentDraft[id] || "").trim(),
      onClick: () => this.addComment(v)
    }, "Post"))));
  }
  // SEO brief (slice 5): content_seo_brief_agent → `seo_brief` block per variant; meta title/description ride the Shopify write.
  seoFor(v) {
    const d = this.state.details[String(v.piece ? v.piece.id : v.containerId)] || {};
    const b = latestBlock(d.blocks, "seo_brief", x => {
      try {
        return String(JSON.parse(x.body || "{}").variantId) === String(v.id);
      } catch (e) {
        return false;
      }
    });
    if (!b) return null;
    try {
      return JSON.parse(b.body || "{}");
    } catch (e) {
      return null;
    }
  }
  renderSeo(v) {
    const id = String(v.id);
    const seo = this.seoFor(v);
    const open = !!this.state.seoOpen[id];
    const busy = !!this.state.busy[`seo:${id}`];
    const ed = this.state.editing[`seo:${id}`];
    const setEd = patch => this.setState({
      editing: {
        ...this.state.editing,
        [`seo:${id}`]: {
          ...(ed || {
            metaTitle: seo.metaTitle || "",
            metaDescription: seo.metaDescription || "",
            slug: seo.slug || ""
          }),
          ...patch
        }
      }
    });
    return /*#__PURE__*/React.createElement("div", {
      style: {
        borderTop: "1px dashed var(--plk-border)",
        paddingTop: 6,
        marginTop: 2
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        alignItems: "center",
        gap: 6,
        flexWrap: "wrap"
      }
    }, /*#__PURE__*/React.createElement(Btn, {
      flat: true,
      disabled: busy,
      onClick: () => seo && !open ? this.setState({
        seoOpen: {
          ...this.state.seoOpen,
          [id]: true
        }
      }) : seo ? this.setState({
        seoOpen: {
          ...this.state.seoOpen,
          [id]: false
        }
      }) : this.seoBrief(v)
    }, busy ? "Writing brief…" : seo ? open ? "Hide SEO brief" : "SEO brief ✓" : "SEO brief →"), !seo && /*#__PURE__*/React.createElement("input", {
      style: {
        ...inp,
        width: 180,
        height: 30,
        padding: "0 8px",
        margin: 0
      },
      placeholder: "target keyword (optional)",
      value: this.state.seoKeyword[id] || "",
      onChange: e => this.setState({
        seoKeyword: {
          ...this.state.seoKeyword,
          [id]: e.target.value
        }
      })
    }), seo && open && /*#__PURE__*/React.createElement(Btn, {
      flat: true,
      disabled: busy,
      onClick: () => this.seoBrief(v)
    }, "Redo")), seo && open && /*#__PURE__*/React.createElement("div", {
      style: {
        fontSize: 12,
        marginTop: 6,
        display: "grid",
        gap: 6
      }
    }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("b", null, seo.targetKeyword), " ", /*#__PURE__*/React.createElement("span", {
      className: "territory-hint",
      style: {
        margin: 0
      }
    }, "\xB7 ", seo.searchIntent, (seo.secondaryKeywords || []).length ? ` · also: ${(seo.secondaryKeywords || []).join(", ")}` : "")), /*#__PURE__*/React.createElement("label", null, "Meta title ", /*#__PURE__*/React.createElement("span", {
      className: "territory-hint",
      style: {
        margin: 0
      }
    }, "(", (ed ? ed.metaTitle : seo.metaTitle || "").length, "/60)"), /*#__PURE__*/React.createElement("input", {
      style: {
        ...inp,
        marginTop: 2
      },
      value: ed ? ed.metaTitle : seo.metaTitle || "",
      onChange: e => setEd({
        metaTitle: e.target.value
      })
    })), /*#__PURE__*/React.createElement("label", null, "Meta description ", /*#__PURE__*/React.createElement("span", {
      className: "territory-hint",
      style: {
        margin: 0
      }
    }, "(", (ed ? ed.metaDescription : seo.metaDescription || "").length, "/155)"), /*#__PURE__*/React.createElement("textarea", {
      rows: 3,
      style: {
        ...inp,
        marginTop: 2,
        resize: "vertical",
        height: 64,
        minHeight: 64
      },
      value: ed ? ed.metaDescription : seo.metaDescription || "",
      onChange: e => setEd({
        metaDescription: e.target.value
      })
    })), /*#__PURE__*/React.createElement("label", null, "Slug", /*#__PURE__*/React.createElement("input", {
      style: {
        ...inp,
        marginTop: 2
      },
      value: ed ? ed.slug : seo.slug || "",
      onChange: e => setEd({
        slug: e.target.value
      })
    })), ed && /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: 6
      }
    }, /*#__PURE__*/React.createElement(Btn, {
      primary: true,
      onClick: async () => {
        await this.saveSeo(v, {
          ...seo,
          ...ed
        });
        const editing = {
          ...this.state.editing
        };
        delete editing[`seo:${id}`];
        this.setState({
          editing
        });
      }
    }, "Save"), /*#__PURE__*/React.createElement(Btn, {
      flat: true,
      onClick: () => {
        const editing = {
          ...this.state.editing
        };
        delete editing[`seo:${id}`];
        this.setState({
          editing
        });
      }
    }, "Cancel")), (seo.outline || []).length > 0 && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("b", null, "Outline"), /*#__PURE__*/React.createElement("ol", {
      style: {
        margin: "2px 0 0 16px",
        padding: 0
      }
    }, seo.outline.map((o, i) => /*#__PURE__*/React.createElement("li", {
      key: i
    }, /*#__PURE__*/React.createElement("b", {
      style: {
        fontWeight: 600
      }
    }, o.h2), o.covers ? /*#__PURE__*/React.createElement("span", {
      className: "territory-hint",
      style: {
        margin: 0
      }
    }, ": ", o.covers) : null)))), (seo.faqs || []).length > 0 && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("b", null, "FAQs"), seo.faqs.map((f, i) => /*#__PURE__*/React.createElement("div", {
      key: i,
      style: {
        marginTop: 2
      }
    }, /*#__PURE__*/React.createElement("span", {
      style: {
        fontWeight: 600
      }
    }, f.q), " ", /*#__PURE__*/React.createElement("span", {
      className: "territory-hint",
      style: {
        margin: 0
      }
    }, f.a)))), (seo.internalLinks || []).length > 0 && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("b", null, "Internal links"), " ", seo.internalLinks.map((l, i) => /*#__PURE__*/React.createElement("span", {
      key: i
    }, i ? " · " : "", /*#__PURE__*/React.createElement("a", {
      href: l.url,
      target: "_blank",
      rel: "noreferrer"
    }, l.anchor)))), (seo.gaps || []).length > 0 && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("b", null, "Gaps"), " ", /*#__PURE__*/React.createElement("span", {
      className: "territory-hint",
      style: {
        margin: 0
      }
    }, seo.gaps.join(" · "))), v.title !== seo.metaTitle && seo.metaTitle && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Btn, {
      flat: true,
      onClick: async () => {
        try {
          if (!(await this.saveText(v, {
            title: seo.metaTitle,
            body: v.body || ""
          }))) return;
          await this.reloadDetails();
        } catch (e) {
          toast("Could not update the title.");
        }
      }
    }, "Use meta title as the draft title"))));
  }
  // Email extras (item 7): subject alternatives + preheader from the repurposer (email_meta block), segment versions.
  emailMetaFor(v) {
    const d = this.state.details[String(v.piece ? v.piece.id : v.containerId)] || {};
    const b = latestBlock(d.blocks, "email_meta", x => {
      try {
        return String(JSON.parse(x.body || "{}").variantId) === String(v.id);
      } catch (e) {
        return false;
      }
    });
    if (!b) return null;
    try {
      return JSON.parse(b.body || "{}");
    } catch (e) {
      return null;
    }
  }
  renderEmailExtras(v) {
    const meta = this.emailMetaFor(v) || {};
    const subjects = (meta.subjects || []).filter(Boolean);
    const id = String(v.id);
    const pe = this.state.previewEdit[id];
    const aud = this.state.audiences || [];
    return /*#__PURE__*/React.createElement("div", {
      style: {
        borderTop: "1px dashed var(--plk-border)",
        paddingTop: 6,
        marginTop: 2,
        fontSize: 12,
        display: "grid",
        gap: 6
      }
    }, meta.segment && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Chip, {
      text: `version for ${meta.segment}`,
      bg: "var(--plk-info-text)"
    })), subjects.length > 1 && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("b", null, "Subject line"), subjects.map((sub, i) => /*#__PURE__*/React.createElement("label", {
      key: i,
      style: {
        display: "flex",
        gap: 6,
        alignItems: "flex-start",
        marginTop: 3,
        cursor: "pointer"
      }
    }, /*#__PURE__*/React.createElement("input", {
      type: "radio",
      name: `subj:${id}`,
      checked: sub === v.title,
      onChange: () => this.useSubject(v, sub),
      style: {
        appearance: "auto",
        WebkitAppearance: "radio",
        margin: "2px 0 0",
        width: 14,
        height: 14,
        flex: "0 0 14px"
      }
    }), /*#__PURE__*/React.createElement("span", {
      style: {
        flex: 1
      }
    }, sub)))), /*#__PURE__*/React.createElement("label", null, "Preview text ", /*#__PURE__*/React.createElement("span", {
      className: "territory-hint",
      style: {
        margin: 0
      }
    }, "\xB7 the preheader next to the subject in the inbox (", (pe != null ? pe : meta.previewText || "").length, "/90)"), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: 6,
        marginTop: 2
      }
    }, /*#__PURE__*/React.createElement("input", {
      style: {
        ...inp,
        padding: "5px 7px"
      },
      value: pe != null ? pe : meta.previewText || "",
      onChange: e => this.setState({
        previewEdit: {
          ...this.state.previewEdit,
          [id]: e.target.value
        }
      }),
      placeholder: "e.g. Early-season boards are in: free shipping this week"
    }), pe != null && pe !== (meta.previewText || "") && /*#__PURE__*/React.createElement(Btn, {
      primary: true,
      onClick: async () => {
        await this.saveEmailMeta(v, {
          previewText: pe
        });
        const previewEdit = {
          ...this.state.previewEdit
        };
        delete previewEdit[id];
        this.setState({
          previewEdit
        });
      }
    }, "Save"))), !meta.segment && aud.length > 0 && /*#__PURE__*/React.createElement("label", null, "Version for an audience ", /*#__PURE__*/React.createElement("span", {
      className: "territory-hint",
      style: {
        margin: 0
      }
    }, "\xB7 a second email written for that segment"), /*#__PURE__*/React.createElement("select", {
      style: {
        ...inp,
        marginTop: 2,
        padding: "4px 6px"
      },
      value: "",
      disabled: !!this.state.busy[`seg:${id}`],
      onChange: e => {
        const a = aud.find(x => String(x.id) === e.target.value);
        this.segmentVersion(v, a);
      }
    }, /*#__PURE__*/React.createElement("option", {
      value: ""
    }, this.state.busy[`seg:${id}`] ? "Writing…" : "Choose an audience…"), aud.map(a => /*#__PURE__*/React.createElement("option", {
      key: String(a.id),
      value: String(a.id)
    }, a.name, a.total != null ? ` · ${num(a.total)}` : "")))));
  }
  renderWrite() {
    const variants = this.allVariants().filter(v => String(v.channel || "").toLowerCase() !== "video");
    const planned = (this.state.plan || []).filter(r => r.on && r.kind === "draft");
    const approved = variants.filter(v => ["approved", "published", "live"].includes(String(v.status || "").toLowerCase())).length;
    const missing = planned.filter(r => !variants.some(v => String(v.channel || "").toLowerCase() === r.channel));
    return /*#__PURE__*/React.createElement("div", null, this.head("Drafts", "Grounded on every piece's copy and the linked products. Edit inline, then approve.", /*#__PURE__*/React.createElement(Btn, {
      story: true,
      disabled: !!this.state.busy.drafts || !missing.length,
      onClick: this.generateDrafts
    }, this.state.busy.drafts ? "Writing…" : missing.length ? `Write ${missing.length} missing draft${missing.length === 1 ? "" : "s"}` : "All planned drafts exist")), !variants.length && /*#__PURE__*/React.createElement("div", {
      className: "hub-bld-empty"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "edit_note"), "No drafts yet. Write the planned drafts and they land here within a few minutes."), /*#__PURE__*/React.createElement("div", {
      className: "hub-bld-drafts"
    }, variants.map(v => {
      const ed = this.state.editing[String(v.id)];
      const st = String(v.status || "").toLowerCase();
      const lm = this.localeFor(v);
      const opts = lm ? [] : this.localeOptions();
      const busyTr = !!this.state.busy[`tr:${v.id}`];
      return /*#__PURE__*/React.createElement("div", {
        key: String(v.id),
        className: "hub-bld-draft"
      }, /*#__PURE__*/React.createElement("div", {
        className: "hub-bld-draft-head"
      }, /*#__PURE__*/React.createElement("span", {
        className: "material-icons"
      }, CHANNEL_ICON[String(v.channel || "").toLowerCase()] || "description"), /*#__PURE__*/React.createElement("b", null, label(v.channel)), v.templateId && this.tpl(v.templateId) && /*#__PURE__*/React.createElement("span", {
        className: "hub-tpl-chip"
      }, this.tpl(v.templateId).name), /*#__PURE__*/React.createElement(Status, {
        s: v.status
      }), lm && /*#__PURE__*/React.createElement(Chip, {
        text: `${lm.locale}${lm.market ? ` · ${lm.market}` : ""}`,
        bg: "var(--plk-info-text)"
      }), /*#__PURE__*/React.createElement("span", {
        className: "territory-hint",
        style: {
          margin: "0 0 0 auto",
          fontSize: 11
        }
      }, v.piece.title)), ed ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("input", {
        style: inp,
        value: ed.title,
        onChange: e => this.setState({
          editing: {
            ...this.state.editing,
            [String(v.id)]: {
              ...ed,
              title: e.target.value
            }
          }
        })
      }), /*#__PURE__*/React.createElement("textarea", {
        rows: 8,
        style: {
          ...inp,
          resize: "vertical"
        },
        value: ed.body,
        onChange: e => this.setState({
          editing: {
            ...this.state.editing,
            [String(v.id)]: {
              ...ed,
              body: e.target.value
            }
          }
        })
      }), /*#__PURE__*/React.createElement("div", {
        style: {
          display: "flex",
          gap: 6
        }
      }, /*#__PURE__*/React.createElement(Btn, {
        primary: true,
        onClick: () => this.saveVariant(v)
      }, "Save"), /*#__PURE__*/React.createElement(Btn, {
        flat: true,
        onClick: () => {
          const editing = {
            ...this.state.editing
          };
          delete editing[String(v.id)];
          this.setState({
            editing
          });
        }
      }, "Cancel"))) : /*#__PURE__*/React.createElement(React.Fragment, null, v.title && /*#__PURE__*/React.createElement("div", {
        className: "hub-bld-draft-title"
      }, v.title), /*#__PURE__*/React.createElement("p", {
        className: "hub-bld-draft-body"
      }, strip(v.body).slice(0, 900), strip(v.body).length > 900 ? "…" : ""), /*#__PURE__*/React.createElement("div", {
        className: "hub-bld-draft-actions"
      }, st === "draft" ? /*#__PURE__*/React.createElement(Btn, {
        primary: true,
        onClick: () => this.setVariantStatus(v, "approved")
      }, "Approve") : st === "approved" ? /*#__PURE__*/React.createElement(Btn, {
        flat: true,
        onClick: () => this.setVariantStatus(v, "draft")
      }, "Unapprove") : null, /*#__PURE__*/React.createElement(Btn, {
        flat: true,
        onClick: () => this.setState({
          editing: {
            ...this.state.editing,
            [String(v.id)]: {
              title: v.title || "",
              body: v.body || ""
            }
          }
        })
      }, "Edit"), /*#__PURE__*/React.createElement(Btn, {
        flat: true,
        title: "Capture the shape of this piece as a template for the next story",
        onClick: () => this.setState({
          tplSheet: {
            kind: "variant",
            variantId: String(v.id),
            channel: String(v.channel || "").toLowerCase()
          }
        })
      }, "Save as template")), SOCIAL_CHANNELS.has(String(v.channel || "").toLowerCase()) && this.renderAttach(v), SEO_CHANNELS.has(String(v.channel || "").toLowerCase()) && this.renderSeo(v), String(v.channel || "").toLowerCase() === "email" && this.renderEmailExtras(v), opts.length > 0 && /*#__PURE__*/React.createElement("label", {
        style: {
          fontSize: 11.5
        }
      }, "Translate for a market", /*#__PURE__*/React.createElement("select", {
        style: {
          ...inp,
          marginTop: 2,
          padding: "4px 6px"
        },
        value: "",
        disabled: busyTr,
        onChange: e => this.translateFor(v, opts.find(o => o.locale === e.target.value))
      }, /*#__PURE__*/React.createElement("option", {
        value: ""
      }, busyTr ? "Translating…" : "Choose a language…"), opts.map(o => /*#__PURE__*/React.createElement("option", {
        key: o.locale,
        value: o.locale
      }, o.label)))), /*#__PURE__*/React.createElement("div", {
        style: {
          display: "flex",
          gap: 4,
          flexWrap: "wrap",
          borderTop: "1px dashed var(--plk-border)",
          paddingTop: 4
        }
      }, this.renderHistory(v), this.renderComments(v))));
    })), this.footer(`${approved} of ${variants.length} approved`, "plan", ["Make the media →", () => this.go("make")]));
  }
  videoUrlOf(done) {
    return done && done.ok ? done.ctx && (done.ctx.videoUrl || done.ctx.url) || done.res && (done.res.videoUrl || done.res.url) : null;
  }
  renderedVideos() {
    return this.allVariants().filter(v => String(v.channel || "").toLowerCase() === "video").map(v => {
      const d = this.state.details[String(v.piece.id)] || {};
      const vb = videoRefFor(d.blocks, v.id);
      const rk = vb && /row:(\S+)/.exec(String(vb.body || ""));
      const row = rk ? (this.state.plan || []).find(r => r.key === rk[1]) : null;
      return vb ? {
        v,
        url: vb.assetRef,
        rowKey: rk ? rk[1] : null,
        vertical: row && row.aspect === "9:16" || /9:16/.test(String(v.body || ""))
      } : null;
    }).filter(Boolean);
  }
  renderMedia() {
    const rows = (this.state.plan || []).filter(r => r.on && r.kind === "media");
    const sb = this.state.storyboard;
    const imgs = this.storyImages();
    return /*#__PURE__*/React.createElement("div", null, this.head("Media", "The story's own product image is the reference, so the product stays true. Images get alt text. A video is a storyboard you edit before the render. Everything lands on the story's primary piece and in Assets."), !rows.length && /*#__PURE__*/React.createElement("div", {
      className: "hub-bld-empty"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "image"), "No media planned. Go back to Brief and pick some."), rows.some(r => r.media.startsWith("video")) && /*#__PURE__*/React.createElement("div", {
      className: "hub-bld-settings"
    }, /*#__PURE__*/React.createElement(VoicePicker, {
      label: "Narration voice",
      value: this.state.bs.voice || "Joanna",
      selectStyle: {
        ...inp,
        width: "auto",
        padding: "4px 8px"
      },
      onChange: voice => {
        this.setState({
          bs: {
            ...this.state.bs,
            voice
          }
        });
        this.saveState({
          voice
        });
      }
    }), /*#__PURE__*/React.createElement("label", {
      className: "grow"
    }, "Music bed", /*#__PURE__*/React.createElement("input", {
      style: {
        ...inp,
        padding: "4px 8px"
      },
      value: this.state.bs.musicUrl || "",
      placeholder: "https://\u2026 .mp3 (optional: mixed under the narration, loops to fit)",
      onChange: e => this.setState({
        bs: {
          ...this.state.bs,
          musicUrl: e.target.value
        }
      }),
      onBlur: e => this.saveState({
        musicUrl: e.target.value.trim()
      })
    })), /*#__PURE__*/React.createElement("span", {
      className: "territory-hint",
      style: {
        margin: 0
      }
    }, "apply to every narrated render from this story")), /*#__PURE__*/React.createElement("div", {
      className: "hub-bld-media"
    }, rows.map(r => {
      const isClip = r.media === "cinematic";
      const isVideo = r.media.startsWith("video") || isClip;
      const count = r.media === "lifestyle" ? 3 : r.media === "sceneset" ? 4 : 1;
      const out = this.state.mediaOut[r.key];
      const vert = this.state.mediaOut[`${r.key}:916`];
      return /*#__PURE__*/React.createElement("div", {
        key: r.key,
        className: "hub-bld-mrow"
      }, /*#__PURE__*/React.createElement("div", {
        className: "hub-bld-mcell item"
      }, /*#__PURE__*/React.createElement("span", {
        className: "material-icons"
      }, isVideo ? "movie" : "image"), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("b", null, r.label), /*#__PURE__*/React.createElement("i", null, r.aspect, count > 1 ? ` · ${count} images` : ""))), /*#__PURE__*/React.createElement("div", {
        className: "hub-bld-mcell result"
      }, isVideo ? this.state.mediaOut[r.key] ? /*#__PURE__*/React.createElement(ClipPlayer, {
        src: this.state.mediaOut[r.key],
        className: "hub-bld-clip"
      }) : /*#__PURE__*/React.createElement("span", {
        className: "territory-hint",
        style: {
          margin: 0
        }
      }, sb && sb.key === r.key ? `${sb.scenes.length} scenes ready to render` : "–") : /*#__PURE__*/React.createElement("div", {
        className: "hub-bld-thumbs"
      }, Array.from({
        length: count
      }).map((_, i) => {
        const u = this.state.mediaOut[`${r.key}:${i}`];
        return u ? /*#__PURE__*/React.createElement("span", {
          key: i,
          className: "hub-bld-thumb"
        }, /*#__PURE__*/React.createElement("img", {
          src: u,
          alt: ""
        }), /*#__PURE__*/React.createElement("span", {
          className: "hub-bld-thumb-rail"
        }, /*#__PURE__*/React.createElement("button", {
          className: "territory-toggle xs",
          title: "Edit Mode: every edit becomes a new version and replaces this image here",
          onClick: () => this.setState({
            editMedia: {
              key: `${r.key}:${i}`,
              url: u
            }
          })
        }, /*#__PURE__*/React.createElement("span", {
          className: "material-icons"
        }, "tune")), /*#__PURE__*/React.createElement("button", {
          className: "territory-toggle xs",
          title: "Smart Resize to a channel set (saved in Assets)",
          onClick: () => this.setState({
            resizeMedia: {
              key: `${r.key}:${i}`,
              url: u
            }
          })
        }, /*#__PURE__*/React.createElement("span", {
          className: "material-icons"
        }, "aspect_ratio")))) : /*#__PURE__*/React.createElement("span", {
          key: i,
          className: "hub-bld-thumb empty"
        }, /*#__PURE__*/React.createElement("span", {
          className: "material-icons"
        }, "image"));
      }))), /*#__PURE__*/React.createElement("div", {
        className: "hub-bld-mcell actions"
      }, isClip ? /*#__PURE__*/React.createElement("div", {
        style: {
          display: "flex",
          gap: 6,
          flexWrap: "wrap"
        }
      }, /*#__PURE__*/React.createElement(Btn, {
        primary: !out,
        disabled: !!this.state.busy[`clip:${r.key}`] || !imgs.length,
        onClick: () => this.makeClip(r)
      }, this.state.busy[`clip:${r.key}`] ? "Rendering…" : out ? "Render again" : "Render clip"), out && !vert && /*#__PURE__*/React.createElement(Btn, {
        disabled: !!this.state.busy[`rf:${r.key}`],
        onClick: () => this.reframe(out, r.key)
      }, this.state.busy[`rf:${r.key}`] ? "Reframing…" : "Reframe 9:16"), vert && /*#__PURE__*/React.createElement("a", {
        href: vert,
        target: "_blank",
        rel: "noreferrer",
        style: {
          fontSize: 12,
          alignSelf: "center"
        }
      }, "9:16 \u2197")) : isVideo ? /*#__PURE__*/React.createElement("div", {
        style: {
          display: "flex",
          gap: 6,
          flexWrap: "wrap"
        }
      }, /*#__PURE__*/React.createElement(Btn, {
        disabled: !!this.state.busy[`sb:${r.key}`],
        onClick: () => this.writeStoryboard(r)
      }, this.state.busy[`sb:${r.key}`] ? "Writing…" : sb && sb.key === r.key ? "Rewrite storyboard" : "Write storyboard →"), !(sb && sb.key === r.key) && r.aspect !== "9:16" && /*#__PURE__*/React.createElement(PreviewLineButton, {
        className: "territory-toggle sm",
        voice: this.state.bs.voice || "Joanna",
        text: this.storyTitle()
      }), /*#__PURE__*/React.createElement(Btn, {
        primary: true,
        disabled: !(sb && sb.key === r.key) || !!this.state.busy[`rv:${r.key}`],
        onClick: () => this.renderVideo(r)
      }, this.state.busy[`rv:${r.key}`] ? "Rendering…" : "Render"), out && r.aspect === "16:9" && !vert && /*#__PURE__*/React.createElement(Btn, {
        disabled: !!this.state.busy[`rf:${r.key}`],
        onClick: () => this.reframe(out, r.key)
      }, this.state.busy[`rf:${r.key}`] ? "Reframing…" : "Reframe 9:16"), vert && /*#__PURE__*/React.createElement("a", {
        href: vert,
        target: "_blank",
        rel: "noreferrer",
        style: {
          fontSize: 12,
          alignSelf: "center"
        }
      }, "9:16 \u2197")) : /*#__PURE__*/React.createElement("div", {
        style: {
          display: "flex",
          gap: 6,
          flexWrap: "wrap"
        }
      }, Array.from({
        length: count
      }).map((_, i) => /*#__PURE__*/React.createElement(Btn, {
        key: i,
        primary: !this.state.mediaOut[`${r.key}:${i}`],
        disabled: !!this.state.busy[`${r.key}:${i}`] || !imgs.length,
        title: (SCENE_PROMPTS[r.media] || [])[i] || "",
        onClick: () => this.makeImage(r, i)
      }, this.state.busy[`${r.key}:${i}`] ? "Generating…" : this.state.mediaOut[`${r.key}:${i}`] ? `Regenerate ${count > 1 ? i + 1 : ""}` : `${count > 1 ? (SCENE_LABELS[r.media] || [])[i] || i + 1 : "Generate"}`)), count > 1 && /*#__PURE__*/React.createElement(Btn, {
        flat: true,
        disabled: !imgs.length || Array.from({
          length: count
        }).some((_, i) => this.state.busy[`${r.key}:${i}`]),
        onClick: () => Array.from({
          length: count
        }).forEach((_, i) => {
          if (!this.state.mediaOut[`${r.key}:${i}`]) this.makeImage(r, i);
        })
      }, "All missing"))));
    })), sb && /*#__PURE__*/React.createElement("div", {
      className: "hub-bld-panel"
    }, /*#__PURE__*/React.createElement("div", {
      className: "hub-bld-panel-head"
    }, /*#__PURE__*/React.createElement("b", null, "Storyboard"), /*#__PURE__*/React.createElement("span", null, "edit narration, swap an image, reorder or drop scenes; the render uses exactly these")), sb.scenes.map((sc, i) => /*#__PURE__*/React.createElement("div", {
      key: i,
      style: {
        display: "flex",
        gap: 8,
        alignItems: "flex-start",
        padding: "6px 0",
        borderTop: i ? "1px solid var(--plk-border)" : "none"
      }
    }, /*#__PURE__*/React.createElement("span", {
      className: "territory-hint",
      style: {
        margin: "6px 0 0",
        width: 16
      }
    }, i + 1), /*#__PURE__*/React.createElement("div", {
      style: {
        position: "relative",
        flexShrink: 0
      }
    }, /*#__PURE__*/React.createElement("img", {
      src: sc.imageUrl,
      alt: "",
      style: {
        width: 64,
        height: 64,
        objectFit: "cover",
        borderRadius: 6,
        border: "1px solid var(--plk-border)"
      }
    }), /*#__PURE__*/React.createElement("select", {
      value: sc.imageUrl,
      title: "Swap image",
      onChange: e => this.setSb(sb.scenes.map((x, k) => k === i ? {
        ...x,
        imageUrl: e.target.value
      } : x)),
      style: {
        position: "absolute",
        inset: 0,
        opacity: 0,
        cursor: "pointer",
        width: "100%"
      }
    }, [sc.imageUrl, ...imgs.filter(u => u !== sc.imageUrl)].map((u, k) => /*#__PURE__*/React.createElement("option", {
      key: k,
      value: u
    }, `image ${k + 1}`)))), /*#__PURE__*/React.createElement("div", {
      style: {
        flex: 1,
        minWidth: 0
      }
    }, /*#__PURE__*/React.createElement("textarea", {
      value: sc.narration,
      rows: 2,
      onChange: e => this.setSb(sb.scenes.map((x, k) => k === i ? {
        ...x,
        narration: e.target.value
      } : x)),
      style: {
        ...inp,
        height: 56,
        minHeight: 56,
        resize: "vertical"
      }
    }), /*#__PURE__*/React.createElement("input", {
      value: sc.overlayText || "",
      placeholder: "On-screen text for this scene (optional, a few words)",
      onChange: e => this.setSb(sb.scenes.map((x, k) => k === i ? {
        ...x,
        overlayText: e.target.value
      } : x)),
      style: {
        ...inp,
        marginTop: 4,
        padding: "4px 7px",
        fontSize: 11.5
      }
    })), /*#__PURE__*/React.createElement("span", {
      style: {
        display: "inline-flex",
        flexDirection: "column",
        gap: 2
      }
    }, /*#__PURE__*/React.createElement("button", {
      className: "territory-toggle xs",
      title: "Rewrite this scene's narration",
      disabled: !!this.state.busy[`sc:w:${i}`],
      onClick: () => this.rewriteScene(i)
    }, this.state.busy[`sc:w:${i}`] ? "…" : "↻ words"), i === 0 && /*#__PURE__*/React.createElement(PreviewLineButton, {
      voice: this.state.bs.voice || "Joanna",
      text: sc.narration
    }), /*#__PURE__*/React.createElement("button", {
      className: "territory-toggle xs",
      title: "Generate a new image for this scene",
      disabled: !!this.state.busy[`sc:i:${i}`],
      onClick: () => this.regenSceneImage(i)
    }, this.state.busy[`sc:i:${i}`] ? "…" : "↻ image")), /*#__PURE__*/React.createElement("span", {
      style: {
        display: "inline-flex",
        flexDirection: "column",
        gap: 2
      }
    }, /*#__PURE__*/React.createElement("button", {
      className: "territory-toggle xs",
      disabled: i === 0,
      onClick: () => {
        const n = sb.scenes.slice();
        [n[i - 1], n[i]] = [n[i], n[i - 1]];
        this.setSb(n);
      }
    }, "\u2191"), /*#__PURE__*/React.createElement("button", {
      className: "territory-toggle xs",
      disabled: i === sb.scenes.length - 1,
      onClick: () => {
        const n = sb.scenes.slice();
        [n[i + 1], n[i]] = [n[i], n[i + 1]];
        this.setSb(n);
      }
    }, "\u2193"), /*#__PURE__*/React.createElement("button", {
      className: "territory-toggle xs",
      disabled: sb.scenes.length <= 1,
      onClick: () => this.setSb(sb.scenes.filter((_, k) => k !== i))
    }, "\u2715")))), /*#__PURE__*/React.createElement("button", {
      className: "territory-toggle sm",
      style: {
        marginTop: 6
      },
      disabled: sb.scenes.length >= 8,
      onClick: () => this.setSb([...sb.scenes, {
        imageUrl: imgs[sb.scenes.length % Math.max(imgs.length, 1)] || sb.scenes[0].imageUrl,
        narration: ""
      }])
    }, "+ Scene")), this.state.editMedia && /*#__PURE__*/React.createElement(EditModeModal, {
      asset: this.state.editMedia.url,
      runAgent: this.runAgent,
      waitAgent: this.waitAgent,
      creditsToast: (e, f) => this.agentError(e, f),
      onSaved: v => {
        if (v && v.url) {
          this.setMedia({
            [this.state.editMedia.key]: v.url
          });
          this.reloadDetails();
        }
        this.refreshCredits();
      },
      onClose: () => this.setState({
        editMedia: null
      })
    }), this.state.resizeMedia && /*#__PURE__*/React.createElement(SmartResizeModal, {
      asset: this.state.resizeMedia.url,
      runAgent: this.runAgent,
      waitAgent: this.waitAgent,
      creditsToast: (e, f) => this.agentError(e, f),
      onSaved: () => {
        this.reloadDetails();
        this.refreshCredits();
      },
      onClose: () => this.setState({
        resizeMedia: null
      })
    }), this.renderedVideos().length > 0 && /*#__PURE__*/React.createElement("div", {
      className: "hub-bld-panel"
    }, /*#__PURE__*/React.createElement("div", {
      className: "hub-bld-panel-head"
    }, /*#__PURE__*/React.createElement("b", null, "Rendered videos"), /*#__PURE__*/React.createElement("span", null, "every video variant on the story; 16:9 renders can be reframed to 9:16 for Reels, Shorts and TikTok")), this.renderedVideos().map(({
      v,
      url,
      vertical
    }) => /*#__PURE__*/React.createElement("div", {
      key: String(v.id),
      style: {
        display: "flex",
        gap: 10,
        alignItems: "center",
        padding: "6px 0",
        borderTop: "1px dashed var(--plk-border)"
      }
    }, /*#__PURE__*/React.createElement("span", {
      style: {
        flex: 1,
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
      }
    }, v.body || v.title, " ", /*#__PURE__*/React.createElement("a", {
      href: url,
      target: "_blank",
      rel: "noreferrer"
    }, "open \u2197")), /*#__PURE__*/React.createElement(Status, {
      s: v.status
    }), !vertical && /*#__PURE__*/React.createElement(Btn, {
      disabled: !!this.state.busy[`rf:v${v.id}`],
      onClick: () => this.reframe(url, `v${v.id}`)
    }, this.state.busy[`rf:v${v.id}`] ? "Reframing…" : "Reframe 9:16")))), this.footer(`${Object.keys(this.state.mediaOut).length} made`, "write", ["Ship it →", () => this.go("ship")]));
  }
  // ---------------------------------------------------------------- Ship
  targetsFor(v, d) {
    const ch = String(v.channel || "").toLowerCase();
    const has = p => (this.state.connectors || []).some(c => String(c.provider || "").toLowerCase() === p && String(c.status || "").toLowerCase() === "connected");
    const shopify = !!this.state.shopify;
    const hasProduct = (d && d.links || []).some(l => String(l.linkType || l.link_type || "").toLowerCase() === "product");
    const list = [];
    // A translated variant ships as a Shopify translation of the original's published resource (slice 1).
    const lm = this.localeFor(v);
    if (lm && shopify) {
      const src = this.sourcePublication(lm, d);
      if (src) list.push(["shopify_translate", `Shopify · ${lm.locale} translation${lm.market ? ` (${lm.market})` : ""}`]);
    }
    if (ch === "product_copy" && shopify && hasProduct && !lm) list.push(["shopify_product", "Shopify · product description"]);
    if (ch === "blog" && shopify && !lm) list.push(["shopify_article", "Shopify · blog post"]);
    if ((ch === "landing" || ch === "web") && shopify && !lm) list.push(["shopify_page", "Shopify · page"]);
    if (ch === "video" && shopify && hasProduct) list.push(["shopify_media", "Shopify · product page video"]);
    if ((ch === "blog" || ch === "landing" || ch === "web") && has("wordpress")) list.push(["wordpress", "WordPress · draft post"]);
    if (ch === "email") list.push(["email_draft", "Plunk · email automation draft"]);
    // Social targets only when that account is connected (Manage Data › Connect) — an unconnected publish is dropped by the worker.
    const canPost = {
      facebook: "pages_manage_posts",
      instagram: "instagram_content_publish"
    };
    const soc = p => (this.state.social || []).some(a => String(a.platform || "").toLowerCase() === p && String(a.status || "").toLowerCase() === "connected" && (!canPost[p] || !a.scopes || String(a.scopes).split(",").map(x => x.trim()).includes(canPost[p])));
    if ((ch === "social_facebook" || ch === "video") && soc("facebook")) list.push(["facebook", ch === "video" ? "Facebook · video post" : "Facebook · post"]);
    if ((ch === "social_instagram" || ch === "video") && soc("instagram")) list.push(["instagram", ch === "video" ? "Instagram · reel" : "Instagram · post"]);
    if (ch === "gbp_post" && soc("google_business")) list.push(["gbp", "Google Business Profile · post"]);
    if ((ch === "social_pinterest" || ch === "social_instagram" || ch === "blog") && soc("pinterest")) list.push(["pinterest", "Pinterest · pin"]);
    if ((ch === "social_linkedin" || ch === "social_facebook" || ch === "blog") && soc("linkedin")) list.push(["linkedin", "LinkedIn · company post"]);
    if (ch === "video" && soc("youtube")) list.push(["youtube", "YouTube · upload"]);
    if (ch === "video" && soc("tiktok")) list.push(["tiktok", "TikTok · post"]);
    // Manual target (DS 2026-10-02): pages and copy the user publishes themselves (their own site, a CMS Plunk cannot write to).
    // Only for pieces this Builder made: an approved draft, or one published by Plunk. Imported pages (live at "website"
    // without Plunk having shipped them) and ad attributions never ship from here.
    const vst = String(v.status || "").toLowerCase();
    const livePubs = (d && d.publications || []).filter(p => String(p.variantId) === String(v.id) && pubIsLive(p));
    const builderMade = vst === "approved" || vst === "published" && livePubs.length > 0 && livePubs.every(p => shippedByPlunk(p.target));
    if (["landing", "web", "blog", "product_copy"].includes(ch) && !lm && builderMade) list.push(["manual", "Elsewhere · I publish it myself"]);
    if (has("custom_api")) list.push(["custom_api", "Custom API · your endpoint"]);
    return list;
  }
  sourcePublication(lm, d) {
    return (d && d.publications || []).find(p => String(p.variantId) === String(lm.sourceVariantId) && /^shopify_(product|article|page)$/.test(String(p.target || "")) && (p.externalId || p.external_id));
  }
  // A connected-then-flagged account (expired / revoked / permissions error) explains itself on the Ship row.
  socialProblem(channel) {
    const ch = String(channel || "").toLowerCase();
    const platform = ch === "social_facebook" ? "facebook" : ch === "social_instagram" ? "instagram" : ch === "gbp_post" ? "google_business" : ch === "social_pinterest" ? "pinterest" : ch === "social_linkedin" ? "linkedin" : null;
    if (!platform) return null;
    const a = (this.state.social || []).find(x => String(x.platform || "").toLowerCase() === platform);
    if (!a) return null;
    const need = platform === "facebook" ? "pages_manage_posts" : platform === "instagram" ? "instagram_content_publish" : null;
    if (String(a.status || "").toLowerCase() === "connected") {
      const sc = String(a.scopes || "");
      if (need && sc && !sc.split(",").map(x => x.trim()).includes(need)) return /*#__PURE__*/React.createElement(React.Fragment, null, label(channel), " is connected without the posting permission (", need, "). ", /*#__PURE__*/React.createElement("a", {
        href: "/Data/Connect"
      }, "Reconnect under Manage Data \u203A Connect"), " and approve \"create and manage content on your Page\".");
      return null;
    }
    let detail = String(a.statusDetail || a.status_detail || "");
    try {
      const j = JSON.parse(detail);
      detail = j.error && j.error.message || detail;
    } catch (e) {/* plain text */}
    return /*#__PURE__*/React.createElement(React.Fragment, null, label(channel), " connection is ", a.status, detail ? `: ${detail.slice(0, 140)}` : "", ". ", /*#__PURE__*/React.createElement("a", {
      href: "/Data/Connect"
    }, "Reconnect under Manage Data \u203A Connect"), ".");
  }
  // Plain-words reason a YouTube / TikTok video has no target (shown on its Publish row).
  destProblem(dest) {
    const name = (VIDEO_DEST[dest] || {}).label || dest;
    const a = (this.state.social || []).find(x => String(x.platform || "").toLowerCase() === dest);
    if (!a) return `made for ${name}, which isn't connected: connect it under Manage Data › Connect`;
    const st = String(a.status || "").toLowerCase();
    return st === "connected" ? `made for ${name}` : `made for ${name}: the connection is ${st || "not working"}, reconnect it under Manage Data › Connect`;
  }
  // Ship-once (CONTENT_HUB_SHIP_ONCE_2026-10-02): the live publication of this variant on a target, if any.
  livePub(v, d, target) {
    const t = String(target || "").toLowerCase();
    if (!t) return null;
    const alt = t.startsWith("social_") ? t.slice(7) : `social_${t}`;
    const same = p => {
      const pt = String(p.target || "").toLowerCase();
      return pt === t || pt === alt || t === "email_draft" && pt === "plunk_campaign";
    };
    return (d && d.publications || []).find(p => String(p.variantId) === String(v.id) && same(p) && pubIsLive(p)) || null;
  }
  buildShip() {
    const plan = this.state.plan || [];
    // A clip attached to a Facebook / Instagram post ("Post this clip on…") already goes out with that post: the
    // video's own row must not post it to the same network again (DS 2026-10-21: duplicate FB and IG posts).
    const carried = {};
    const vids = this.renderedVideos();
    this.allVariants().filter(x => ["social_facebook", "social_instagram"].includes(String(x.channel || "").toLowerCase()) && ["approved", "published"].includes(String(x.status || "").toLowerCase())).forEach(x => {
      let m = {};
      try {
        m = this.socialMediaFor(x, this.state.details[String(x.piece.id)]) || {};
      } catch (e) {
        m = {};
      }
      if (!m.videoUrl) return;
      vids.filter(rv => rv.url === m.videoUrl).forEach(rv => {
        const k = String(rv.v.id);
        (carried[k] = carried[k] || new Set()).add(String(x.channel).toLowerCase() === "social_facebook" ? "facebook" : "instagram");
      });
    });
    return this.allVariants().filter(v => ["approved", "published"].includes(String(v.status || "").toLowerCase())).map(v => {
      const d = this.state.details[String(v.piece.id)];
      const targets = this.targetsFor(v, d).filter(([k]) => !(carried[String(v.id)] && carried[String(v.id)].has(k)));
      // a published variant only shows when it still has a target it has not gone to, or an owned target to update
      // "manual" counts as open only while the piece is live nowhere: a page already on Shopify or WordPress does not re-list for it
      const anyLive = (d && d.publications || []).some(p => String(p.variantId) === String(v.id) && pubIsLive(p));
      const open = targets.filter(([k]) => k === "manual" ? !anyLive : !this.livePub(v, d, k));
      let firstTarget = open.length ? open[0][0] : targets.length ? targets[0][0] : "";
      let destNote = "";
      if (String(v.channel || "").toLowerCase() === "video") {
        // the row this video was made for says where it goes (2026-10-16): YouTube row → YouTube, Reel/Short/TikTok → TikTok, else Facebook
        const rv = this.renderedVideos().find(x => String(x.v.id) === String(v.id));
        const row = rv && rv.rowKey ? plan.find(r => r.key === rv.rowKey) : null;
        const lbl = String(row && (row.label || row.channel) || "").toLowerCase();
        const want = row && row.goesTo ? [row.goesTo] : /youtube/.test(lbl) ? ["youtube"] : /reel|short|tiktok|9:16/.test(lbl) || rv && rv.vertical ? ["tiktok", "instagram"] : ["facebook", "instagram"];
        const hit = want.map(w => open.find(([k]) => k === w)).find(Boolean);
        if (hit) firstTarget = hit[0];
        // made for a platform that isn't connected (or needs reconnecting): say so instead of quietly picking another
        if (row && row.goesTo && !targets.some(([k]) => k === row.goesTo)) destNote = this.destProblem(row.goesTo);
      }
      const planned = plan.find(r => r.kind === "draft" && r.channel === String(v.channel || "").toLowerCase());
      // An email automation draft is created once; a second publish would duplicate it — flag and untick.
      const hasEmailDraft = String(v.channel || "").toLowerCase() === "email" && (d && d.publications || []).some(p => String(p.variantId) === String(v.id) && String(p.target || "") === "plunk_campaign");
      const draftPub = hasEmailDraft ? (d && d.publications || []).find(p => String(p.variantId) === String(v.id) && String(p.target || "") === "plunk_campaign") : null;
      const meta = String(v.channel || "").toLowerCase() === "email" ? this.emailMetaFor(v) || {} : {};
      const subjectB = (meta.subjects || []).find(x => x && x !== v.title) || "";
      return {
        key: String(v.id),
        v,
        d,
        targets,
        target: firstTarget,
        on: open.length > 0 && !hasEmailDraft,
        again: false,
        note: hasEmailDraft ? "automation draft already created: pick an audience to send it, or find it under Email › Automations" : destNote || (meta.segment ? `version for ${meta.segment}` : ""),
        automationId: draftPub ? String(draftPub.externalId || draftPub.external_id || "") : "",
        audienceId: meta.audienceId ? String(meta.audienceId) : "",
        subjectB,
        ab: false,
        when: planned && planned.when && planned.when > dayOffset(0) ? planned.when : ""
      };
    }).filter(r => {
      // Approved pieces always list (a missing target explains itself). Published ones (incl. imported ad / website
      // attributions, which never ship from here) only when a real target remains, or an owned one can be updated.
      if (String(r.v.status || "").toLowerCase() !== "published") return true;
      if (!r.targets.length) return false;
      return r.targets.some(([k]) => !this.livePub(r.v, r.d, k)) || r.targets.some(([k]) => isShopifyTarget(k) || k === "wordpress");
    });
  }
  shippedCount() {
    return this.shippedGroups().reduce((n, g) => n + g.items.length, 0);
  }
  /** Placements this Builder (or the piece page) shipped, grouped by piece, newest first. Imported attributions stay on the piece page. */
  shippedGroups() {
    const groups = new Map();
    for (const m of this.state.members) {
      const d = this.state.details[String(m.id)];
      if (!d) continue;
      const vars = {};
      (d.variants || []).forEach(v => {
        vars[String(v.id)] = {
          ...v,
          containerId: v.containerId || m.id,
          piece: m
        };
      });
      (d.publications || []).forEach(pub => {
        const v = vars[String(pub.variantId)];
        if (!v || !shippedByPlunk(pub.target)) return;
        const g = groups.get(String(v.id)) || {
          v,
          items: []
        };
        g.items.push({
          pub,
          v,
          d
        });
        groups.set(String(v.id), g);
      });
    }
    const when = p => String(p.publishedDate || p.createdDate || "");
    const out = [...groups.values()];
    out.forEach(g => g.items.sort((a, b) => when(b.pub).localeCompare(when(a.pub))));
    out.sort((a, b) => when(b.items[0].pub).localeCompare(when(a.items[0].pub)));
    return out;
  }
  // ---- Publish + Results helpers (redesign 2026-10-09, calflow Publish.dc.html / Results.dc.html): plain names, pictures, dates.
  /** What a piece is, in plain words ("Instagram post", "Web page"). */
  kindName(ch) {
    const c = String(ch || "").toLowerCase();
    const K = {
      landing: "Web page",
      web: "Web page",
      website: "Web page",
      blog: "Blog post",
      email: "Email",
      product_copy: "Product page",
      social_instagram: "Instagram post",
      social_facebook: "Facebook post",
      social_pinterest: "Pinterest pin",
      social_linkedin: "LinkedIn post",
      gbp_post: "Google Business post",
      video: "Video"
    };
    return K[c] || label(c);
  }
  /** The connected account's name for a platform ("@yourstore", "Your Page"), when the connection carries one. */
  socialAccountName(platform) {
    const a = (this.state.social || []).find(x => String(x.platform || "").toLowerCase() === platform && String(x.status || "").toLowerCase() === "connected");
    if (!a) return "";
    const h = a.handle || a.username || a.userName;
    if (h) return String(h).startsWith("@") ? String(h) : `@${h}`;
    return String(a.accountName || a.account_name || a.pageName || a.displayName || a.name || "");
  }
  /** A target (or a publication's target) in plain words: "Instagram · @handle", "Your email list", "Your website · /spring". */
  whereName(target, fallback, v) {
    const t = String(target || "").toLowerCase();
    const seo = v && (t === "shopify_page" || t === "shopify_article") ? this.seoFor(v) : null;
    const slug = seo && seo.slug ? `/${String(seo.slug).replace(/^\/+/, "")}` : "";
    const video = v && String(v.channel || "").toLowerCase() === "video";
    const net = {
      facebook: ["Facebook", "facebook"],
      social_facebook: ["Facebook", "facebook"],
      instagram: ["Instagram", "instagram"],
      social_instagram: ["Instagram", "instagram"],
      pinterest: ["Pinterest", "pinterest"],
      social_pinterest: ["Pinterest", "pinterest"],
      linkedin: ["LinkedIn", "linkedin"],
      social_linkedin: ["LinkedIn", "linkedin"],
      gbp: ["Google Business", "google_business"],
      gbp_post: ["Google Business", "google_business"],
      google_business: ["Google Business", "google_business"],
      youtube: ["YouTube", "youtube"],
      tiktok: ["TikTok", "tiktok"]
    }[t];
    if (net) {
      const acct = this.socialAccountName(net[1]);
      return `${net[0]}${t === "instagram" && video ? " reel" : video ? " video" : ""}${acct ? ` · ${acct}` : ""}`;
    }
    if (t === "email_draft" || t === "plunk_campaign" || t === "email") return "Your email list";
    if (t === "plunk_journey") return "Your email list · journey";
    if (t === "shopify_page") return `Your website · ${slug || "new page"}`;
    if (t === "shopify_article") return `Your website · blog${slug ? ` ${slug}` : ""}`;
    if (t === "shopify_product") return "Your website · product description";
    if (t === "shopify_media") return "Your website · product video";
    if (t === "shopify_translate" || t === "shopify_translation") return String(fallback || "Your website · translation").replace(/^Shopify/, "Your website");
    if (t === "wordpress") return "Your WordPress site · as a draft";
    if (t === "manual") return "Somewhere else · you post it yourself";
    if (t === "custom_api") return "Your own system";
    if (t === "mailchimp") return "Mailchimp";
    if (t === "constant_contact") return "Constant Contact";
    return fallback || targetName(t);
  }
  /** The piece's picture for a row: the attached social image, else a poster of its video, else the story's first image. */
  thumbFor(v, d) {
    let pick = {};
    try {
      pick = this.socialMediaFor(v, d) || {};
    } catch (e) {
      pick = {};
    }
    return pick.imageUrl || (pick.videoUrl ? this.posterFor(pick.videoUrl) : null) || null;
  }
  renderThumb(v, d, cls) {
    const src = this.thumbFor(v, d);
    if (src) return /*#__PURE__*/React.createElement("img", {
      className: cls,
      src: src,
      alt: "",
      loading: "lazy"
    });
    const k = this.kindName(v.channel);
    return /*#__PURE__*/React.createElement("span", {
      className: cls + " none",
      "aria-hidden": "true"
    }, k.charAt(0));
  }
  /** "2026-10-15" → "Thu, Oct 15" (local day, no time zone shift). */
  fmtDay(ymd, short) {
    const m = String(ymd || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return "";
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return d.toLocaleDateString(undefined, short ? {
      month: "short",
      day: "numeric"
    } : {
      weekday: "short",
      month: "short",
      day: "numeric"
    });
  }
  /** The brand check as one small chip: passes, the first issue in plain words, or nothing while unchecked. */
  safetyChip(r) {
    const s = this.state.safety[r.key];
    if (!s) return null;
    if (s.error) return /*#__PURE__*/React.createElement("span", {
      className: "bp-chip mute",
      title: `The brand check did not finish (${s.error}). It runs again when you publish.`
    }, "Brand check runs again when you publish");
    const issues = s.issues || [];
    if (!issues.length) return /*#__PURE__*/React.createElement("span", {
      className: "bp-chip ok",
      title: s.summary || undefined
    }, "\u2713 Passes your brand rules");
    const first = issues[0];
    const critical = this.isCritical(s);
    const all = issues.map(i => `${i.text}${i.fix ? `: ${i.fix}` : ""}`).join("\n");
    return /*#__PURE__*/React.createElement("span", {
      className: "bp-chip " + (critical ? "bad" : "warn"),
      title: all
    }, "! ", clip(first.text, 70), issues.length > 1 ? ` (+${issues.length - 1} more)` : "");
  }

  // Ads run as a campaign (flight), not a post (2026-10-16): the same hand-off Make's ad piece uses.
  /** The Flight Builder hand-off for an ad piece (Make "Send to a flight", Publish). Carries the flight name (the
   *  story title), the landing page (the story's web page, else its product page) and the picture (the piece's own,
   *  else the story's first) as well as the copy (DS 2026-10-21: title and image were missing). */
  flightPrefill(v, d) {
    let pick = {};
    try {
      pick = this.socialMediaFor(v, d) || {};
    } catch (e) {
      pick = {};
    }
    const lines = String(v.body || "").split(/\n+/).map(x => x.trim()).filter(Boolean);
    const rest = strip(lines.slice(v.title ? 0 : 1).join(" "));
    const web = this.allVariants().find(x => ["landing", "web", "website"].includes(String(x.channel || "").toLowerCase()) && x.piece && x.piece.url);
    const facts = this.allBlocks().filter(b => b.kind === "product_facts").map(b => factMap(plainText(b.body)).page).find(u => /^https?:\/\//i.test(u || ""));
    const container = d && d.container && d.container.url || this.state.primary && this.state.primary.url || "";
    // last resort: the first web address the story itself mentions ("Try it free at plunk.io/studio")
    const mentioned = (this.storyText(" ").match(/\b(?:https?:\/\/)?(?:[a-z0-9-]+\.)+[a-z]{2,}(?:\/[^\s"'<>)]*)?/i) || [])[0];
    const landingPage = web && web.piece.url || facts || (/^https?:\/\//i.test(container) ? container : "") || (mentioned ? (/^https?:\/\//i.test(mentioned) ? mentioned : `https://${mentioned}`).replace(/[.,;:!?]+$/, "") : "");
    const imageUrl = pick.videoUrl ? null : pick.imageUrl || this.storyImages().find(u => !/\.(mp4|mov|webm)(\?|$)/i.test(u)) || null;
    return {
      from: "content",
      variantId: String(v.id),
      storyId: String(this.state.storyId || ""),
      eventName: this.storyTitle(),
      landingPage,
      headline: (v.title || lines[0] || "").slice(0, 90),
      description: rest.slice(0, 180),
      body: rest.slice(0, 1500),
      imageUrl,
      videoUrl: pick.videoUrl || null
    };
  }
  renderShip() {
    const ship = this.state.ship || this.buildShip();
    if (!this.state.ship) this.setState({
      ship
    });
    const setRow = (key, patch) => this.setState({
      ship: ship.map(r => r.key === key ? {
        ...r,
        ...patch
      } : r)
    });
    const ready = ship.filter(r => r.targets.length);
    const adRows = ship.filter(r => !r.targets.length && String(r.v.channel || "").toLowerCase() === "ad");
    const noTarget = ship.filter(r => !r.targets.length && String(r.v.channel || "").toLowerCase() !== "ad");
    const picked = ship.filter(r => r.on && r.target);
    const groups = this.shippedGroups();
    const liveCount = groups.reduce((n, g) => n + g.items.filter(it => pubIsLive(it.pub)).length, 0);
    const view = this.state.shipView || "ready";
    const seg = /*#__PURE__*/React.createElement("div", {
      className: "bp-seg",
      role: "group",
      "aria-label": "Show"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: view === "ready" ? "on" : "",
      "aria-pressed": view === "ready",
      onClick: () => this.setState({
        shipView: "ready"
      })
    }, "Ready \xB7 ", ready.length), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: view === "shipped" ? "on" : "",
      "aria-pressed": view === "shipped",
      onClick: () => this.setState({
        shipView: "shipped"
      })
    }, "Live \xB7 ", liveCount));
    if (view === "shipped") return this.renderLive(groups, seg);
    // "Now" vs a date: translations always write now (the calendar has no translation path); an email send with a date goes at the send hour.
    const dated = picked.filter(r => r.when && r.target !== "shopify_translate");
    const nowN = picked.length - dated.length;
    const days = [...new Set(dated.map(r => r.when))].sort();
    const schedLine = !dated.length ? "" : days.length === 1 ? `${dated.length} ${dated.length === 1 ? "is" : "are"} scheduled for ${this.fmtDay(days[0], true)}` : `${dated.length} are scheduled between ${this.fmtDay(days[0], true)} and ${this.fmtDay(days[days.length - 1], true)}`;
    const notReady = this.allVariants().filter(v => String(v.status || "").toLowerCase() === "draft");
    const res = this.state.shipResult;
    return /*#__PURE__*/React.createElement("div", {
      className: "bp-view"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bp-head"
    }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("h2", null, "Ready to publish"), /*#__PURE__*/React.createElement("p", null, "Pick where each piece goes and when. \"Now\" publishes it straight away; a date puts it on the calendar.")), seg), !ship.length && /*#__PURE__*/React.createElement("div", {
      className: "bp-empty"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "send"), "Nothing approved yet. Approve pieces in Make and they show up here."), ready.length > 0 && /*#__PURE__*/React.createElement("div", {
      className: "bp-list"
    }, ready.map(r => this.renderShipRow(r, setRow)), /*#__PURE__*/React.createElement("div", {
      className: "bp-list-foot"
    }, /*#__PURE__*/React.createElement("span", null, picked.length ? `${picked.length} picked: ${[nowN ? `${nowN} go${nowN === 1 ? "es" : ""} now` : "", schedLine].filter(Boolean).join(", ")}.` : "Tick the pieces you want to publish."), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "bx-link",
      disabled: !!this.state.busy.safety || !picked.length,
      onClick: this.checkSelected
    }, this.state.busy.safety ? "Checking against your brand rules…" : "Check them all against your brand rules again"))), notReady.length > 0 && /*#__PURE__*/React.createElement("div", {
      className: "bp-dashed"
    }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("b", null, plural(notReady.length, "piece"), " still need", notReady.length === 1 ? "s" : "", " approval"), " ", /*#__PURE__*/React.createElement("i", null, "\xB7 ", notReady.slice(0, 2).map(v => v.title || this.kindName(v.channel)).join(", "), notReady.length > 2 ? ` and ${notReady.length - 2} more` : "")), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "bx-link",
      onClick: () => this.go("make")
    }, "Approve them in Make")), adRows.map(r => /*#__PURE__*/React.createElement("div", {
      className: "bp-dashed",
      key: `ad:${r.key}`
    }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("b", null, r.v.title || "Ad"), " ", /*#__PURE__*/React.createElement("i", null, "\xB7 an ad runs as a campaign with a budget, not as a post. Your Meta and Google connections are used there.")), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "bx-link",
      onClick: () => this.adToFlight(r.v)
    }, "Build the campaign"))), noTarget.length > 0 && /*#__PURE__*/React.createElement("div", {
      className: "bp-dashed"
    }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("b", null, plural(noTarget.length, "piece"), " ", noTarget.length === 1 ? "has" : "have", " nowhere to go yet"), " ", /*#__PURE__*/React.createElement("i", null, "\xB7 ", noTarget.slice(0, 2).map(r => `${this.kindName(r.v.channel)}${r.v.title ? `, ${r.v.title}` : ""}`).join("; "), noTarget.length > 2 ? ` and ${noTarget.length - 2} more` : ""), noTarget.map(r => this.socialProblem(r.v.channel)).filter(Boolean).slice(0, 1).map((p, i) => /*#__PURE__*/React.createElement("span", {
      key: i,
      className: "bp-dashed-note"
    }, p))), /*#__PURE__*/React.createElement("a", {
      className: "bx-link",
      href: "/Data/Connect"
    }, "Connect an account")), res && /*#__PURE__*/React.createElement("div", {
      className: "bp-result" + (res.failed.length ? " bad" : "")
    }, /*#__PURE__*/React.createElement("b", null, [res.ok ? `${res.ok} published` : "", res.queued ? `${res.queued} on the way` : "", res.scheduled ? `${res.scheduled} scheduled` : "", res.failed.length ? `${res.failed.length} did not go` : ""].filter(Boolean).join(" · ") || "Nothing went out"), res.queued > 0 && /*#__PURE__*/React.createElement("p", null, "Social posts go out within a minute and then show under Live. If an account has a problem, Manage Data \u203A Connect says why."), res.failed.map((f, i) => /*#__PURE__*/React.createElement("p", {
      key: i,
      className: "bad"
    }, f)), res.failed.some(f => /content permission|write_content/i.test(f)) && /*#__PURE__*/React.createElement("p", null, "Shopify needs the content permission for blog posts and pages: open Plunk from your Shopify admin (Apps \u203A Plunk Commerce) and approve it, then publish again.")), /*#__PURE__*/React.createElement("div", {
      className: "bp-foot"
    }, /*#__PURE__*/React.createElement("span", null, liveCount ? `${plural(liveCount, "piece")} already live.` : ""), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "bx-link quiet",
      onClick: () => this.go("measure")
    }, "See results")));
  }
  renderShipRow(r, setRow) {
    const live = r.target !== "email_draft" ? this.livePub(r.v, r.d, r.target) : null;
    const owned = isShopifyTarget(r.target) || r.target === "wordpress";
    const title = r.v.title || r.v.piece.title || this.kindName(r.v.channel);
    const whenVal = r.when ? "date" : r.pickDate ? "pick" : "now";
    const noDate = r.target === "shopify_translate";
    const today = new Date();
    const minDay = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    return /*#__PURE__*/React.createElement("div", {
      key: r.key,
      className: "bp-row" + (r.on ? "" : " off")
    }, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      className: "bp-cb",
      checked: r.on,
      "aria-label": `Include ${title}`,
      disabled: !r.targets.length || !!live && !r.again,
      title: live && !r.again ? "Already live there: tick Publish again to send it once more" : undefined,
      onChange: () => setRow(r.key, {
        on: !r.on
      })
    }), this.renderThumb(r.v, r.d, "bp-thumb"), /*#__PURE__*/React.createElement("div", {
      className: "bp-piece"
    }, /*#__PURE__*/React.createElement("b", {
      title: title
    }, title), /*#__PURE__*/React.createElement("span", {
      className: "bp-sub"
    }, this.kindName(r.v.channel), " \xB7 ", /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "bx-link",
      title: "See it as it will look",
      onClick: () => this.setState({
        shipPreview: r
      })
    }, "Preview")), this.safetyChip(r), this.isCritical(this.state.safety[r.key]) && /*#__PURE__*/React.createElement("label", {
      className: "bp-mini"
    }, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      checked: !!r.override,
      onChange: () => setRow(r.key, {
        override: !r.override
      })
    }), " Publish anyway"), r.note && /*#__PURE__*/React.createElement("span", {
      className: "bp-note"
    }, /^automation draft/.test(r.note) ? "Email draft already made. Pick who gets it to send it, or find it under Email." : r.note), live && /*#__PURE__*/React.createElement("span", {
      className: "bp-note"
    }, "Live since ", this.fmtDay(String(live.publishedDate || live.createdDate || ""), true) || "earlier", live.url ? /*#__PURE__*/React.createElement(React.Fragment, null, " \xB7 ", /*#__PURE__*/React.createElement("a", {
      href: live.url,
      target: "_blank",
      rel: "noreferrer"
    }, "Open \u2197")) : null), live && /*#__PURE__*/React.createElement("label", {
      className: "bp-mini",
      title: owned ? "The same page or post gets the current copy" : "A new post goes out; the earlier one stays live"
    }, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      checked: !!r.again,
      onChange: () => setRow(r.key, {
        again: !r.again,
        on: !r.again
      })
    }), " Publish again", owned ? " (updates the same page)" : " (posts a second time)")), /*#__PURE__*/React.createElement("label", {
      className: "bp-field"
    }, "Where", /*#__PURE__*/React.createElement("select", {
      className: "bp-select",
      value: r.target,
      onChange: e => {
        const t = e.target.value;
        setRow(r.key, {
          target: t,
          again: false,
          on: t !== "email_draft" && !!this.livePub(r.v, r.d, t) ? false : r.on
        });
      }
    }, r.targets.map(([k, l]) => /*#__PURE__*/React.createElement("option", {
      key: k,
      value: k
    }, this.whereName(k, l, r.v)))), r.target === "email_draft" && /*#__PURE__*/React.createElement("select", {
      className: "bp-select",
      value: r.audienceId,
      title: "Pick who gets it, or keep it as a draft to send later",
      onChange: e => setRow(r.key, {
        audienceId: e.target.value,
        on: e.target.value ? true : r.on
      })
    }, /*#__PURE__*/React.createElement("option", {
      value: ""
    }, "Keep as a draft (send it later from Email)"), (this.state.audiences || []).map(a => /*#__PURE__*/React.createElement("option", {
      key: String(a.id),
      value: String(a.id)
    }, "Send to ", a.name, a.total != null ? ` · ${num(a.total)}` : ""))), r.target === "email_draft" && r.audienceId && r.subjectB && /*#__PURE__*/React.createElement("span", {
      className: "bp-mini",
      title: "Half get each subject line; compare opens under Email"
    }, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      checked: !!r.ab,
      onChange: () => setRow(r.key, {
        ab: !r.ab
      })
    }), " Try a second subject: ", /*#__PURE__*/React.createElement("i", null, r.subjectB)), r.target === "manual" && /*#__PURE__*/React.createElement("input", {
      className: "bp-select mono",
      value: r.manualUrl || "",
      placeholder: "Link once it is up (optional)",
      title: "Copy the page from Make, put it on your site, and paste its link so Results can count its visits",
      onChange: e => setRow(r.key, {
        manualUrl: e.target.value
      })
    })), /*#__PURE__*/React.createElement("label", {
      className: "bp-field"
    }, "When", /*#__PURE__*/React.createElement("select", {
      className: "bp-select",
      value: noDate ? "now" : whenVal,
      disabled: noDate,
      title: noDate ? "Translations are written straight away" : undefined,
      onChange: e => {
        const x = e.target.value;
        if (x === "now") setRow(r.key, {
          when: "",
          pickDate: false
        });else if (x === "pick") setRow(r.key, {
          pickDate: true
        });
      }
    }, /*#__PURE__*/React.createElement("option", {
      value: "now"
    }, "Now"), r.when && /*#__PURE__*/React.createElement("option", {
      value: "date"
    }, this.fmtDay(r.when)), /*#__PURE__*/React.createElement("option", {
      value: "pick"
    }, r.when ? "Pick another date…" : "Pick a date…")), !noDate && (r.pickDate || whenVal === "pick") && /*#__PURE__*/React.createElement("input", {
      type: "date",
      className: "bp-select",
      value: r.when,
      min: minDay,
      "aria-label": "Date",
      onChange: e => setRow(r.key, {
        when: e.target.value
      })
    }), r.target === "email_draft" && r.audienceId && r.when && this.state.sendTime && /*#__PURE__*/React.createElement("span", {
      className: "bp-note"
    }, "Sends at ", this.state.sendTime.suggested, " UTC", this.state.sendTime.basis === "opens" ? ", when your contacts open most" : "")));
  }

  /** Publish › Live: every live placement, newest first, with Open and Publish again; updating or taking one down sits under Manage. */
  renderLive(groups, seg) {
    const rows = groups.flatMap(g => g.items.filter(it => pubIsLive(it.pub)));
    const again = (pub, v) => {
      const has = (this.state.ship || this.buildShip()).some(r => r.key === String(v.id));
      this.shipAgain(pub, v);
      if (has) this.setState({
        shipView: "ready"
      });
    };
    const isEmail = t => /email|campaign|journey|mailchimp|constant/.test(String(t || "").toLowerCase());
    return /*#__PURE__*/React.createElement("div", {
      className: "bp-view"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bp-head"
    }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("h2", null, "Live now"), /*#__PURE__*/React.createElement("p", null, "Everything this story has published, newest first. Each piece goes to a place once; Publish again sends it on purpose.")), seg), !rows.length && /*#__PURE__*/React.createElement("div", {
      className: "bp-empty"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "public_off"), "Nothing from this story is live yet."), rows.length > 0 && /*#__PURE__*/React.createElement("div", {
      className: "bp-list"
    }, rows.map(({
      pub,
      v,
      d
    }) => {
      const t = String(pub.target || "").toLowerCase();
      const st = String(pub.status || "").toLowerCase();
      const day = this.fmtDay(String(pub.publishedDate || pub.createdDate || ""), true);
      return /*#__PURE__*/React.createElement("div", {
        key: String(pub.id),
        className: "bp-row live"
      }, this.renderThumb(v, d, "bp-thumb"), /*#__PURE__*/React.createElement("div", {
        className: "bp-piece"
      }, /*#__PURE__*/React.createElement("b", null, v.title || v.piece.title || this.kindName(v.channel)), /*#__PURE__*/React.createElement("span", {
        className: "bp-sub"
      }, this.kindName(v.channel))), /*#__PURE__*/React.createElement("div", {
        className: "bp-piece"
      }, /*#__PURE__*/React.createElement("span", {
        className: "bp-where"
      }, this.whereName(t, targetName(t), v)), /*#__PURE__*/React.createElement("span", {
        className: "bp-sub"
      }, st === "queued" || st === "draft" ? "On its way" : st === "removing" ? "Being taken down" : st === "private" ? "Private on YouTube for now" : day ? `Since ${day}` : "Live")), /*#__PURE__*/React.createElement("div", {
        className: "bp-acts"
      }, pub.url && /*#__PURE__*/React.createElement("a", {
        className: "bx-link",
        href: pub.url,
        target: "_blank",
        rel: "noreferrer"
      }, "Open \u2197"), !isEmail(t) && /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "bp-quiet-btn",
        onClick: () => again(pub, v)
      }, "Publish again")));
    })), groups.length > 0 && /*#__PURE__*/React.createElement("details", {
      className: "bp-more"
    }, /*#__PURE__*/React.createElement("summary", null, "Update or take something down"), groups.map(g => /*#__PURE__*/React.createElement("div", {
      key: String(g.v.id),
      className: "hub-shp-group"
    }, /*#__PURE__*/React.createElement("div", {
      className: "hub-shp-ghead"
    }, /*#__PURE__*/React.createElement("b", null, g.v.title || g.v.piece.title), /*#__PURE__*/React.createElement("span", {
      className: "territory-hint",
      style: {
        margin: 0
      }
    }, "\xB7 ", this.kindName(g.v.channel))), /*#__PURE__*/React.createElement(ShippedList, {
      items: g.items,
      compact: true,
      limit: 4,
      shop: this.state.shopify && this.state.shopify.shop || "",
      onChanged: () => this.reloadDetails(),
      onAgain: this.shipAgain
    })))));
  }
  renderSafety(r, setRow) {
    const s = this.state.safety[r.key];
    if (!s) return null;
    if (s.error) return /*#__PURE__*/React.createElement("div", {
      className: "bp-note"
    }, "The brand check did not finish (", s.error, "). It runs again when you publish.");
    const issues = s.issues || [];
    if (!issues.length) return /*#__PURE__*/React.createElement("div", {
      className: "bp-note ok"
    }, "\u2713 Passes your brand rules", s.summary ? ` · ${s.summary}` : "");
    const critical = this.isCritical(s);
    return /*#__PURE__*/React.createElement("div", {
      className: "bp-note"
    }, issues.map((i, k) => /*#__PURE__*/React.createElement("div", {
      key: k
    }, "! ", i.text, i.fix ? `: ${i.fix}` : "")), critical && /*#__PURE__*/React.createElement("label", {
      className: "bp-mini"
    }, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      checked: !!r.override,
      onChange: () => setRow(r.key, {
        override: !r.override
      })
    }), " Publish anyway"));
  }

  // ---------------------------------------------------------------- Measure
  // Measure: per-channel comparison across the story's pieces (funnel rows aggregated by channel) + what to make next.
  /** Variant ids this story shipped from Plunk (Builder or piece page). Imported pages, ads and social imports are not measured here. */
  shippedVariantIds() {
    return new Set(this.shippedGroups().map(g => String(g.v.id)));
  }
  channelRows() {
    const by = {};
    const shipped = this.shippedVariantIds();
    this.state.members.forEach(m => {
      const f = (this.state.funnels || {})[String(m.id)];
      (f && f.variants || []).filter(v => shipped.has(String(v.variantId))).forEach(v => {
        const ch = String(v.channel || "other").toLowerCase();
        const r = by[ch] || (by[ch] = {
          channel: ch,
          pieces: new Set(),
          impressions: 0,
          reach: 0,
          clicks: 0,
          sessions: 0,
          engagements: 0,
          conversions: 0,
          conversionValue: 0,
          orders: 0,
          salesValue: 0
        });
        r.pieces.add(String(m.id));
        ["impressions", "reach", "clicks", "sessions", "engagements", "conversions", "conversionValue", "orders", "salesValue"].forEach(k => {
          r[k] += Number(v[k]) || 0;
        });
      });
    });
    return Object.values(by).map(r => ({
      ...r,
      pieces: r.pieces.size,
      cvr: r.sessions > 0 ? r.conversions / r.sessions : 0
    })).sort((a, b) => b.sessions - a.sessions || b.impressions - a.impressions);
  }
  nextMoves(rows) {
    const moves = [];
    const total = rows.reduce((a, r) => a + r.sessions, 0);
    const best = rows.find(r => r.sessions > 0);
    if (best && total > 0) moves.push({
      kind: "double_down",
      text: `${this.kindName(best.channel)}s bring ${Math.round(best.sessions / total * 100)}% of this story's visits. Plan another one.`,
      action: ["Plan another piece", () => this.go("brief")]
    });
    rows.filter(r => r.sessions >= 5 && r.conversions === 0).slice(0, 2).forEach(r => moves.push({
      kind: "no_conv",
      text: `The ${this.kindName(r.channel).toLowerCase()} brings visits but no orders yet. Add a product link and a clear next step.`,
      action: ["Open it in Make", () => this.go("make")]
    }));
    const planned = (this.state.plan || []).filter(r => r.on && r.kind === "draft");
    const have = new Set(this.allVariants().map(v => String(v.channel || "").toLowerCase()));
    const missing = planned.filter(r => !have.has(r.channel));
    if (missing.length) moves.push({
      kind: "gap",
      text: `Planned but not written yet: ${missing.map(r => this.kindName(r.channel)).join(", ")}.`,
      action: ["Write them", () => this.go("make")]
    });
    const pubs = this.state.members.flatMap(m => (this.state.details[String(m.id)] || {}).publications || []);
    const livePubs = pubs.filter(p => String(p.status || "").toLowerCase() === "live" && (p.target || "") !== "website");
    if (livePubs.length && rows.every(r => r.sessions === 0 && r.impressions === 0)) moves.push({
      kind: "quiet",
      text: "Published, but no visits counted yet. Share the links, or put an ad behind the best piece.",
      action: null
    });
    const titles = this.state.members.map(m => String(m.title || "").toLowerCase()).filter(Boolean);
    (this.state.recs || []).filter(r => String(r.status || "open").toLowerCase() === "open" && (String(r.class || "") === "refresh" || titles.some(t => t && String(r.title || "").toLowerCase().includes(t)))).slice(0, 2).forEach(r => moves.push({
      kind: "rec",
      text: `Idea: ${r.title}`,
      action: ["See ideas", () => this.props.history.push("/Content")]
    }));
    if (!moves.length) moves.push({
      kind: "ok",
      text: "Nothing to change yet. Check back once the story has a few days of visits.",
      action: null
    });
    return moves.slice(0, 4);
  }
  pageStatFor(url) {
    const k = pathKey(url);
    return k && this.state.pageStats ? this.state.pageStats[k] || null : null;
  }
  renderMeasure() {
    if (this.state.pageStats === undefined) setTimeout(this.loadPageStats, 0);
    const rows = this.channelRows();
    const shippedGroups = this.shippedGroups();
    const funnelOf = v => {
      const f = (this.state.funnels || {})[String(v.piece ? v.piece.id : v.containerId)];
      return (f && f.variants || []).find(x => String(x.variantId) === String(v.id)) || {};
    };
    const PAGE_TARGETS = /^shopify_(page|article|product)$|^wordpress$|^custom_api$|^manual$/;
    // One row per piece: views = network impressions (a web page's own page views when it has none); visits = visits from the
    // piece's links (a web page's own visits when none were tagged); orders and sales from those visits.
    const pieces = shippedGroups.map(g => {
      const f = funnelOf(g.v);
      const seen = new Set();
      const live = g.items.filter(it => pubIsLive(it.pub)).filter(it => {
        const k = String(it.pub.target || "") + (it.pub.url || "");
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
      const page = (live.find(it => PAGE_TARGETS.test(String(it.pub.target || "").toLowerCase()) && it.pub.url) || {}).pub;
      const ps = page ? this.pageStatFor(page.url) : null;
      const views = Number(f.impressions) || (ps ? Number(ps.views) || 0 : 0);
      const visits = Number(f.sessions) || (ps ? Number(ps.sessions) || 0 : 0);
      const open = (live.find(it => it.pub.url) || {}).pub;
      return {
        g,
        f,
        live,
        views,
        visits,
        orders: Number(f.orders) || 0,
        conversions: Number(f.conversions) || 0,
        sales: Number(f.salesValue) || 0,
        open
      };
    });
    const sum = k => pieces.reduce((a, p) => a + (p[k] || 0), 0);
    const totVisits = sum("visits");
    const anyLive = pieces.some(p => p.live.length);
    // "+N% vs last month" only when the funnel carries an earlier period (prev<Key> / <key>Prev / previous.<key>); otherwise a plain line.
    const prevOf = k => {
      let found = false;
      let n = 0;
      const cap = k.charAt(0).toUpperCase() + k.slice(1);
      pieces.forEach(p => {
        const f = p.f || {};
        const x = f[`prev${cap}`] != null ? f[`prev${cap}`] : f[`${k}Prev`] != null ? f[`${k}Prev`] : f.previous && f.previous[k] != null ? f.previous[k] : null;
        if (x != null) {
          found = true;
          n += Number(x) || 0;
        }
      });
      return found ? n : null;
    };
    const delta = (cur, k) => {
      const prev = prevOf(k);
      if (prev == null || prev <= 0) return null;
      const pct = Math.round((cur - prev) / prev * 100);
      return `${pct >= 0 ? "+" : ""}${pct}% vs the period before`;
    };
    const totConv = sum("conversions");
    const totOrders = sum("orders");
    const tiles = [["Views", sum("views"), "impressions", "times people saw a piece", false], ["Site visits", totVisits, "sessions", "people who came from a piece", false], ["Orders", totOrders, "orders", totOrders ? "from people who came from a piece" : totConv ? `${num(totConv)} other ${totConv === 1 ? "action" : "actions"} like sign-ups` : "from people who came from a piece", false], ["Sales", sum("sales"), "salesValue", totOrders ? "from those orders" : "from people who came from a piece", true]];
    // Best piece: the most visits, or the most views when nothing has visits yet.
    const byVisits = pieces.filter(p => p.visits > 0).sort((a, b) => b.visits - a.visits);
    const byViews = pieces.filter(p => p.views > 0).sort((a, b) => b.views - a.views);
    const best = byVisits[0] || byViews[0] || null;
    const SIBLINGS = {
      social_instagram: ["social_facebook", "social_pinterest"],
      social_facebook: ["social_instagram", "social_linkedin"],
      social_pinterest: ["social_instagram", "social_facebook"],
      social_linkedin: ["social_facebook", "email"],
      gbp_post: ["social_facebook", "social_instagram"],
      email: ["social_instagram", "social_facebook"],
      landing: ["email", "social_instagram"],
      web: ["email", "social_instagram"],
      blog: ["email", "social_pinterest"],
      product_copy: ["email", "social_instagram"],
      video: ["social_instagram", "social_facebook"]
    };
    const bestCh = best ? String(best.g.v.channel || "").toLowerCase() : "";
    const planned = new Set((this.state.plan || []).filter(r => r.kind === "draft" && r.on).map(r => r.channel));
    const sibs = (SIBLINGS[bestCh] || ["email", "social_instagram"]).slice().sort((a, b) => (planned.has(a) ? 1 : 0) - (planned.has(b) ? 1 : 0));
    const sibName = c => /^social_|^gbp/.test(c) ? this.kindName(c) : this.kindName(c).toLowerCase();
    const bestWhere = best && best.live.length ? this.whereName(String(best.live[0].pub.target || "").toLowerCase(), "", best.g.v).split(" · ")[0].replace(/^Your /, "your ") : this.kindName(bestCh);
    const moves = this.nextMoves(rows).filter(m => !(best && m.kind === "double_down"));
    const doneTpls = (this.state.templates || []).filter(t => Number(t.pieces) > 0);
    const dash = /*#__PURE__*/React.createElement("span", {
      className: "bp-nil"
    }, "\u2013");
    return /*#__PURE__*/React.createElement("div", {
      className: "bp-view"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bp-head results"
    }, /*#__PURE__*/React.createElement("h2", null, "How the story is doing"), /*#__PURE__*/React.createElement("p", null, this.measureLabel(), " \xB7 from every piece in this story, wherever it went")), /*#__PURE__*/React.createElement("div", {
      className: "bp-tiles"
    }, tiles.map(([l, v, k, line, money]) => {
      const d = v > 0 ? delta(v, k) : null;
      return /*#__PURE__*/React.createElement("div", {
        key: l,
        className: "bp-tile"
      }, /*#__PURE__*/React.createElement("b", null, v > 0 ? money ? usd(v) : num(v) : anyLive ? money ? "$0" : "0" : "Not yet"), /*#__PURE__*/React.createElement("span", null, l), /*#__PURE__*/React.createElement("i", {
        className: d && !/^-/.test(d) ? "up" : ""
      }, d || (anyLive || v > 0 ? line : "Shows once a piece is live")));
    })), best ? /*#__PURE__*/React.createElement("div", {
      className: "bp-best"
    }, /*#__PURE__*/React.createElement("span", {
      className: "bp-best-tag"
    }, "Best piece"), /*#__PURE__*/React.createElement("span", {
      className: "bp-best-t"
    }, /*#__PURE__*/React.createElement("b", null, best.g.v.title || best.g.v.piece.title), " on ", bestWhere, " brought the most ", byVisits.length ? "visits" : "views", ". A similar ", sibs.slice(0, 2).map(sibName).join(" or "), " could do the same."), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "bp-btn",
      onClick: () => this.makeSibling(sibs[0])
    }, "Make a version for another channel")) : anyLive ? /*#__PURE__*/React.createElement("div", {
      className: "bp-dashed"
    }, /*#__PURE__*/React.createElement("span", null, "Numbers show up a day after the first visits from these pieces.")) : null, pieces.length ? /*#__PURE__*/React.createElement("div", {
      className: "bp-wide"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bp-table",
      role: "table",
      "aria-label": "Results by piece"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bp-tr bp-th",
      role: "row"
    }, /*#__PURE__*/React.createElement("span", {
      role: "columnheader",
      "aria-label": "Picture"
    }), /*#__PURE__*/React.createElement("span", {
      role: "columnheader"
    }, "Piece"), /*#__PURE__*/React.createElement("span", {
      role: "columnheader"
    }, "Share of visits"), /*#__PURE__*/React.createElement("span", {
      role: "columnheader",
      className: "n"
    }, "Views"), /*#__PURE__*/React.createElement("span", {
      role: "columnheader",
      className: "n"
    }, "Visits"), /*#__PURE__*/React.createElement("span", {
      role: "columnheader",
      className: "n"
    }, "Sales")), pieces.map(p => /*#__PURE__*/React.createElement("div", {
      key: String(p.g.v.id),
      className: "bp-tr" + (p.live.length ? "" : " off"),
      role: "row"
    }, /*#__PURE__*/React.createElement("span", {
      role: "cell"
    }, this.renderThumb(p.g.v, p.g.items[0] && p.g.items[0].d, "bp-thumb sm")), /*#__PURE__*/React.createElement("span", {
      role: "cell",
      className: "bp-piece"
    }, /*#__PURE__*/React.createElement("b", null, p.g.v.title || p.g.v.piece.title), /*#__PURE__*/React.createElement("span", {
      className: "bp-sub"
    }, p.live.length ? p.live.slice(0, 2).map(it => this.whereName(String(it.pub.target || "").toLowerCase(), targetName(it.pub.target), p.g.v).split(" · ")[0]).join(", ") : `${this.kindName(p.g.v.channel)} · taken down`, p.live.length > 2 ? ` +${p.live.length - 2}` : "", p.open ? /*#__PURE__*/React.createElement(React.Fragment, null, " \xB7 ", /*#__PURE__*/React.createElement("a", {
      href: p.open.url,
      target: "_blank",
      rel: "noreferrer",
      title: p.open.url
    }, "Open \u2197")) : null)), /*#__PURE__*/React.createElement("span", {
      role: "cell",
      className: "bp-bar",
      "aria-label": `${totVisits ? Math.round(p.visits / totVisits * 100) : 0}% of visits`
    }, /*#__PURE__*/React.createElement("span", {
      style: {
        width: `${totVisits ? Math.round(p.visits / totVisits * 100) : 0}%`
      }
    })), /*#__PURE__*/React.createElement("span", {
      role: "cell",
      className: "n"
    }, p.views ? num(p.views) : dash), /*#__PURE__*/React.createElement("span", {
      role: "cell",
      className: "n"
    }, p.visits ? num(p.visits) : dash), /*#__PURE__*/React.createElement("span", {
      role: "cell",
      className: "n" + (p.sales ? " good" : "")
    }, p.sales ? usd(p.sales) : dash))))) : /*#__PURE__*/React.createElement("div", {
      className: "bp-empty"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "insights"), "Nothing published from this story yet. Results show once a piece is live.", /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "bx-link",
      onClick: () => this.go("ship")
    }, "Go to Publish")), doneTpls.length > 0 && /*#__PURE__*/React.createElement("details", {
      className: "bp-more"
    }, /*#__PURE__*/React.createElement("summary", null, "By layout"), /*#__PURE__*/React.createElement("div", {
      className: "bp-wide"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bp-table layouts",
      role: "table",
      "aria-label": "Results by layout"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bp-tr bp-th",
      role: "row"
    }, /*#__PURE__*/React.createElement("span", {
      role: "columnheader"
    }, "Layout"), /*#__PURE__*/React.createElement("span", {
      role: "columnheader"
    }, "Kind"), /*#__PURE__*/React.createElement("span", {
      role: "columnheader",
      className: "n"
    }, "Pieces"), /*#__PURE__*/React.createElement("span", {
      role: "columnheader",
      className: "n"
    }, "Visits, 30 days"), /*#__PURE__*/React.createElement("span", {
      role: "columnheader",
      className: "n"
    }, "Orders, 30 days"), /*#__PURE__*/React.createElement("span", {
      role: "columnheader",
      className: "n"
    }, "Sales, 90 days")), doneTpls.slice().sort((a, b) => Number(b.sessions30) - Number(a.sessions30)).map(t => /*#__PURE__*/React.createElement("div", {
      key: String(t.id),
      className: "bp-tr",
      role: "row"
    }, /*#__PURE__*/React.createElement("span", {
      role: "cell"
    }, /*#__PURE__*/React.createElement("b", null, t.name)), /*#__PURE__*/React.createElement("span", {
      role: "cell"
    }, this.kindName(t.channel)), /*#__PURE__*/React.createElement("span", {
      role: "cell",
      className: "n"
    }, num(t.pieces)), /*#__PURE__*/React.createElement("span", {
      role: "cell",
      className: "n"
    }, Number(t.sessions30) > 0 ? num(t.sessions30) : dash), /*#__PURE__*/React.createElement("span", {
      role: "cell",
      className: "n"
    }, Number(t.conversions30) > 0 ? num(t.conversions30) : dash), /*#__PURE__*/React.createElement("span", {
      role: "cell",
      className: "n"
    }, Number(t.sales90) > 0 ? usd(t.sales90) : dash)))))), moves.length > 0 && /*#__PURE__*/React.createElement("div", {
      className: "bp-next"
    }, /*#__PURE__*/React.createElement("h3", null, "What to make next"), /*#__PURE__*/React.createElement("ul", null, moves.map((m, i) => /*#__PURE__*/React.createElement("li", {
      key: i
    }, /*#__PURE__*/React.createElement("span", null, m.text), m.action && /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "bx-link",
      onClick: m.action[1]
    }, m.action[0]))))), /*#__PURE__*/React.createElement("div", {
      className: "bp-foot"
    }, /*#__PURE__*/React.createElement("span", null, "Visits and sales are counted when someone arrives from a piece's link. A day can pass before new numbers show."), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "bx-link quiet",
      onClick: () => this.props.history.push("/Content")
    }, "Start the next story")));
  }

  // ---------------------------------------------------------------- shell
  /** Brief rail: what the current selection would plan, and the brand the writers follow. */
  renderBriefRail() {
    const b = this.state.bs.brief;
    const brand = this.state.brand || {};
    const plan = this.buildPlan(b);
    const drafts = plan.filter(r => r.kind === "draft");
    const vids = plan.filter(r => r.kind === "media");
    const social = drafts.filter(r => SOCIAL_CHANNELS.has(r.channel));
    const name = ch => BRIEF_LABEL[ch] || label(ch);
    const rows = drafts.filter(r => !SOCIAL_CHANNELS.has(r.channel)).map(r => [name(r.channel), 1]);
    if (social.length) {
      const names = social.map(r => name(r.channel));
      rows.push([social.length === 1 ? `${names[0]} post` : social.length <= 3 ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]} posts` : "Social posts", social.length]);
    }
    if (vids.length) rows.push([vids.length === 1 ? "Short video" : "Short videos", vids.length]);
    const kept = drafts.filter(r => r.status !== "to write").length;
    const curPlan = this.state.plan ? this.state.plan.filter(r => r.on).length : 0;
    const avoid = String(brand.bannedWords || "").split(/[,\n]/).map(x => x.trim()).filter(Boolean).slice(0, 4);
    const links = this.productLinks();
    return /*#__PURE__*/React.createElement("aside", {
      className: "bx-rail",
      "aria-label": "Summary"
    }, /*#__PURE__*/React.createElement("section", null, /*#__PURE__*/React.createElement("h3", null, "What Plunk will make"), rows.length ? /*#__PURE__*/React.createElement("ul", {
      className: "bx-make-rows"
    }, rows.map(([l, n], i) => /*#__PURE__*/React.createElement("li", {
      key: i
    }, /*#__PURE__*/React.createElement("span", null, l), /*#__PURE__*/React.createElement("span", null, n)))) : /*#__PURE__*/React.createElement("p", {
      className: "bx-quiet"
    }, "Pick where it goes and the pieces show up here."), kept > 0 && /*#__PURE__*/React.createElement("p", {
      className: "bx-quiet"
    }, kept === 1 ? "One of these already has a draft" : `${kept} of these already have a draft`, "; Plunk keeps ", kept === 1 ? "it" : "them", "."), /*#__PURE__*/React.createElement("p", {
      className: "bx-quiet"
    }, "Planning is free. You see every piece before anything is written.", vids.length ? " Videos use credits only when you make them." : ""), this.state.plan && /*#__PURE__*/React.createElement("p", {
      className: "bx-note"
    }, "Your current plan has ", plural(curPlan, "piece"), ". Changes here apply when you re-plan.")), /*#__PURE__*/React.createElement("section", null, /*#__PURE__*/React.createElement("h3", null, "Your brand"), brand.company && /*#__PURE__*/React.createElement("p", {
      className: "bx-brand-name"
    }, brand.company), /*#__PURE__*/React.createElement("p", null, brand.voice ? `Voice: ${clip(brand.voice, 220)}` : "No brand voice set yet.", avoid.length ? ` Avoids: ${avoid.map(w => `"${w}"`).join(", ")}.` : ""), /*#__PURE__*/React.createElement("a", {
      className: "bx-link",
      onClick: () => this.props.history.push("/Settings/Brand")
    }, "Change in Brand kit")), links.length > 0 && /*#__PURE__*/React.createElement("section", null, /*#__PURE__*/React.createElement("h3", null, "Products in this story"), /*#__PURE__*/React.createElement("ul", {
      className: "bx-prod-list"
    }, links.slice(0, 5).map((l, i) => /*#__PURE__*/React.createElement("li", {
      key: i
    }, l.title || l.name || String(l.refId || l.ref_id || "").replace(/^shopify:/, "")))), links.length > 5 && /*#__PURE__*/React.createElement("p", {
      className: "bx-quiet"
    }, "and ", links.length - 5, " more")));
  }
  /** Make progress, the same count Make shows ("N of M approved"): the plan's switched-on written pieces (videos are not
   *  counted); one counts once its version is approved or published. Mirrors make.jsx pieces / variantFor / approvedCount
   *  (piece_map first, then the nth free draft of that channel). */
  makeProgress() {
    const plan = this.state.plan || [];
    const pd = this.state.primary && this.state.details[String(this.state.primary.id)] || {};
    const map = parseJson(latestBlock(pd.blocks, "piece_map")) || {};
    const taken = new Set(Object.values(map).map(String));
    const variants = this.allVariants();
    const ord = key => {
      const m = String(key || "").match(/^d:[^:]+:(\d+)$/);
      return m ? Number(m[1]) : 1;
    };
    const pieces = plan.filter(r => r.on && r.kind === "draft").map(r => {
      const same = variants.filter(v => String(v.channel || "").toLowerCase() === r.channel);
      let v = map[r.key] ? same.find(x => String(x.id) === String(map[r.key])) : null;
      if (!v) {
        const free = same.filter(x => String(x.status || "").toLowerCase() !== "archived" && !taken.has(String(x.id)));
        const n = ord(r.key);
        v = free[n - 1] || (n === 1 ? same.find(x => !taken.has(String(x.id))) || null : null);
      }
      return !!v && ["approved", "published", "live"].includes(String(v.status || "").toLowerCase());
    });
    return {
      planned: !!this.state.plan,
      total: pieces.length,
      approved: pieces.filter(Boolean).length
    };
  }
  /** Publish counts, the same numbers the Publish step shows ("Ready to publish · N", "Already live · N"). */
  shipSummary() {
    const ship = this.state.ship || this.buildShip();
    const live = this.shippedGroups().reduce((n, g) => n + g.items.filter(it => pubIsLive(it.pub)).length, 0);
    return {
      rows: ship.length,
      ready: ship.filter(r => r.targets.length).length,
      selected: ship.filter(r => r.on && r.target).length,
      live
    };
  }
  stepStatus() {
    const mp = this.makeProgress();
    const sp = this.shipSummary();
    return {
      brief: {
        sub: mp.planned ? "Planned" : "What, who, where",
        done: mp.planned
      },
      make: {
        sub: !mp.planned ? "Not planned yet" : mp.total ? `${mp.approved} of ${mp.total} approved` : "No pieces picked",
        done: mp.total > 0 && mp.approved === mp.total
      },
      ship: {
        sub: sp.ready ? `${sp.ready} ready${sp.live ? ` · ${sp.live} live` : ""}` : sp.live ? `${sp.live} live` : sp.rows ? "Nowhere to send yet" : "Nothing approved",
        done: sp.live > 0 && !sp.ready
      },
      measure: {
        sub: sp.live ? this.measureLabel() : "After publishing",
        done: false
      }
    };
  }
  primaryAction() {
    const {
      stage,
      busy
    } = this.state;
    if (stage === "brief") return /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "bx-primary",
      disabled: !!busy.plan,
      onClick: () => this.planIt()
    }, busy.plan ? "Planning…" : this.state.plan ? "Re-plan" : "Plan the pieces");
    if (stage === "make") {
      const o = this.state.primaryOverride;
      if (o && o.stage === "make") return /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "bx-primary",
        disabled: o.disabled,
        title: o.title || undefined,
        onClick: () => {
          if (this._primaryClick) this._primaryClick();
        }
      }, o.label);
      return /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "bx-primary",
        onClick: () => this.go("ship")
      }, "Go to Publish");
    }
    if (stage === "ship") {
      if ((this.state.shipView || "ready") !== "ready") return /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "bx-primary",
        onClick: () => this.setState({
          shipView: "ready"
        })
      }, "Ready to publish");
      const n = this.shipSummary().selected;
      return /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "bx-primary",
        disabled: !!busy.ship || !n,
        title: n ? undefined : "Tick a piece below to publish it",
        onClick: () => {
          if (!this.state.busy.ship && n) this.shipNow();
        }
      }, busy.ship ? "Publishing…" : n ? `Publish ${n}` : "Publish");
    }
    return null;
  }
  metaLine() {
    const mp = this.makeProgress();
    const dates = this.state.members.map(m => m.modifiedDate || m.createdDate).concat(this.allBlocks().map(x => x.createdDate || x.created)).filter(Boolean).sort();
    const upd = relDay(dates[dates.length - 1]);
    return [mp.planned ? plural(mp.total, "piece") : "Not planned yet", upd ? `Updated ${upd}` : ""].filter(Boolean).join(" · ");
  }
  render() {
    const {
      loading,
      error,
      stage
    } = this.state;
    if (error) return /*#__PURE__*/React.createElement(Error, {
      message: error
    });
    if (loading) return /*#__PURE__*/React.createElement(Loader, null);
    const steps = this.stepStatus();
    const idx = STAGES.findIndex(x => x[0] === stage);
    const body = stage === "brief" ? this.renderBrief() : stage === "make" ? /*#__PURE__*/React.createElement(MakeStage, {
      b: this
    }) : stage === "ship" ? this.renderShip() : this.renderMeasure();
    const menu = !!this.state.moreOpen;
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view wa-view content-view hub-bld bx-view bx-at-" + stage
    }, /*#__PURE__*/React.createElement("header", {
      className: "bx-top"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "bx-back",
      "aria-label": "Back to Stories",
      title: "Back to Stories",
      onClick: this.backToStories
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons",
      "aria-hidden": "true"
    }, "chevron_left")), /*#__PURE__*/React.createElement("div", {
      className: "bx-title"
    }, /*#__PURE__*/React.createElement("b", {
      title: this.storyTitle()
    }, this.storyTitle()), /*#__PURE__*/React.createElement("span", null, this.metaLine())), /*#__PURE__*/React.createElement("nav", {
      className: "bx-steps",
      "aria-label": "Steps"
    }, STAGES.map(([k, l], i) => {
      const s = steps[k];
      const on = stage === k;
      return /*#__PURE__*/React.createElement(React.Fragment, {
        key: k
      }, i > 0 && /*#__PURE__*/React.createElement("span", {
        className: "bx-step-line",
        "aria-hidden": "true"
      }), /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "bx-step" + (on ? " on" : "") + (s.done && !on ? " done" : ""),
        "aria-current": on ? "step" : undefined,
        onClick: () => {
          if (!on) this.go(k);
        }
      }, /*#__PURE__*/React.createElement("span", {
        className: "bx-step-dot",
        "aria-hidden": "true"
      }, s.done && !on ? /*#__PURE__*/React.createElement("span", {
        className: "material-icons"
      }, "check") : i + 1), /*#__PURE__*/React.createElement("span", {
        className: "bx-step-t"
      }, /*#__PURE__*/React.createElement("b", null, l), /*#__PURE__*/React.createElement("i", null, s.sub))));
    })), /*#__PURE__*/React.createElement("div", {
      className: "bx-acts"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bx-more-wrap"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "bx-more",
      "aria-label": "More actions",
      "aria-haspopup": "menu",
      "aria-expanded": menu,
      onClick: () => this.setState({
        moreOpen: !menu
      })
    }, "\xB7\xB7\xB7"), menu && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "bx-menu-catch",
      onClick: () => this.setState({
        moreOpen: false
      })
    }), /*#__PURE__*/React.createElement("div", {
      className: "bx-menu",
      role: "menu"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      role: "menuitem",
      onClick: () => this.setState({
        moreOpen: false,
        archiveAsk: true
      })
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons",
      "aria-hidden": "true"
    }, "archive"), "Archive story")))), this.primaryAction())), /*#__PURE__*/React.createElement("div", {
      className: "bx-body"
    }, /*#__PURE__*/React.createElement("section", {
      className: "bx-main bx-main-" + stage,
      "aria-label": (STAGES[idx] || [])[1]
    }, body), stage === "brief" && this.renderBriefRail()), this.renderConflict(), this.state.archiveAsk && /*#__PURE__*/React.createElement("div", {
      className: "wa-modal-backdrop hub-edit-backdrop",
      onClick: () => !this.state.busy.archive && this.setState({
        archiveAsk: false
      })
    }, /*#__PURE__*/React.createElement("div", {
      className: "hub-trash-sheet hub-sched",
      role: "dialog",
      "aria-modal": "true",
      onClick: e => e.stopPropagation()
    }, /*#__PURE__*/React.createElement("h3", {
      className: "hub-h3",
      style: {
        margin: 0
      }
    }, "Archive \"", this.storyTitle(), "\"?"), /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        margin: "6px 0 12px"
      }
    }, "Its ", this.state.members.length, " piece", this.state.members.length === 1 ? "" : "s", " leave the Library, Stories and Results. Anything already published stays live where it is. Restore it any time from the Library's archived list."), /*#__PURE__*/React.createElement("div", {
      className: "hub-tplp-acts",
      style: {
        justifyContent: "flex-end",
        gap: 8
      }
    }, /*#__PURE__*/React.createElement("button", {
      className: "territory-toggle sm",
      style: {
        margin: 0
      },
      disabled: !!this.state.busy.archive,
      onClick: () => this.setState({
        archiveAsk: false
      })
    }, "Cancel"), /*#__PURE__*/React.createElement("button", {
      className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary",
      disabled: !!this.state.busy.archive,
      onClick: this.archiveStory
    }, this.state.busy.archive ? "Archiving…" : "Archive")))), this.state.shipPreview && (() => {
      const r = this.state.shipPreview;
      const v = r.v;
      const d = r.d || {};
      const pick = this.socialMediaFor(v, d);
      const vb = videoRefFor(d && d.blocks && d.blocks, v.id);
      const videoUrl = v.channel === "video" ? vb ? vb.assetRef : null : pick.videoUrl || null;
      const imageUrl = videoUrl ? null : pick.imageUrl || null;
      const link = d.container && d.container.url || null;
      const close = () => this.setState({
        shipPreview: null
      });
      return /*#__PURE__*/React.createElement("div", {
        className: "wa-modal-backdrop hub-edit-backdrop",
        onClick: close
      }, /*#__PURE__*/React.createElement("div", {
        className: "hub-trash-sheet hub-tplp-sheet",
        onClick: e => e.stopPropagation()
      }, /*#__PURE__*/React.createElement("div", {
        className: "hub-tplp-head"
      }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("h3", {
        className: "hub-h3",
        style: {
          margin: 0
        }
      }, label(v.channel), r.target ? ` → ${targetLabel(r.target)}` : ""), /*#__PURE__*/React.createElement("span", {
        className: "territory-hint",
        style: {
          margin: 0
        }
      }, v.piece.title, r.when ? ` · ${r.when}` : " · publishes now")), /*#__PURE__*/React.createElement("span", {
        className: "hub-tplp-acts"
      }, /*#__PURE__*/React.createElement(Btn, {
        flat: true,
        onClick: () => {
          close();
          this.go("make");
        }
      }, "Edit in Make"), /*#__PURE__*/React.createElement("button", {
        className: "territory-toggle sm",
        style: {
          margin: 0
        },
        onClick: close
      }, "Close"))), /*#__PURE__*/React.createElement("div", {
        className: "hub-tplp-stage"
      }, /*#__PURE__*/React.createElement(PiecePreview, {
        channel: v.channel,
        target: r.target,
        title: v.title,
        body: v.body,
        imageUrl: imageUrl,
        videoUrl: videoUrl,
        posterUrl: null,
        link: link,
        layout: v.templateId && this.tpl(v.templateId) ? (recipeOf(this.tpl(v.templateId)).copy || {}).layout : null,
        images: this.storyImages().slice(0, 8)
      })), /*#__PURE__*/React.createElement("p", {
        className: "territory-hint",
        style: {
          margin: "10px 0 0"
        }
      }, "The copy and media exactly as they go out. The network applies its own layout, fonts and link card.")));
    })(), this.state.tplPreview && /*#__PURE__*/React.createElement(TemplatePreviewSheet, {
      t: this.state.tplPreview,
      containerId: this.state.primary ? String(this.state.primary.id) : null,
      onPick: t => {
        const b = this.state.bs.brief;
        this.toggleTemplate(b, t, patch => this.setState({
          bs: {
            ...this.state.bs,
            brief: {
              ...b,
              ...patch
            }
          }
        }));
      },
      onClose: () => this.setState({
        tplPreview: null
      })
    }), this.state.tplSheet && /*#__PURE__*/React.createElement(TemplateSheet, {
      variantId: this.state.tplSheet.variantId,
      channel: this.state.tplSheet.channel,
      storyId: this.state.storyId,
      variants: this.allVariants().filter(v => String(v.channel || "").toLowerCase() !== "video"),
      onSaved: async t => {
        await this.loadTemplates();
        if (t && t.id && this.state.tplSheet && this.state.tplSheet.kind === "reference") {
          const b = this.state.bs.brief;
          const key = String(t.channel || "any").toLowerCase();
          this.setState({
            bs: {
              ...this.state.bs,
              brief: {
                ...b,
                templates: {
                  ...(b.templates || {}),
                  [key]: String(t.id)
                }
              }
            }
          });
        }
      },
      onClose: () => this.setState({
        tplSheet: null
      })
    }));
  }
}
export const ContentBuilderViewComponent = withRouter(ContentBuilder);
export default ContentBuilderViewComponent;